import { ArrowDown, ArrowRight, ChevronLeft, CircleHelp, Expand, Pause, Play, ScanSearch, Settings2, Shrink, Target, Volume2, VolumeX, X } from 'lucide-react';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { ApiService } from '../../../services/api';

import type {
  ConcreteMeaningRoundResult,
  ConcreteMeaningWord,
  GroupingSessionPrepareResponse,
  GroupingSettings,
  MatchingRoundResult,
  MatchingWordApiResponse,
  ModeConfig,
  PeripheryPosition,
  PilotSettings,
  RecommendedStoryContent,
  WordPair,
} from './speedReadingShared';

import * as shared from './speedReadingShared';
import { ExerciseTextSourceControls } from './StoryExerciseViews';

const {
  ACCENT_COLOR_OPTIONS,
  ANTONYM_LEVEL_PAIRS,
  ANTONYM_INFO_PARAGRAPHS,
  BLOCKING_INFO_PARAGRAPHS,
  CAPTURE_INFO_PARAGRAPHS,
  CAPTURE_REVIEW_QUESTION,
  CAPTURE_SAMPLE_TEXT,
  CONCRETE_MEANING_LEVEL_WORDS,
  CONCRETE_MEANING_INFO_PARAGRAPHS,
  FONT_FAMILY_OPTIONS,
  FONT_SIZE_LABELS,
  FONT_SIZE_OPTIONS,
  GROUPING_INFO_PARAGRAPHS,
  GROUPING_REVIEW_QUESTIONS,
  GROUPING_SAMPLE_TEXT,
  MATCHING_INFO_PARAGRAPHS,
  MULTIFOCUS_LEVEL_WORDS,
  MULTIFOCUS_INFO_PARAGRAPHS,
  PERIPHERY_FOCUS_INFO_PARAGRAPHS,
  PILOT_TEST_LEVEL_WORDS,
  PILOT_TEST_INFO_PARAGRAPHS,
  SHADOW_INFO_PARAGRAPHS,
  SYNONYM_LEVEL_PAIRS,
  SYNONYM_INFO_PARAGRAPHS,
  THEME_PRIMARY_CSS,
  TEXT_READING_FONT_SIZE_CLASS,
  TEXT_READING_INFO_PARAGRAPHS,
  TEXT_READING_REVIEW_QUESTIONS,
  TEXT_READING_SAMPLE_TEXT,
  applySharedExerciseSettings,
  buildMatchingRelationLookup,
  buildMatchingRoundKey,
  fetchGroupingBootstrap,
  formatClock,
  getAdaptiveFlashWordStyle,
  getCaptureDurationSeconds,
  getModeCanonicalKey,
  getModeQueryValue,
  getGroupingTimingMetrics,
  getMatchingPair,
  getMatchingRevealDuration,
  getMatchingWordTypeByModeName,
  getPilotCooldownDuration,
  getPilotRevealDuration,
  getRandomPeripheryPosition,
  getSliderBackground,
  getThemeChromeButtonStyle,
  getThemeChromeTextStyle,
  getThemedBadgeStyle,
  getThemedLabelStyle,
  getThemedOptionButtonStyle,
  getThemedRangeStyle,
  getThemedReviewSelectionDividerStyle,
  getThemedReviewSelectionStyle,
  getThemedReviewSelectionTextStyle,
  getThemedSwatchStyle,
  mapMatchingApiItemsToPairs,
  normalizeDifficultyLevel,
  normalizeMatchingWordKey,
  normalizeAccentColor,
  normalizePilotAnswer,
  persistSharedExerciseSettings,
  resolveAccentColor,
  shouldStopForWrongGap,
  ModeIllustration,
  ModesGrid,
  SidebarPreparationCard,
} = shared;

const EXERCISE_TOOL_BUTTON_CLASS = 'inline-flex h-10 w-10 items-center justify-center rounded-xl text-[#2f7188] transition hover:text-[#174e67]';

const getConcreteMeaningFallbackWords = (level: number): ConcreteMeaningWord[] => {
  const current = CONCRETE_MEANING_LEVEL_WORDS[level] || [];
  const previous = CONCRETE_MEANING_LEVEL_WORDS[Math.max(1, level - 1)] || [];
  const next = CONCRETE_MEANING_LEVEL_WORDS[Math.min(13, level + 1)] || [];
  const global = Object.values(CONCRETE_MEANING_LEVEL_WORDS).flat();
  const seen = new Set<string>();

  return [...current, ...previous, ...next, ...global].filter((item) => {
    const key = String(item?.word || '').trim().toLocaleLowerCase('tr-TR');
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getMatchingPairFallback = (modeName: string, level: number): WordPair[] => {
  const normalizedMode = shared.getModeCanonicalKey(modeName);
  const source = normalizedMode === 'zit-anlam'
    ? ANTONYM_LEVEL_PAIRS
    : normalizedMode === 'es-anlam'
      ? SYNONYM_LEVEL_PAIRS
      : null;

  if (!source) return [];

  const current = source[level] || [];
  const previous = source[Math.max(1, level - 1)] || [];
  const next = source[Math.min(13, level + 1)] || [];
  const global = Object.values(source).flat();
  const seen = new Set<string>();

  return [...current, ...previous, ...next, ...global].filter((pair) => {
    const left = String(pair?.[0] || '').trim().toLocaleLowerCase('tr-TR');
    const right = String(pair?.[1] || '').trim().toLocaleLowerCase('tr-TR');
    const key = [left, right].sort((a, b) => a.localeCompare(b, 'tr')).join('|');
    if (!left || !right || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const TURKISH_SYLLABLE_VOWELS = /[aeıioöuü]/gi;
const WORD_READING_DIFFICULTY_MAX_LEVEL = 5;
const PILOT_CUE_BLINK_INTERVAL_MS = 75;
const PILOT_CUE_BLINK_COUNT = 4;
const FLASH_VISIBILITY_KEYFRAMES = `
@keyframes speedReadingFlashVisibility {
  0%, 99% { opacity: 1; }
  100% { opacity: 0; }
}
`;

const countTurkishSyllables = (value: string) => {
  const normalized = String(value || '').toLocaleLowerCase('tr-TR');
  const matches = normalized.match(TURKISH_SYLLABLE_VOWELS);
  return Math.max(1, matches?.length || 1);
};

const normalizeWordReadingDifficultyLevel = (level: number) => (
  shared.clamp(Math.round(Number(level) || 1), 1, WORD_READING_DIFFICULTY_MAX_LEVEL)
);

const wordMatchesReadingDifficulty = (word: string, level: number) => {
  const targetSyllables = normalizeWordReadingDifficultyLevel(level);
  const syllableCount = countTurkishSyllables(word);
  return targetSyllables === WORD_READING_DIFFICULTY_MAX_LEVEL
    ? syllableCount >= targetSyllables
    : syllableCount === targetSyllables;
};

const uniqueNormalizedWords = (words: string[]) => {
  const seen = new Set<string>();

  return words.filter((word) => {
    const key = String(word || '').trim().toLocaleLowerCase('tr-TR');
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const filterWordsByReadingDifficulty = (words: string[], level: number) => {
  const uniqueWords = uniqueNormalizedWords(words.map((word) => String(word || '').trim()).filter(Boolean));
  const exactWords = uniqueWords.filter((word) => wordMatchesReadingDifficulty(word, level));

  return exactWords.length > 0 ? exactWords : uniqueWords;
};

const getMultifocusFallbackWords = (level: number): string[] => {
  return filterWordsByReadingDifficulty([
    ...Object.values(MULTIFOCUS_LEVEL_WORDS).flat(),
    ...Object.values(PILOT_TEST_LEVEL_WORDS).flat(),
  ], level);
};

const WORD_GLANCE_MIN_COUNT = 1;
const WORD_GLANCE_MAX_COUNT = 5;

type WordGlanceSettings = PilotSettings & {
  wordGlanceWordCount: number;
};

const WORD_GLANCE_INITIAL_POSITION: PeripheryPosition = { top: '10%', left: '8%', width: '84%', textAlign: 'left' };

const WORD_GLANCE_JUMP_POSITIONS: PeripheryPosition[] = [
  WORD_GLANCE_INITIAL_POSITION,
  { top: '10%', right: '8%', width: '84%', textAlign: 'right' },
  { bottom: '12%', left: '8%', width: '84%', textAlign: 'left' },
  { bottom: '12%', right: '8%', width: '84%', textAlign: 'right' },
  { top: '50%', left: '50%', width: '84%', textAlign: 'center', transform: 'translate(-50%, -50%)' },
];

const normalizeWordGlanceCount = (value: unknown) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 3;
  return Math.max(WORD_GLANCE_MIN_COUNT, Math.min(WORD_GLANCE_MAX_COUNT, Math.round(parsed)));
};

const WORD_GLANCE_FALLBACK_PHRASES: Record<number, string[]> = {
  1: ['su', 'sarı top', 'ali top attı', 'sarı kalem masada durdu', 'küçük kuş dalda sessiz durdu'],
  2: ['kitap', 'mavi çanta', 'siyah kalem bitti', 'çocuk kapıyı yavaş açtı', 'bahçede sarı çiçekler sessizce açtı'],
  3: ['pencere', 'temiz defter', 'burada yağmur yağıyor', 'kırmızı araba hızla geçti', 'kalem kutusu masanın üstünde duruyor'],
  4: ['yolculuk', 'sessiz oda', 'portakal tatlı meyvedir', 'siyah kalem yine bitti', 'öğrenci yeni kitabını dikkatle okudu'],
  5: ['bilgisayar', 'hızlı tren', 'yarasalar doğuştan kördür', 'küçük defter masada duruyor', 'bilgisayarımın şarjı çok çabuk bitiyor'],
};

const splitWordGlancePhrase = (phrase: string) => String(phrase || '').trim().split(/\s+/).filter(Boolean);

const getWordGlanceFallbackPhrases = (): string[] => {
  const global = [
    ...Object.values(WORD_GLANCE_FALLBACK_PHRASES).flat(),
    ...Object.values(PILOT_TEST_LEVEL_WORDS).flat(),
    ...Object.values(MULTIFOCUS_LEVEL_WORDS).flat(),
  ];
  const seen = new Set<string>();

  return global.filter((phrase) => {
    const key = String(phrase || '').trim().toLocaleLowerCase('tr-TR');
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const wordGlancePhraseMatchesReadingDifficulty = (phraseWords: string[], level: number) => (
  phraseWords.length > 0 && phraseWords.every((word) => wordMatchesReadingDifficulty(word, level))
);

const filterWordGlancePhrasesByReadingDifficulty = (phrases: string[], level: number) => {
  const cleanPhrases = phrases.map((phrase) => String(phrase || '').trim()).filter(Boolean);
  const exactPhrases = cleanPhrases.filter((phrase) => wordGlancePhraseMatchesReadingDifficulty(splitWordGlancePhrase(phrase), level));

  return exactPhrases.length > 0 ? exactPhrases : cleanPhrases;
};

const takeRandomWords = (words: string[], count: number) => {
  const selected: string[] = [];
  const candidates = [...words];

  while (selected.length < count && candidates.length > 0) {
    const index = Math.floor(Math.random() * candidates.length);
    const [word] = candidates.splice(index, 1);
    if (word) selected.push(word);
  }

  return selected;
};

const pickWordGlancePhrase = (phrases: string[], count: number, level: number): string[] => {
  const cleanPhrases = phrases.map((phrase) => String(phrase || '').trim()).filter(Boolean);
  if (cleanPhrases.length === 0) return [];
  const safeCount = normalizeWordGlanceCount(count);
  const exactPhrases = cleanPhrases
    .map((phrase) => splitWordGlancePhrase(phrase))
    .filter((words) => words.length === safeCount && wordGlancePhraseMatchesReadingDifficulty(words, level));

  if (exactPhrases.length > 0) {
    return exactPhrases[Math.floor(Math.random() * exactPhrases.length)] || [];
  }

  const readableWords = filterWordsByReadingDifficulty(cleanPhrases.flatMap(splitWordGlancePhrase), level);
  if (readableWords.length >= safeCount) return takeRandomWords(readableWords, safeCount);

  const sourcePhrases = cleanPhrases
    .map((phrase) => splitWordGlancePhrase(phrase))
    .filter((words) => words.length > 0);

  return sourcePhrases[Math.floor(Math.random() * sourcePhrases.length)] || [];
};

const getWordGlancePositionKey = (position: PeripheryPosition) => {
  return `${position.top || ''}|${position.right || ''}|${position.bottom || ''}|${position.left || ''}`;
};

const getNextWordGlanceCornerPosition = (currentPosition: PeripheryPosition): PeripheryPosition => {
  const currentKey = getWordGlancePositionKey(currentPosition);
  const candidates = WORD_GLANCE_JUMP_POSITIONS.filter((position) => getWordGlancePositionKey(position) !== currentKey);

  return candidates[Math.floor(Math.random() * candidates.length)] || WORD_GLANCE_INITIAL_POSITION;
};

const getWordGlanceEdgeCount = (chars: string[]) => {
  return chars.length > 5 ? 3 : 2;
};

const isWordGlanceStartAccentChar = (chars: string[], index: number) => {
  const edgeCount = getWordGlanceEdgeCount(chars);
  return index < edgeCount;
};

const isWordGlanceEndAccentChar = (chars: string[], index: number) => {
  const edgeCount = getWordGlanceEdgeCount(chars);
  return index >= chars.length - edgeCount;
};

const renderWordGlanceWord = (word: string, activeAccent: string, accentSide: 'start' | 'end' | 'none') => {
  const chars = Array.from(word);

  return (
    <span style={{ display: 'inline-block', verticalAlign: 'baseline', whiteSpace: 'nowrap' }}>
      {chars.map((char, index) => (
        <span
          key={`${word}-${char}-${index}`}
          style={{
            color: (
              accentSide === 'start'
                ? isWordGlanceStartAccentChar(chars, index)
                : accentSide === 'end'
                  ? isWordGlanceEndAccentChar(chars, index)
                  : false
            ) ? activeAccent : '#111827',
            verticalAlign: 'baseline',
          }}
        >
          {char}
        </span>
      ))}
    </span>
  );
};

const SettingsSwitchKnob: React.FC<{ checked: boolean }> = ({ checked }) => (
  <span
    className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow-[0_2px_8px_rgba(43,62,76,0.18)] transition-all ${checked ? 'left-[34px]' : 'left-1'}`}
  />
);

const SettingsToggleRow: React.FC<{
  label: string;
  checked: boolean;
  onChange: () => void;
}> = ({ label, checked, onChange }) => (
  <div className="overflow-hidden rounded-xl border border-[#d9e6eb] bg-white">
    <div className="flex min-h-14 items-center gap-4 px-4 py-3">
      <span className="flex-1 text-sm font-bold uppercase tracking-[0.08em] text-[#8b9ca5]">{label}</span>
      <button
        onClick={onChange}
        className={`relative h-8 w-16 shrink-0 rounded-full transition-colors ${checked ? 'bg-[#b6d2ad]' : 'bg-[#d7e1e7]'}`}
        aria-pressed={checked}
        type="button"
      >
        <SettingsSwitchKnob checked={checked} />
      </button>
    </div>
  </div>
);

const SettingsStepperRow: React.FC<{
  label: string;
  value: string;
  onDecrease: () => void;
  onIncrease: () => void;
}> = ({ label, value, onDecrease, onIncrease }) => (
  <div className="overflow-hidden rounded-xl border border-[#d9e6eb] bg-white">
    <div className="grid h-14 grid-cols-[minmax(0,1fr)_64px_120px_64px] items-center">
      <div className="border-r border-[#e7eef2] px-4 text-sm font-bold uppercase tracking-[0.08em] text-[#8b9ca5]">{label}</div>
      <button
        onClick={onDecrease}
        className="h-full border-r border-[#e7eef2] text-xl font-black text-[#b0bec6] transition-colors hover:bg-[#f7fbfd] hover:text-[#6f8a96]"
        type="button"
      >
        &laquo;
      </button>
      <div className="text-center text-[15px] font-black text-[#5d7380]">{value}</div>
      <button
        onClick={onIncrease}
        className="h-full border-l border-[#e7eef2] text-xl font-black text-[#b0bec6] transition-colors hover:bg-[#f7fbfd] hover:text-[#6f8a96]"
        type="button"
      >
        &raquo;
      </button>
    </div>
  </div>
);

const DISAPPEARING_TEXT_INFO_PARAGRAPHS = [
  'Metni akış içinde izlerken soldan sağa kaybolan alanı takip ederek odakta kalmanızı destekler.',
  'Kelimeler soldan sağa, seçtiğiniz kelime sayısı kadar gruplar halinde kaybolur.',
  'Sayfa metin ilerledikçe kendisi akar; bir sonraki görünen kelimeyi takip ederek okuma ritmini koruyun.',
];

type CaptureSettings = PilotSettings & {
  disappearWordCount: number;
};

const DISAPPEARING_WORD_COUNT_OPTIONS = [1, 2, 3, 4, 5] as const;

export const PilotTestExerciseView: React.FC<{
  profile: any;
  mode: ModeConfig;
  onClose: () => void;
}> = ({ profile, mode, onClose }) => {
  const { user } = useAuth();
  const fullscreenContainerRef = useRef<HTMLDivElement | null>(null);
  const flashTimeoutRef = useRef<number | null>(null);
  const cueIntervalRef = useRef<number | null>(null);
  const cueTimeoutRef = useRef<number | null>(null);
  const answerInputRef = useRef<HTMLInputElement | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [muted, setMuted] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [settings, setSettings] = useState<PilotSettings>({
    wordsPerMinute: 275,
    level: 1,
    fontSize: 'buyuk',
    fontFamily: 'GoogleSans',
    accentColor: 'default',
    enableCountdown: true,
  });
  const [phase, setPhase] = useState<'idle' | 'countdown' | 'cue' | 'flashing' | 'answering'>('idle');
  const [countdown, setCountdown] = useState(3);
  const [currentWord, setCurrentWord] = useState('');
  const [expectedWord, setExpectedWord] = useState('');
  const [answer, setAnswer] = useState('');
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [remoteWordPool, setRemoteWordPool] = useState<string[]>([]);
  const [wordPoolError, setWordPoolError] = useState<string | null>(null);
  const [cueBlinkVisible, setCueBlinkVisible] = useState(true);
  const [currentRevealDuration, setCurrentRevealDuration] = useState(() => getPilotRevealDuration(275, 1));

  const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || profile?.name || 'Kullanıcı';
  const userImage = user?.avatar || user?.media?.url || user?.profilePhoto?.url || null;
  const userInitials = userName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part.charAt(0).toUpperCase())
    .join('');
  const introParagraphs = PILOT_TEST_INFO_PARAGRAPHS;
  const isRunning = phase !== 'idle';
  const activeAccent = resolveAccentColor(settings.accentColor);
  const activeFontFamily = FONT_FAMILY_OPTIONS.find((item) => item.value === settings.fontFamily)?.css || settings.fontFamily;
  const activePilotWordStyle = getAdaptiveFlashWordStyle(settings.fontSize, currentWord, 'center');
  useEffect(() => {
    setSettings((prev) => applySharedExerciseSettings(prev));
  }, []);

  useEffect(() => {
    if (!settingsOpen) return;

    persistSharedExerciseSettings(settings);
  }, [settings, settingsOpen]);

  useEffect(() => {
    let mounted = true;

    ApiService.getApi(`speed-reading/exercises/word-pools?type=takistoskop&level=${settings.level}&limit=200`)
      .then((response: any) => {
        if (!mounted) return;
        const words = Array.isArray(response?.items)
          ? response.items.map((item: any) => String(item?.word || '').trim()).filter(Boolean)
          : [];
        setRemoteWordPool(words);
        setWordPoolError(words.length > 0 ? null : 'Veritabanında Takistoskop için kayıtlı kelime bulunamadı.');
      })
      .catch((error) => {
        if (!mounted) return;
        console.error('Takistoskop kelime havuzu yüklenemedi:', error);
        setRemoteWordPool([]);
        setWordPoolError('Takistoskop kelime havuzu veritabanından yüklenemedi.');
      });

    return () => {
      mounted = false;
    };
  }, [settings.level]);

  const clearFlashTimeout = () => {
    if (flashTimeoutRef.current) {
      window.clearTimeout(flashTimeoutRef.current);
      flashTimeoutRef.current = null;
    }
  };

  const clearCueTimers = () => {
    if (cueIntervalRef.current) {
      window.clearInterval(cueIntervalRef.current);
      cueIntervalRef.current = null;
    }
    if (cueTimeoutRef.current) {
      window.clearTimeout(cueTimeoutRef.current);
      cueTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => () => {
    clearFlashTimeout();
    clearCueTimers();
  }, []);

  useEffect(() => {
    if (phase !== 'countdown' || countdown <= 0) return;

    const timer = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);

          return 0;
        };

        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [phase, countdown]);

  useEffect(() => {
    if (phase !== 'countdown' || countdown !== 0) return;

    showNextWord();
  }, [phase, countdown]);

  useEffect(() => {
    if (phase !== 'answering') return;
    const focusTimer = window.setTimeout(() => answerInputRef.current?.focus(), 50);

    return () => window.clearTimeout(focusTimer);
  }, [phase, expectedWord]);

  const showNextWord = () => {
    clearFlashTimeout();
    clearCueTimers();
    if (remoteWordPool.length === 0) {
      setPhase('idle');
      setCurrentWord('');
      setExpectedWord('');
      return;
    }

    const nextWord = remoteWordPool[Math.floor(Math.random() * remoteWordPool.length)] || remoteWordPool[0];

    setExpectedWord(nextWord);
    setCurrentWord(nextWord.toLocaleUpperCase('tr-TR'));
    setAnswer('');
    setCueBlinkVisible(true);
    setPhase('cue');

    cueIntervalRef.current = window.setInterval(() => {
      setCueBlinkVisible((prev) => !prev);
    }, PILOT_CUE_BLINK_INTERVAL_MS);

    cueTimeoutRef.current = window.setTimeout(() => {
      clearCueTimers();
      const revealDuration = getPilotRevealDuration(settings.wordsPerMinute, settings.level);
      setCurrentRevealDuration(revealDuration);
      setCueBlinkVisible(true);
      setPhase('flashing');

      flashTimeoutRef.current = window.setTimeout(() => {
        setCurrentWord('');
        setPhase('answering');
        flashTimeoutRef.current = null;
      }, revealDuration);
    }, PILOT_CUE_BLINK_INTERVAL_MS * PILOT_CUE_BLINK_COUNT);
  };

  const handleReset = () => {
    clearFlashTimeout();
    clearCueTimers();
    setPhase('idle');
    setCountdown(3);
    setCurrentWord('');
    setExpectedWord('');
    setAnswer('');
    setCorrectCount(0);
    setWrongCount(0);
    setCueBlinkVisible(true);
  };

  const handleCloseExercise = async () => {
    if (document.fullscreenElement === fullscreenContainerRef.current) {
      try {
        await document.exitFullscreen();
      } catch (error) {
        console.error('Tam ekrandan çıkılamadı:', error);
      }
    }

    handleReset();
    onClose();
  };

  const handleToggleFullscreen = async () => {
    const container = fullscreenContainerRef.current;
    if (!container) return;

    try {
      if (document.fullscreenElement === container) {
        await document.exitFullscreen();
        return;
      }

      await container.requestFullscreen();
    } catch (error) {
      console.error('Tam ekran değiştirilemedi:', error);
    }
  };

  const handlePrimaryAction = async () => {
    if (isRunning) {
      handleReset();
      return;
    }

    if (settings.enableCountdown) {
      setCountdown(3);
      setPhase('countdown');
      return;
    }

    showNextWord();
  };

  const finishSession = () => {
    clearFlashTimeout();
    clearCueTimers();
    setPhase('idle');
    setCurrentWord('');
    setExpectedWord('');
    setAnswer('');
    setCueBlinkVisible(true);
  };

  const handleAnswerResult = (isCorrect: boolean) => {
    const nextCorrectCount = correctCount + (isCorrect ? 1 : 0);
    const nextWrongCount = wrongCount + (isCorrect ? 0 : 1);
    setCorrectCount(nextCorrectCount);
    setWrongCount(nextWrongCount);

    if (shouldStopForWrongGap(nextCorrectCount, nextWrongCount)) {
      finishSession();
      return;
    }

    showNextWord();
  };

  const handleSubmitAnswer = () => {
    if (phase !== 'answering' || !expectedWord) return;
    if (!answer.trim()) return;
    handleAnswerResult(normalizePilotAnswer(answer) === normalizePilotAnswer(expectedWord));
  };

  const handleMissedWord = () => {
    if (phase !== 'answering') return;
    handleAnswerResult(false);
  };

  return (
    <div
      ref={fullscreenContainerRef}
      className={`${isFullscreen ? 'h-screen p-3' : 'h-full p-6'} bg-[#f3f5f7]`}
    >
      <style>{FLASH_VISIBILITY_KEYFRAMES}</style>
      <div className={`${isFullscreen ? 'max-w-none' : 'max-w-[1260px]'} mx-auto h-full`}>
        <div className="h-full border border-[#e6edf1] rounded-xl bg-[#f8fafb] px-6 py-5 flex gap-5 relative">
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Target className="w-[19px] h-[19px]" style={getThemeChromeTextStyle()} />
                <div className="text-[20px] font-bold" style={getThemeChromeTextStyle()}>{mode.name}</div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setMuted((m) => !m)} className={EXERCISE_TOOL_BUTTON_CLASS} title={muted ? 'Sesi Aç' : 'Sesi Kapat'}>
                  {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
                <button onClick={() => setSettingsOpen(true)} className={EXERCISE_TOOL_BUTTON_CLASS} title="Ayarlar">
                  <Settings2 className="w-5 h-5" />
                </button>
                <button onClick={() => { void handleToggleFullscreen(); }} className={EXERCISE_TOOL_BUTTON_CLASS} title={isFullscreen ? 'Tam Ekrandan Çık' : 'Tam Ekran'}>
                  {isFullscreen ? <Shrink className="w-5 h-5" /> : <Expand className="w-5 h-5" />}
                </button>
                <button onClick={() => { void handleCloseExercise(); }} className={EXERCISE_TOOL_BUTTON_CLASS} title="Kapat">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 border border-[#e8edf0] rounded-[10px] px-6 py-5 bg-white overflow-hidden">
              {phase === 'idle' && (
                <div className="h-full flex items-center justify-center">
                  <div className="max-w-[980px] translate-y-12 text-center">
                    {introParagraphs.map((paragraph, index) => (
                      <p
                        key={paragraph}
                        className={`mx-auto max-w-[900px] text-[20px] font-bold leading-[1.55] text-[#2e6276] ${index > 0 ? 'mt-8' : ''}`}
                      >
                        {paragraph}
                      </p>
                    ))}
                    {wordPoolError ? (
                      <p className="mx-auto mt-8 max-w-[780px] rounded-2xl border border-[#f3d6d6] bg-[#fff5f5] px-5 py-4 text-[15px] font-semibold leading-7 text-[#a44b4b]">
                        {wordPoolError}
                      </p>
                    ) : null}
                  </div>
                </div>
              )}

              {phase === 'countdown' && (
                <div className="h-full flex items-center justify-center">
                  <span className="text-7xl font-extrabold text-[#18485c]">{countdown}</span>
                </div>
              )}

              {phase === 'cue' && (
                <div className="h-full flex items-center justify-center">
                  <div className="relative flex h-[210px] w-[210px] items-center justify-center">
                    <div
                      className="absolute inset-0 rounded-[32px] transition-all duration-100"
                      style={{
                        border: `4px solid ${activeAccent}`,
                        opacity: cueBlinkVisible ? 1 : 0.2,
                        boxShadow: cueBlinkVisible ? `0 0 0 10px ${activeAccent}1f, 0 0 36px ${activeAccent}33` : 'none',
                        transform: cueBlinkVisible ? 'scale(1)' : 'scale(0.94)',
                      }}
                    />
                    <div
                      className="absolute inset-[22px] rounded-[24px] border border-dashed"
                      style={{
                        borderColor: `${activeAccent}66`,
                        opacity: cueBlinkVisible ? 0.95 : 0.35,
                      }}
                    />
                    <div
                      className="text-center text-[18px] font-black tracking-[0.35em] text-[#9ab0ba]"
                      style={{ fontFamily: activeFontFamily }}
                    >
                    </div>
                  </div>
                </div>
              )}

              {phase === 'flashing' && (
                <div className="h-full flex items-center justify-center">
                  <div className="flex h-[220px] w-[220px] items-center justify-center overflow-hidden rounded-[32px] border border-[#e5edf0] bg-[#fbfdfe] px-8 py-6 shadow-[0_18px_40px_rgba(18,71,94,0.08)]">
                    <div
                      className="text-center font-black"
                      style={{
                        ...activePilotWordStyle,
                        color: activeAccent,
                        fontFamily: activeFontFamily,
                        maxWidth: '100%',
                        animation: `speedReadingFlashVisibility ${currentRevealDuration}ms linear forwards`,
                      }}
                    >
                      {currentWord}
                    </div>
                  </div>
                </div>
              )}

              {phase === 'answering' && (
                <div className="h-full flex items-center justify-center">
                  <div className="w-full max-w-[600px] rounded-2xl border border-[#edf2f4] bg-white px-14 py-12 shadow-[0_18px_45px_rgba(18,71,94,0.08)]">
                    <h4 className="text-center text-[22px] font-black" style={{ color: activeAccent, fontFamily: activeFontFamily }}>Zaman kaybetmeden cevabı yaz!</h4>
                    <input
                      ref={answerInputRef}
                      value={answer}
                      onChange={(e) => setAnswer(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSubmitAnswer();
                        }
                      }}
                      placeholder="Cevabı buraya yazınız."
                      className="mt-8 h-14 w-full rounded-xl border border-[#edf1f4] bg-[#f6f7f8] px-5 text-[18px] font-semibold text-[#295567] outline-none placeholder:text-[#c4c8cb] focus:bg-white"
                      style={{ fontFamily: activeFontFamily, borderColor: answer.trim() ? `${activeAccent}55` : undefined }}
                    />
                    <div className="mt-10 grid grid-cols-2 gap-5">
                      <button
                        onClick={handleSubmitAnswer}
                        disabled={!answer.trim()}
                        className={`h-12 rounded-xl text-[18px] font-bold transition-colors ${answer.trim()
                          ? 'text-white'
                          : 'bg-[#dfe7ea] text-[#7f929c] cursor-not-allowed'
                          }`}
                        style={answer.trim() ? { backgroundColor: activeAccent, fontFamily: activeFontFamily } : { fontFamily: activeFontFamily }}
                      >
                        Cevapla
                      </button>
                      <button
                        onClick={handleMissedWord}
                        className="h-12 rounded-xl bg-[#f1f3f5] text-[18px] font-bold text-[#2d6175] transition-colors hover:bg-[#e8ecef]"
                        style={{ fontFamily: activeFontFamily }}
                      >
                        Göremedim
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <aside className="hidden lg:flex w-[320px] shrink-0 h-full min-h-0 rounded-xl border border-[#e0eaee] bg-white p-5 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="flex items-center gap-3">
                {userImage ? (
                  <img src={userImage} alt={userName} className="w-11 h-11 rounded-full object-cover bg-[#daf0f7]" />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-[#daf0f7] text-[#146f8e] flex items-center justify-center font-bold">
                    {userInitials || 'K'}
                  </div>
                )}
                <div>
                  <div className="text-[#355c68] text-sm font-bold">{userName}</div>
                  <div className="text-[#94a8b3] text-xs">1. Seviye</div>
                </div>
              </div>

              <div className="mt-6 flex flex-col items-center text-center">
                <div className="rounded-[2rem] bg-[#f7fafc] px-5 py-4">
                  <ModeIllustration mode={mode} />
                </div>
                <h3 className="mt-4 text-[34px] leading-none font-black" style={getThemeChromeTextStyle()}>{mode.name}</h3>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-6 text-center">
                <div>
                  <div className="text-[14px] text-[#b1b8bd]">Doğru</div>
                  <div className="mt-1 text-[20px] leading-none font-black text-[#194e62]">{correctCount}</div>
                </div>
                <div>
                  <div className="text-[14px] text-[#b1b8bd]">Yanlış</div>
                  <div className="mt-1 text-[20px] leading-none font-black text-[#194e62]">{wrongCount}</div>
                </div>
              </div>

              {phase === 'idle' && <SidebarPreparationCard paragraphs={introParagraphs} fontFamily={activeFontFamily} />}

            </div>

            <div className="shrink-0 pt-4">
              <button
                onClick={() => { void handlePrimaryAction(); }}
                className="w-full h-12 rounded-lg text-white font-bold text-base transition-colors inline-flex items-center justify-center gap-2"
                style={getThemeChromeButtonStyle()}
              >
                {isRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {isRunning ? 'Durdur' : 'Başlat'}
              </button>

              {(isRunning || correctCount > 0 || wrongCount > 0) && (
                <button
                  onClick={handleReset}
                  className="mt-4 w-full h-12 rounded-lg bg-[#f1f3f5] text-[#2d6175] font-bold text-base transition-colors hover:bg-[#e7ecef]"
                >
                  Sıfırla
                </button>
              )}
            </div>
          </aside>

          {isFullscreen && (
            <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 text-[12px] font-medium text-[#96a6af]">
              Tam ekrandan çıkmak için Esc tuşuna basın veya tıklayın
            </div>
          )}

          {settingsOpen && (
            <div className="fixed inset-0 z-[120] bg-black/55 backdrop-blur-[4px] flex items-center justify-center">
              <div className="w-[920px] bg-white rounded-xl shadow-lg p-6 select-none">
                <div className="flex items-center justify-between mb-5">
                  <h4 className="text-xl font-bold" style={getThemedLabelStyle()}>Egzersiz Ayarları</h4>
                  <button className="text-gray-500 hover:text-gray-700" onClick={() => setSettingsOpen(false)}>
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-7">
                  <div className="space-y-5">
                    <SettingsStepperRow
                      label="Gösterim Hızı"
                      value={`${shared.wordsPerMinuteToClosestPilotPaceMs(settings.wordsPerMinute)} ms`}
                      onDecrease={() => {
                        const currentPaceMs = shared.wordsPerMinuteToClosestPilotPaceMs(settings.wordsPerMinute);
                        const currentIndex = shared.PILOT_PACE_MS_OPTIONS.findIndex((paceMs) => paceMs === currentPaceMs);
                        const nextPaceMs = shared.PILOT_PACE_MS_OPTIONS[Math.max(0, currentIndex - 1)];
                        setSettings((prev) => ({
                          ...prev,
                          wordsPerMinute: shared.paceMsToWordsPerMinute(nextPaceMs),
                          paceMs: nextPaceMs,
                        }));
                      }}
                      onIncrease={() => {
                        const currentPaceMs = shared.wordsPerMinuteToClosestPilotPaceMs(settings.wordsPerMinute);
                        const currentIndex = shared.PILOT_PACE_MS_OPTIONS.findIndex((paceMs) => paceMs === currentPaceMs);
                        const nextPaceMs = shared.PILOT_PACE_MS_OPTIONS[Math.min(shared.PILOT_PACE_MS_OPTIONS.length - 1, currentIndex + 1)];
                        setSettings((prev) => ({
                          ...prev,
                          wordsPerMinute: shared.paceMsToWordsPerMinute(nextPaceMs),
                          paceMs: nextPaceMs,
                        }));
                      }}
                    />

                    <SettingsStepperRow
                      label="Seviye"
                      value={`${settings.level}`}
                      onDecrease={() => setSettings((prev) => ({ ...prev, level: Math.max(1, prev.level - 1) }))}
                      onIncrease={() => setSettings((prev) => ({ ...prev, level: Math.min(13, prev.level + 1) }))}
                    />

                    <SettingsToggleRow
                      label="Başlamadan önce geriye say"
                      checked={settings.enableCountdown}
                      onChange={() => setSettings((prev) => ({ ...prev, enableCountdown: !prev.enableCountdown }))}
                    />
                  </div>

                  <div className="space-y-5 border-l border-[#edf2f5] pl-7">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Boyutu</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {FONT_SIZE_LABELS[settings.fontSize]}
                        </span>
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        {FONT_SIZE_OPTIONS.map((sizeOption) => {
                          const isActive = settings.fontSize === sizeOption.value;
                          return (
                            <button
                              key={sizeOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, fontSize: sizeOption.value }))}
                              className="h-10 rounded-lg border transition-colors"
                              style={getThemedOptionButtonStyle(isActive)}
                            >
                              <span className={`font-semibold ${sizeOption.preview}`}>{sizeOption.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <p className="mb-2 text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Karakteri</p>
                      <div className="grid grid-cols-2 gap-2">
                        {FONT_FAMILY_OPTIONS.map((fontOption) => {
                          const isActive = settings.fontFamily === fontOption.value;
                          return (
                            <button
                              key={fontOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, fontFamily: fontOption.value }))}
                              className="h-10 rounded-lg border text-sm transition-colors"
                              style={{ ...getThemedOptionButtonStyle(isActive), fontFamily: fontOption.css }}
                            >
                              {fontOption.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Vurgu Rengi</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {ACCENT_COLOR_OPTIONS.find((colorOption) => colorOption.value === settings.accentColor)?.label || 'Varsayılan'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        {ACCENT_COLOR_OPTIONS.map((colorOption) => {
                          const isActive = settings.accentColor === colorOption.value;
                          return (
                            <button
                              key={colorOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, accentColor: colorOption.value }))}
                              className="h-7 w-7 rounded-full border-2 transition-transform hover:scale-105"
                              style={{ ...getThemedSwatchStyle(isActive, colorOption.hex), backgroundColor: colorOption.hex }}
                              title={colorOption.label}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const WordGlanceExerciseView: React.FC<{
  profile: any;
  mode: ModeConfig;
  onClose: () => void;
}> = ({ profile, mode, onClose }) => {
  const { user } = useAuth();
  const fullscreenContainerRef = useRef<HTMLDivElement | null>(null);
  const flashTimeoutRef = useRef<number | null>(null);
  const cooldownTimeoutRef = useRef<number | null>(null);
  const settingsRef = useRef<WordGlanceSettings | null>(null);
  const effectiveWordPoolRef = useRef<string[]>([]);
  const wordGlanceSettingsKeyRef = useRef('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [settings, setSettings] = useState<WordGlanceSettings>({
    wordsPerMinute: 275,
    level: 1,
    wordGlanceWordCount: 3,
    fontSize: 'buyuk',
    fontFamily: 'GoogleSans',
    accentColor: 'default',
    enableCountdown: true,
  });
  const [phase, setPhase] = useState<'idle' | 'countdown' | 'flashing' | 'cooldown'>('idle');
  const [countdown, setCountdown] = useState(3);
  const [currentWords, setCurrentWords] = useState<string[]>([]);
  const [phrasePosition, setPhrasePosition] = useState<PeripheryPosition>(WORD_GLANCE_INITIAL_POSITION);
  const [shownCount, setShownCount] = useState(0);
  const [remoteWordPool, setRemoteWordPool] = useState<string[]>([]);
  const [wordPoolError, setWordPoolError] = useState<string | null>(null);
  const [currentPhraseRevealDuration, setCurrentPhraseRevealDuration] = useState(() => getPilotRevealDuration(275, 1));

  const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || profile?.name || 'Kullanıcı';
  const userImage = user?.avatar || user?.media?.url || user?.profilePhoto?.url || null;
  const userInitials = userName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part.charAt(0).toUpperCase())
    .join('');
  const modeKey = getModeCanonicalKey(mode);
  const isCornerGlanceMode = modeKey === 'gozlerimizi-sektirerek-gorelim';
  const introParagraphs = isCornerGlanceMode
    ? [
      'Kelimeleri tek tek okumadan, ekranın farklı köşelerinde kısa süre görünen sözcük grubunu bir bütün olarak algılamaya çalışın.',
      'Her gösterimde gözünüzü yeni köşe alanına sektirerek kelime grubunu yakalayın; kelime sayısı ve zorluk arttıkça görsel alanınız daha yoğun çalışır.',
    ]
    : [
      'Kelimeleri tek tek okumadan, ekranda kısa süre görünen sözcük grubunu bir bütün olarak algılamaya çalışın.',
      'Kelime sayısını artırdıkça görsel alanınızı genişletir, zorluk seviyesini artırdıkça daha uzun ve karmaşık sözcüklerle çalışırsınız.',
  ];
  const isRunning = phase !== 'idle';
  const activeAccent = resolveAccentColor(settings.accentColor);
  const activeFontFamily = FONT_FAMILY_OPTIONS.find((item) => item.value === settings.fontFamily)?.css || settings.fontFamily;
  const phraseText = currentWords.join(' ');
  const activePhraseStyle = getAdaptiveFlashWordStyle(settings.fontSize, phraseText, isCornerGlanceMode ? 'periphery' : 'center');
  const phraseJustify = phrasePosition.textAlign === 'center'
    ? 'center'
    : phrasePosition.textAlign === 'right'
      ? 'flex-end'
      : 'flex-start';
  const normalizedReadingLevel = normalizeWordReadingDifficultyLevel(settings.level);
  const fallbackWordPool = useMemo(() => getWordGlanceFallbackPhrases(), []);
  const effectiveWordPool = useMemo(() => {
    const sourcePool = remoteWordPool.length > 0 ? remoteWordPool : fallbackWordPool;
    return filterWordGlancePhrasesByReadingDifficulty(sourcePool, normalizedReadingLevel);
  }, [fallbackWordPool, normalizedReadingLevel, remoteWordPool]);

  useEffect(() => {
    setSettings((prev) => {
      const sharedSettings = applySharedExerciseSettings(prev) as WordGlanceSettings;
      return {
        ...sharedSettings,
        accentColor: sharedSettings.accentColor || 'default',
        level: normalizeWordReadingDifficultyLevel(sharedSettings.level),
        wordGlanceWordCount: normalizeWordGlanceCount(sharedSettings.wordGlanceWordCount),
      };
    });
  }, []);

  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  useEffect(() => {
    effectiveWordPoolRef.current = effectiveWordPool;
  }, [effectiveWordPool]);

  useEffect(() => {
    if (!settingsOpen) return;

    persistSharedExerciseSettings(settings);
  }, [settings, settingsOpen]);

  useEffect(() => {
    let mounted = true;

    ApiService.getApi(`speed-reading/exercises/word-pools?type=bir-bakista-gorelim&level=${normalizedReadingLevel}&wordCount=${settings.wordGlanceWordCount}&limit=500`)
      .then((response: any) => {
        if (!mounted) return;
        const words = Array.isArray(response?.items)
          ? response.items.map((item: any) => String(item?.word || '').trim()).filter(Boolean)
          : [];
        setRemoteWordPool(words);
        setWordPoolError(null);
      })
      .catch((error) => {
        if (!mounted) return;
        console.error(`${mode.name} kelime havuzu yüklenemedi:`, error);
        setRemoteWordPool([]);
        setWordPoolError('Kelime havuzu veritabanından yüklenemedi; varsayılan kelimelerle devam ediliyor.');
      });

    return () => {
      mounted = false;
    };
  }, [normalizedReadingLevel, settings.wordGlanceWordCount]);

  const clearTimers = () => {
    if (flashTimeoutRef.current) {
      window.clearTimeout(flashTimeoutRef.current);
      flashTimeoutRef.current = null;
    }
    if (cooldownTimeoutRef.current) {
      window.clearTimeout(cooldownTimeoutRef.current);
      cooldownTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => () => clearTimers(), []);

  useEffect(() => {
    if (phase !== 'countdown' || countdown <= 0) return;

    const timer = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);

          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [phase, countdown]);

  useEffect(() => {
    if (phase !== 'countdown' || countdown !== 0) return;

    showNextPhrase();
  }, [phase, countdown]);

  function showNextPhrase() {
    clearTimers();
    const activeSettings = settingsRef.current || settings;
    const activeReadingLevel = normalizeWordReadingDifficultyLevel(activeSettings.level);
    const activeWordPool = effectiveWordPoolRef.current.length > 0
      ? effectiveWordPoolRef.current
      : filterWordGlancePhrasesByReadingDifficulty(fallbackWordPool, activeReadingLevel);

    const nextPhrase = pickWordGlancePhrase(activeWordPool, activeSettings.wordGlanceWordCount, activeReadingLevel);
    if (nextPhrase.length === 0) {
      setPhase('idle');
      setCurrentWords([]);
      return;
    }

    setCurrentWords(nextPhrase);
    if (isCornerGlanceMode) {
      setPhrasePosition((previousPosition) => getNextWordGlanceCornerPosition(previousPosition));
    }
    setShownCount((prev) => prev + 1);
    const revealDuration = getPilotRevealDuration(activeSettings.wordsPerMinute, activeReadingLevel);
    setCurrentPhraseRevealDuration(revealDuration);
    setPhase('flashing');

    flashTimeoutRef.current = window.setTimeout(() => {
      setCurrentWords([]);
      setPhase('cooldown');
      flashTimeoutRef.current = null;

      cooldownTimeoutRef.current = window.setTimeout(() => {
        cooldownTimeoutRef.current = null;
        showNextPhrase();
      }, getPilotCooldownDuration(activeSettings.wordsPerMinute, activeReadingLevel));
    }, revealDuration);
  }

  useEffect(() => {
    const nextKey = `${normalizedReadingLevel}:${settings.wordGlanceWordCount}`;
    const previousKey = wordGlanceSettingsKeyRef.current;
    wordGlanceSettingsKeyRef.current = nextKey;
    if (!previousKey || previousKey === nextKey) return;
    if (phase !== 'flashing' && phase !== 'cooldown') return;

    showNextPhrase();
  }, [normalizedReadingLevel, settings.wordGlanceWordCount]);

  const handleReset = () => {
    clearTimers();
    setPhase('idle');
    setCountdown(3);
    setCurrentWords([]);
    setPhrasePosition(WORD_GLANCE_INITIAL_POSITION);
    setShownCount(0);
  };

  const handleCloseExercise = async () => {
    if (document.fullscreenElement === fullscreenContainerRef.current) {
      try {
        await document.exitFullscreen();
      } catch (error) {
        console.error('Tam ekrandan çıkılamadı:', error);
      }
    }

    handleReset();
    onClose();
  };

  const handleToggleFullscreen = async () => {
    const container = fullscreenContainerRef.current;
    if (!container) return;

    try {
      if (document.fullscreenElement === container) {
        await document.exitFullscreen();
        return;
      }

      await container.requestFullscreen();
    } catch (error) {
      console.error('Tam ekran değiştirilemedi:', error);
    }
  };

  const handlePrimaryAction = async () => {
    if (isRunning) {
      handleReset();
      return;
    }

    if (settings.enableCountdown) {
      setCountdown(3);
      setPhase('countdown');
      return;
    }

    showNextPhrase();
  };

  return (
    <div
      ref={fullscreenContainerRef}
      className={`${isFullscreen ? 'h-screen p-3' : 'h-full p-6'} bg-[#f3f5f7]`}
    >
      <style>{FLASH_VISIBILITY_KEYFRAMES}</style>
      <div className={`${isFullscreen ? 'max-w-none' : 'max-w-[1260px]'} mx-auto h-full`}>
        <div className="h-full border border-[#e6edf1] rounded-xl bg-[#f8fafb] px-6 py-5 flex gap-5 relative">
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Target className="w-[19px] h-[19px]" style={getThemeChromeTextStyle()} />
                <div className="text-[20px] font-bold" style={getThemeChromeTextStyle()}>{mode.name}</div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setSettingsOpen(true)} className={EXERCISE_TOOL_BUTTON_CLASS} title="Ayarlar">
                  <Settings2 className="w-5 h-5" />
                </button>
                <button onClick={() => { void handleToggleFullscreen(); }} className={EXERCISE_TOOL_BUTTON_CLASS} title={isFullscreen ? 'Tam Ekrandan Çık' : 'Tam Ekran'}>
                  {isFullscreen ? <Shrink className="w-5 h-5" /> : <Expand className="w-5 h-5" />}
                </button>
                <button onClick={() => { void handleCloseExercise(); }} className={EXERCISE_TOOL_BUTTON_CLASS} title="Kapat">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="relative min-h-0 flex-1 border border-[#e8edf0] rounded-[10px] px-6 py-5 bg-white overflow-hidden">
              {phase === 'idle' && (
                <div className="h-full flex items-center justify-center">
                  <div className="max-w-[980px] translate-y-12 text-center">
                    {introParagraphs.map((paragraph, index) => (
                      <p
                        key={paragraph}
                        className={`mx-auto max-w-[900px] text-[20px] font-bold leading-[1.55] text-[#2e6276] ${index > 0 ? 'mt-8' : ''}`}
                      >
                        {paragraph}
                      </p>
                    ))}
                    {wordPoolError ? (
                      <p className="mx-auto mt-8 max-w-[780px] rounded-2xl border border-[#f3e2c2] bg-[#fffaf0] px-5 py-4 text-[15px] font-semibold leading-7 text-[#94611f]">
                        {wordPoolError}
                      </p>
                    ) : null}
                  </div>
                </div>
              )}

              {phase === 'countdown' && (
                <div className="h-full flex items-center justify-center">
                  <span className="text-7xl font-extrabold text-[#18485c]">{countdown}</span>
                </div>
              )}

              {phase === 'flashing' && isCornerGlanceMode && (
                <div
                  key={`corner-${phraseText}-${shownCount}`}
                  className="absolute flex flex-nowrap items-baseline gap-x-3 px-2 font-black"
                  style={{
                    ...phrasePosition,
                    ...activePhraseStyle,
                    lineHeight: 1.15,
                    justifyContent: phraseJustify,
                    fontFamily: activeFontFamily,
                    animation: `speedReadingFlashVisibility ${currentPhraseRevealDuration}ms linear forwards`,
                  }}
                >
                  {currentWords.map((word, index) => (
                    <span key={`${word}-${index}`} style={{ display: 'inline-block', whiteSpace: 'nowrap', verticalAlign: 'baseline' }}>
                      {renderWordGlanceWord(
                        word,
                        activeAccent,
                        index === 0 ? 'start' : index === currentWords.length - 1 ? 'end' : 'none'
                      )}
                    </span>
                  ))}
                </div>
              )}

              {phase === 'flashing' && !isCornerGlanceMode && (
                <div className="h-full flex items-center justify-center">
                  <div
                    key={`center-${phraseText}-${shownCount}`}
                    className="flex max-w-[92%] flex-wrap items-baseline justify-center gap-x-3 gap-y-2 text-center font-black"
                    style={{
                      ...activePhraseStyle,
                      lineHeight: 1.15,
                      fontFamily: activeFontFamily,
                      animation: `speedReadingFlashVisibility ${currentPhraseRevealDuration}ms linear forwards`,
                    }}
                  >
                    {currentWords.map((word, index) => (
                      <span key={`${word}-${index}`} style={{ display: 'inline-block', whiteSpace: 'nowrap', verticalAlign: 'baseline' }}>
                        {renderWordGlanceWord(
                          word,
                          activeAccent,
                          index === 0 ? 'start' : index === currentWords.length - 1 ? 'end' : 'none'
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {phase === 'cooldown' && (
                <div className="h-full flex items-center justify-center" />
              )}
            </div>
          </div>

          <aside className="hidden lg:flex w-[320px] shrink-0 h-full min-h-0 rounded-xl border border-[#e0eaee] bg-white p-5 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="flex items-center gap-3">
                {userImage ? (
                  <img src={userImage} alt={userName} className="w-11 h-11 rounded-full object-cover bg-[#daf0f7]" />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-[#daf0f7] text-[#146f8e] flex items-center justify-center font-bold">
                    {userInitials || 'K'}
                  </div>
                )}
                <div>
                  <div className="text-[#355c68] text-sm font-bold">{userName}</div>
                  <div className="text-[#94a8b3] text-xs">{normalizedReadingLevel}. Seviye</div>
                </div>
              </div>

              <div className="mt-6 flex flex-col items-center text-center">
                <div className="rounded-[2rem] bg-[#f7fafc] px-5 py-4">
                  <ModeIllustration mode={mode} />
                </div>
                <h3 className="mt-4 text-[32px] leading-[1.05] font-black" style={getThemeChromeTextStyle()}>{mode.name}</h3>
              </div>

              <div className="mt-8 grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-[13px] text-[#b1b8bd]">Gösterim</div>
                  <div className="mt-1 text-[20px] leading-none font-black text-[#194e62]">{shownCount}</div>
                </div>
                <div>
                  <div className="text-[13px] text-[#b1b8bd]">Kelime</div>
                  <div className="mt-1 text-[20px] leading-none font-black text-[#194e62]">{settings.wordGlanceWordCount}</div>
                </div>
                <div>
                  <div className="text-[13px] text-[#b1b8bd]">Seviye</div>
                  <div className="mt-1 text-[20px] leading-none font-black text-[#194e62]">{normalizedReadingLevel}</div>
                </div>
              </div>

              {phase === 'idle' && <SidebarPreparationCard paragraphs={introParagraphs} fontFamily={activeFontFamily} />}
            </div>

            <div className="shrink-0 pt-4">
              <button
                onClick={() => { void handlePrimaryAction(); }}
                className="w-full h-12 rounded-lg text-white font-bold text-base transition-colors inline-flex items-center justify-center gap-2"
                style={getThemeChromeButtonStyle()}
              >
                {isRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {isRunning ? 'Durdur' : 'Başlat'}
              </button>

              {(isRunning || shownCount > 0) && (
                <button
                  onClick={handleReset}
                  className="mt-4 w-full h-12 rounded-lg bg-[#f1f3f5] text-[#2d6175] font-bold text-base transition-colors hover:bg-[#e7ecef]"
                >
                  Sıfırla
                </button>
              )}
            </div>
          </aside>

          {isFullscreen && (
            <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 text-[12px] font-medium text-[#96a6af]">
              Tam ekrandan çıkmak için Esc tuşuna basın veya tıklayın
            </div>
          )}

          {settingsOpen && (
            <div className="fixed inset-0 z-[120] bg-black/55 backdrop-blur-[4px] flex items-center justify-center">
              <div className="w-[920px] bg-white rounded-xl shadow-lg p-6 select-none">
                <div className="flex items-center justify-between mb-5">
                  <h4 className="text-xl font-bold" style={getThemedLabelStyle()}>Egzersiz Ayarları</h4>
                  <button className="text-gray-500 hover:text-gray-700" onClick={() => setSettingsOpen(false)}>
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-7">
                  <div className="space-y-5">
                    <SettingsStepperRow
                      label="Gösterim Hızı"
                      value={`${shared.wordsPerMinuteToClosestPilotPaceMs(settings.wordsPerMinute)} ms`}
                      onDecrease={() => {
                        const currentPaceMs = shared.wordsPerMinuteToClosestPilotPaceMs(settings.wordsPerMinute);
                        const currentIndex = shared.PILOT_PACE_MS_OPTIONS.findIndex((paceMs) => paceMs === currentPaceMs);
                        const nextPaceMs = shared.PILOT_PACE_MS_OPTIONS[Math.max(0, currentIndex - 1)];
                        setSettings((prev) => ({
                          ...prev,
                          wordsPerMinute: shared.paceMsToWordsPerMinute(nextPaceMs),
                          paceMs: nextPaceMs,
                        }));
                      }}
                      onIncrease={() => {
                        const currentPaceMs = shared.wordsPerMinuteToClosestPilotPaceMs(settings.wordsPerMinute);
                        const currentIndex = shared.PILOT_PACE_MS_OPTIONS.findIndex((paceMs) => paceMs === currentPaceMs);
                        const nextPaceMs = shared.PILOT_PACE_MS_OPTIONS[Math.min(shared.PILOT_PACE_MS_OPTIONS.length - 1, currentIndex + 1)];
                        setSettings((prev) => ({
                          ...prev,
                          wordsPerMinute: shared.paceMsToWordsPerMinute(nextPaceMs),
                          paceMs: nextPaceMs,
                        }));
                      }}
                    />

                    <SettingsStepperRow
                      label="Kelime Sayısı"
                      value={`${settings.wordGlanceWordCount}`}
                      onDecrease={() => setSettings((prev) => ({ ...prev, wordGlanceWordCount: normalizeWordGlanceCount(prev.wordGlanceWordCount - 1) }))}
                      onIncrease={() => setSettings((prev) => ({ ...prev, wordGlanceWordCount: normalizeWordGlanceCount(prev.wordGlanceWordCount + 1) }))}
                    />

                    <SettingsStepperRow
                      label="Okunuş Zorluğu"
                      value={`${normalizedReadingLevel}. Seviye`}
                      onDecrease={() => setSettings((prev) => ({ ...prev, level: normalizeWordReadingDifficultyLevel(prev.level - 1) }))}
                      onIncrease={() => setSettings((prev) => ({ ...prev, level: normalizeWordReadingDifficultyLevel(prev.level + 1) }))}
                    />

                    <SettingsToggleRow
                      label="Başlamadan önce geriye say"
                      checked={settings.enableCountdown}
                      onChange={() => setSettings((prev) => ({ ...prev, enableCountdown: !prev.enableCountdown }))}
                    />
                  </div>

                  <div className="space-y-5 border-l border-[#edf2f5] pl-7">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Boyutu</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {FONT_SIZE_LABELS[settings.fontSize]}
                        </span>
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        {FONT_SIZE_OPTIONS.map((sizeOption) => {
                          const isActive = settings.fontSize === sizeOption.value;
                          return (
                            <button
                              key={sizeOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, fontSize: sizeOption.value }))}
                              className="h-10 rounded-lg border transition-colors"
                              style={getThemedOptionButtonStyle(isActive)}
                            >
                              <span className={`font-semibold ${sizeOption.preview}`}>{sizeOption.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <p className="mb-2 text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Karakteri</p>
                      <div className="grid grid-cols-2 gap-2">
                        {FONT_FAMILY_OPTIONS.map((fontOption) => {
                          const isActive = settings.fontFamily === fontOption.value;
                          return (
                            <button
                              key={fontOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, fontFamily: fontOption.value }))}
                              className="h-10 rounded-lg border text-sm transition-colors"
                              style={{ ...getThemedOptionButtonStyle(isActive), fontFamily: fontOption.css }}
                            >
                              {fontOption.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Vurgu Rengi</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {ACCENT_COLOR_OPTIONS.find((colorOption) => colorOption.value === settings.accentColor)?.label || 'Varsayılan'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        {ACCENT_COLOR_OPTIONS.map((colorOption) => {
                          const isActive = settings.accentColor === colorOption.value;
                          return (
                            <button
                              key={colorOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, accentColor: colorOption.value }))}
                              className="h-7 w-7 rounded-full border-2 transition-transform hover:scale-105"
                              style={{ ...getThemedSwatchStyle(isActive, colorOption.hex), backgroundColor: colorOption.hex }}
                              title={colorOption.label}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const PeripheryFocusExerciseView: React.FC<{
  profile: any;
  mode: ModeConfig;
  onClose: () => void;
}> = ({ profile, mode, onClose }) => {
  const { user } = useAuth();
  const fullscreenContainerRef = useRef<HTMLDivElement | null>(null);
  const flashTimeoutRef = useRef<number | null>(null);
  const answerInputRef = useRef<HTMLInputElement | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [muted, setMuted] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [settings, setSettings] = useState<PilotSettings>({
    wordsPerMinute: 275,
    level: 1,
    fontSize: 'buyuk',
    fontFamily: 'GoogleSans',
    accentColor: 'default',
    enableCountdown: true,
  });
  const [phase, setPhase] = useState<'idle' | 'countdown' | 'flashing' | 'answering'>('idle');
  const [countdown, setCountdown] = useState(3);
  const [currentWord, setCurrentWord] = useState('');
  const [expectedWord, setExpectedWord] = useState('');
  const [answer, setAnswer] = useState('');
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [wordPosition, setWordPosition] = useState<PeripheryPosition>({ right: '14%', top: '44%' });
  const [remoteWordPool, setRemoteWordPool] = useState<string[]>([]);
  const [wordPoolError, setWordPoolError] = useState<string | null>(null);

  const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || profile?.name || 'Kullanıcı';
  const userImage = user?.avatar || user?.media?.url || user?.profilePhoto?.url || null;
  const userInitials = userName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part.charAt(0).toUpperCase())
    .join('');
  const introParagraphs = PERIPHERY_FOCUS_INFO_PARAGRAPHS;
  const isRunning = phase !== 'idle';
  const activeAccent = resolveAccentColor(settings.accentColor);
  const activeFontFamily = FONT_FAMILY_OPTIONS.find((item) => item.value === settings.fontFamily)?.css || settings.fontFamily;
  const activePeripheryWordStyle = getAdaptiveFlashWordStyle(settings.fontSize, currentWord, 'periphery');
  useEffect(() => {
    setSettings((prev) => applySharedExerciseSettings(prev));
  }, []);

  useEffect(() => {
    if (!settingsOpen) return;
    persistSharedExerciseSettings(settings);
  }, [settings, settingsOpen]);

  useEffect(() => {
    let mounted = true;

    ApiService.getApi(`speed-reading/exercises/word-pools?type=takistoskop&level=${settings.level}&limit=200`)
      .then((response: any) => {
        if (!mounted) return;
        const words = Array.isArray(response?.items)
          ? response.items.map((item: any) => String(item?.word || '').trim()).filter(Boolean)
          : [];
        setRemoteWordPool(words);
        setWordPoolError(words.length > 0 ? null : 'Veritabanında Çevre Odak için kayıtlı kelime bulunamadı.');
      })
      .catch((error) => {
        if (!mounted) return;
        console.error('Çevre Odak kelime havuzu yüklenemedi:', error);
        setRemoteWordPool([]);
        setWordPoolError('Çevre Odak kelime havuzu veritabanından yüklenemedi.');
      });

    return () => {
      mounted = false;
    };
  }, [settings.level]);

  const clearFlashTimeout = () => {
    if (flashTimeoutRef.current) {
      window.clearTimeout(flashTimeoutRef.current);
      flashTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => () => clearFlashTimeout(), []);

  useEffect(() => {
    if (phase !== 'countdown' || countdown <= 0) return;

    const timer = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [phase, countdown]);

  useEffect(() => {
    if (phase !== 'countdown' || countdown !== 0) return;
    showNextWord();
  }, [phase, countdown]);

  useEffect(() => {
    if (phase !== 'answering') return;
    const focusTimer = window.setTimeout(() => answerInputRef.current?.focus(), 50);
    return () => window.clearTimeout(focusTimer);
  }, [phase, expectedWord]);

  const showNextWord = () => {
    clearFlashTimeout();
    if (remoteWordPool.length === 0) {
      setPhase('idle');
      setCurrentWord('');
      setExpectedWord('');
      return;
    }
    const nextWord = remoteWordPool[Math.floor(Math.random() * remoteWordPool.length)] || remoteWordPool[0];
    setExpectedWord(nextWord);
    setCurrentWord(nextWord.toLocaleUpperCase('tr-TR'));
    setWordPosition(getRandomPeripheryPosition());
    setAnswer('');
    setPhase('flashing');

    flashTimeoutRef.current = window.setTimeout(() => {
      setCurrentWord('');
      setPhase('answering');
      flashTimeoutRef.current = null;
    }, getPilotRevealDuration(settings.wordsPerMinute, settings.level));
  };

  const handleReset = () => {
    clearFlashTimeout();
    setPhase('idle');
    setCountdown(3);
    setCurrentWord('');
    setExpectedWord('');
    setAnswer('');
    setCorrectCount(0);
    setWrongCount(0);
    setWordPosition({ right: '14%', top: '44%' });
  };

  const handleCloseExercise = async () => {
    if (document.fullscreenElement === fullscreenContainerRef.current) {
      try {
        await document.exitFullscreen();
      } catch (error) {
        console.error('Tam ekrandan çıkılamadı:', error);
      }
    }

    handleReset();
    onClose();
  };

  const handleToggleFullscreen = async () => {
    const container = fullscreenContainerRef.current;
    if (!container) return;

    try {
      if (document.fullscreenElement === container) {
        await document.exitFullscreen();
        return;
      }

      await container.requestFullscreen();
    } catch (error) {
      console.error('Tam ekran değiştirilemedi:', error);
    }
  };

  const handlePrimaryAction = async () => {
    if (isRunning) {
      handleReset();
      return;
    }

    if (settings.enableCountdown) {
      setCountdown(3);
      setPhase('countdown');
      return;
    }

    showNextWord();
  };

  const finishSession = () => {
    clearFlashTimeout();
    setPhase('idle');
    setCurrentWord('');
    setExpectedWord('');
    setAnswer('');
  };

  const handleAnswerResult = (isCorrect: boolean) => {
    const nextCorrectCount = correctCount + (isCorrect ? 1 : 0);
    const nextWrongCount = wrongCount + (isCorrect ? 0 : 1);
    setCorrectCount(nextCorrectCount);
    setWrongCount(nextWrongCount);

    if (shouldStopForWrongGap(nextCorrectCount, nextWrongCount)) {
      finishSession();
      return;
    }

    showNextWord();
  };

  const handleSubmitAnswer = () => {
    if (phase !== 'answering' || !expectedWord) return;
    if (!answer.trim()) return;
    handleAnswerResult(normalizePilotAnswer(answer) === normalizePilotAnswer(expectedWord));
  };

  const handleMissedWord = () => {
    if (phase !== 'answering') return;
    handleAnswerResult(false);
  };

  return (
    <div
      ref={fullscreenContainerRef}
      className={`${isFullscreen ? 'h-screen p-3' : 'h-full p-6'} bg-[#f3f5f7]`}
    >
      <div className={`${isFullscreen ? 'max-w-none' : 'max-w-[1260px]'} mx-auto h-full`}>
        <div className="h-full border border-[#e6edf1] rounded-xl bg-[#f8fafb] px-6 py-5 flex gap-5 relative">
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ScanSearch className="w-[19px] h-[19px]" style={getThemeChromeTextStyle()} />
                <div className="text-[20px] font-bold" style={getThemeChromeTextStyle()}>{mode.name}</div>
              </div>
              <div className="flex items-center gap-3 text-[#afbbc2]">
                <button onClick={() => setMuted((m) => !m)} className="hover:text-[#6b7f8a]">
                  {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
                <button onClick={() => setSettingsOpen(true)} className="hover:text-[#6b7f8a]">
                  <Settings2 className="w-5 h-5" />
                </button>
                <button onClick={() => { void handleToggleFullscreen(); }} className="hover:text-[#6b7f8a]">
                  {isFullscreen ? <Shrink className="w-5 h-5" /> : <Expand className="w-5 h-5" />}
                </button>
                <button onClick={() => { void handleCloseExercise(); }} className="hover:text-[#6b7f8a]">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="relative min-h-0 flex-1 border border-[#e8edf0] rounded-[10px] px-6 py-5 bg-white overflow-hidden">
              {phase === 'idle' && (
                <div className="h-full flex items-center justify-center">
                  <div className="max-w-[980px] translate-y-12 text-center">
                    {introParagraphs.map((paragraph, index) => (
                      <p
                        key={paragraph}
                        className={`mx-auto max-w-[900px] text-[20px] font-bold leading-[1.55] text-[#2e6276] ${index > 0 ? 'mt-8' : ''}`}
                      >
                        {paragraph}
                      </p>
                    ))}
                    {wordPoolError ? (
                      <p className="mx-auto mt-8 max-w-[780px] rounded-2xl border border-[#f3d6d6] bg-[#fff5f5] px-5 py-4 text-[15px] font-semibold leading-7 text-[#a44b4b]">
                        {wordPoolError}
                      </p>
                    ) : null}
                  </div>
                </div>
              )}

              {phase === 'countdown' && (
                <div className="h-full flex items-center justify-center">
                  <span className="text-7xl font-extrabold text-[#18485c]">{countdown}</span>
                </div>
              )}

              {phase === 'flashing' && (
                <div
                  className="absolute px-2 font-black"
                  style={{ ...wordPosition, ...activePeripheryWordStyle, color: activeAccent, fontFamily: activeFontFamily }}
                >
                  {currentWord}
                </div>
              )}

              {phase === 'answering' && (
                <div className="h-full flex items-center justify-center">
                  <div className="w-full max-w-[600px] rounded-2xl border border-[#edf2f4] bg-white px-14 py-12 shadow-[0_18px_45px_rgba(18,71,94,0.08)]">
                    <h4 className="text-center text-[22px] font-black" style={{ color: activeAccent, fontFamily: activeFontFamily }}>Gördüğün kelimeyi hemen yaz!</h4>
                    <input
                      ref={answerInputRef}
                      value={answer}
                      onChange={(e) => setAnswer(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSubmitAnswer();
                        }
                      }}
                      placeholder="Cevabı buraya yazınız."
                      className="mt-8 h-14 w-full rounded-xl border border-[#edf1f4] bg-[#f6f7f8] px-5 text-[18px] font-semibold text-[#295567] outline-none placeholder:text-[#c4c8cb] focus:bg-white"
                      style={{ fontFamily: activeFontFamily, borderColor: answer.trim() ? `${activeAccent}55` : undefined }}
                    />
                    <div className="mt-10 grid grid-cols-2 gap-5">
                      <button
                        onClick={handleSubmitAnswer}
                        disabled={!answer.trim()}
                        className={`h-12 rounded-xl text-[18px] font-bold transition-colors ${answer.trim()
                          ? 'text-white'
                          : 'bg-[#dfe7ea] text-[#7f929c] cursor-not-allowed'
                          }`}
                        style={answer.trim() ? { backgroundColor: activeAccent, fontFamily: activeFontFamily } : { fontFamily: activeFontFamily }}
                      >
                        Cevapla
                      </button>
                      <button
                        onClick={handleMissedWord}
                        className="h-12 rounded-xl bg-[#f1f3f5] text-[18px] font-bold text-[#2d6175] transition-colors hover:bg-[#e8ecef]"
                        style={{ fontFamily: activeFontFamily }}
                      >
                        Göremedim
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <aside className="hidden lg:flex w-[320px] shrink-0 h-full min-h-0 rounded-xl border border-[#e0eaee] bg-white p-5 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="flex items-center gap-3">
                {userImage ? (
                  <img src={userImage} alt={userName} className="w-11 h-11 rounded-full object-cover bg-[#daf0f7]" />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-[#daf0f7] text-[#146f8e] flex items-center justify-center font-bold">
                    {userInitials || 'K'}
                  </div>
                )}
                <div>
                  <div className="text-[#355c68] text-sm font-bold">{userName}</div>
                  <div className="text-[#94a8b3] text-xs">1. Seviye</div>
                </div>
              </div>

              <div className="mt-6 flex flex-col items-center text-center">
                <div className="rounded-[2rem] bg-[#f7fafc] px-5 py-4">
                  <ModeIllustration mode={mode} />
                </div>
                <h3 className="mt-4 text-[34px] leading-none font-black" style={getThemeChromeTextStyle()}>{mode.name}</h3>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-6 text-center">
                <div>
                  <div className="text-[14px] text-[#b1b8bd]">Doğru</div>
                  <div className="mt-1 text-[20px] leading-none font-black text-[#194e62]">{correctCount}</div>
                </div>
                <div>
                  <div className="text-[14px] text-[#b1b8bd]">Yanlış</div>
                  <div className="mt-1 text-[20px] leading-none font-black text-[#194e62]">{wrongCount}</div>
                </div>
              </div>

              {phase === 'idle' && <SidebarPreparationCard paragraphs={introParagraphs} fontFamily={activeFontFamily} />}

            </div>

            <div className="shrink-0 pt-4">
              <button
                onClick={() => { void handlePrimaryAction(); }}
                className="w-full h-12 rounded-lg text-white font-bold text-base transition-colors inline-flex items-center justify-center gap-2"
                style={getThemeChromeButtonStyle()}
              >
                {isRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {isRunning ? 'Durdur' : 'Başlat'}
              </button>

              {(isRunning || correctCount > 0 || wrongCount > 0) && (
                <button
                  onClick={handleReset}
                  className="mt-4 w-full h-12 rounded-lg bg-[#f1f3f5] text-[#2d6175] font-bold text-base transition-colors hover:bg-[#e7ecef]"
                >
                  Sıfırla
                </button>
              )}
            </div>
          </aside>

          {isFullscreen && (
            <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 text-[12px] font-medium text-[#96a6af]">
              Tam ekrandan çıkmak için Esc tuşuna basın veya tıklayın
            </div>
          )}

          {settingsOpen && (
            <div className="fixed inset-0 z-[120] bg-black/55 backdrop-blur-[4px] flex items-center justify-center">
              <div className="w-[920px] bg-white rounded-xl shadow-lg p-6 select-none">
                <div className="flex items-center justify-between mb-5">
                  <h4 className="text-xl font-bold" style={getThemedLabelStyle()}>Egzersiz Ayarları</h4>
                  <button className="text-gray-500 hover:text-gray-700" onClick={() => setSettingsOpen(false)}>
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-7">
                  <div className="space-y-5">
                    <SettingsStepperRow
                      label="Gösterim Hızı"
                      value={`${shared.wordsPerMinuteToClosestPaceMs(settings.wordsPerMinute)} ms`}
                      onDecrease={() => {
                        const currentPaceMs = shared.wordsPerMinuteToClosestPaceMs(settings.wordsPerMinute);
                        const currentIndex = shared.PACE_MS_OPTIONS.findIndex((paceMs) => paceMs === currentPaceMs);
                        const nextPaceMs = shared.PACE_MS_OPTIONS[Math.max(0, currentIndex - 1)];
                        setSettings((prev) => ({
                          ...prev,
                          wordsPerMinute: shared.paceMsToWordsPerMinute(nextPaceMs),
                          paceMs: nextPaceMs,
                        }));
                      }}
                      onIncrease={() => {
                        const currentPaceMs = shared.wordsPerMinuteToClosestPaceMs(settings.wordsPerMinute);
                        const currentIndex = shared.PACE_MS_OPTIONS.findIndex((paceMs) => paceMs === currentPaceMs);
                        const nextPaceMs = shared.PACE_MS_OPTIONS[Math.min(shared.PACE_MS_OPTIONS.length - 1, currentIndex + 1)];
                        setSettings((prev) => ({
                          ...prev,
                          wordsPerMinute: shared.paceMsToWordsPerMinute(nextPaceMs),
                          paceMs: nextPaceMs,
                        }));
                      }}
                    />

                    <SettingsStepperRow
                      label="Seviye"
                      value={`${settings.level}`}
                      onDecrease={() => setSettings((prev) => ({ ...prev, level: Math.max(1, prev.level - 1) }))}
                      onIncrease={() => setSettings((prev) => ({ ...prev, level: Math.min(13, prev.level + 1) }))}
                    />

                    <SettingsToggleRow
                      label="Başlamadan önce geriye say"
                      checked={settings.enableCountdown}
                      onChange={() => setSettings((prev) => ({ ...prev, enableCountdown: !prev.enableCountdown }))}
                    />
                  </div>

                  <div className="space-y-5 border-l border-[#edf2f5] pl-7">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Boyutu</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {FONT_SIZE_LABELS[settings.fontSize]}
                        </span>
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        {FONT_SIZE_OPTIONS.map((sizeOption) => {
                          const isActive = settings.fontSize === sizeOption.value;
                          return (
                            <button
                              key={sizeOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, fontSize: sizeOption.value }))}
                              className="h-10 rounded-lg border transition-colors"
                              style={getThemedOptionButtonStyle(isActive)}
                            >
                              <span className={`font-semibold ${sizeOption.preview}`}>{sizeOption.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <p className="mb-2 text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Karakteri</p>
                      <div className="grid grid-cols-2 gap-2">
                        {FONT_FAMILY_OPTIONS.map((fontOption) => {
                          const isActive = settings.fontFamily === fontOption.value;
                          return (
                            <button
                              key={fontOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, fontFamily: fontOption.value }))}
                              className="h-10 rounded-lg border text-sm transition-colors"
                              style={{ ...getThemedOptionButtonStyle(isActive), fontFamily: fontOption.css }}
                            >
                              {fontOption.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Vurgu Rengi</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {ACCENT_COLOR_OPTIONS.find((colorOption) => colorOption.value === settings.accentColor)?.label || 'Varsayılan'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        {ACCENT_COLOR_OPTIONS.map((colorOption) => {
                          const isActive = settings.accentColor === colorOption.value;
                          return (
                            <button
                              key={colorOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, accentColor: colorOption.value }))}
                              className="h-7 w-7 rounded-full border-2 transition-transform hover:scale-105"
                              style={{ ...getThemedSwatchStyle(isActive, colorOption.hex), backgroundColor: colorOption.hex }}
                              title={colorOption.label}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const ConcreteMeaningExerciseView: React.FC<{
  profile: any;
  mode: ModeConfig;
  onClose: () => void;
}> = ({ profile, mode, onClose }) => {
  const { user } = useAuth();
  const fullscreenContainerRef = useRef<HTMLDivElement | null>(null);
  const revealTimeoutRef = useRef<number | null>(null);
  const cooldownTimeoutRef = useRef<number | null>(null);
  const transitionTimeoutRef = useRef<number | null>(null);
  const currentPromptRef = useRef<ConcreteMeaningWord | null>(null);
  const roundOpenRef = useRef(false);
  const phaseRef = useRef<'idle' | 'countdown' | 'flashing' | 'cooldown' | 'results'>('idle');
  const cooldownDurationRef = useRef(0);
  const cooldownEndsAtRef = useRef<number | null>(null);
  const correctCountRef = useRef(0);
  const wrongCountRef = useRef(0);
  const roundHistoryRef = useRef<ConcreteMeaningRoundResult[]>([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [muted, setMuted] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [settings, setSettings] = useState<PilotSettings>({
    wordsPerMinute: Math.max(275, Number(profile?.currentSpeed) || 275),
    level: 1,
    fontSize: 'buyuk',
    fontFamily: 'GoogleSans',
    accentColor: 'default',
    enableCountdown: true,
  });
  const [phase, setPhase] = useState<'idle' | 'countdown' | 'flashing' | 'cooldown' | 'results'>('idle');
  const [countdown, setCountdown] = useState(3);
  const [currentPrompt, setCurrentPrompt] = useState<ConcreteMeaningWord | null>(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [roundHistory, setRoundHistory] = useState<ConcreteMeaningRoundResult[]>([]);
  const [remoteWordPool, setRemoteWordPool] = useState<ConcreteMeaningWord[]>([]);
  const [wordsLoading, setWordsLoading] = useState(false);
  const [wordsError, setWordsError] = useState<string | null>(null);

  const isRunning = phase === 'countdown' || phase === 'flashing' || phase === 'cooldown';
  const resultsReady = phase === 'results';
  const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || profile?.name || 'Kullanıcı';
  const userImage = user?.avatar || user?.media?.url || user?.profilePhoto?.url || null;
  const userInitials = userName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part.charAt(0).toUpperCase())
    .join('');
  const introParagraphs = CONCRETE_MEANING_INFO_PARAGRAPHS;
  const activeAccent = resolveAccentColor(settings.accentColor);
  const activeFontFamily = FONT_FAMILY_OPTIONS.find((item) => item.value === settings.fontFamily)?.css || settings.fontFamily;
  const activeWordStyle = getAdaptiveFlashWordStyle(settings.fontSize, currentPrompt?.word || 'somut', 'center');
  useEffect(() => {
    setSettings((prev) => applySharedExerciseSettings(prev));
  }, []);

  useEffect(() => {
    if (!settingsOpen) return;
    persistSharedExerciseSettings(settings);
  }, [settings, settingsOpen]);

  useEffect(() => {
    let cancelled = false;

    const loadConcreteWords = async () => {
      setWordsLoading(true);
      setWordsError(null);

      try {
        const query = new URLSearchParams({
          level: String(settings.level),
          limit: '260',
        });
        const response = await ApiService.getApi(`speed-reading/exercises/concrete-meaning/words?${query.toString()}`);
        const normalizedPool: ConcreteMeaningWord[] = Array.isArray(response?.items)
          ? response.items
            .map((item: any) => ({
              word: String(item?.word || '').trim(),
              isConcrete: typeof item?.isConcrete === 'boolean' ? item.isConcrete : true,
            }))
            .filter((item: ConcreteMeaningWord) => item.word)
          : [];
        const fallbackPool = getConcreteMeaningFallbackWords(settings.level);
        const mergedPool = [...normalizedPool, ...fallbackPool].filter((item, index, list) => {
          const key = String(item.word || '').trim().toLocaleLowerCase('tr-TR');
          return key && list.findIndex((candidate) => String(candidate.word || '').trim().toLocaleLowerCase('tr-TR') === key) === index;
        });

        if (cancelled) return;
        setRemoteWordPool(mergedPool);
      } catch (error) {
        if (cancelled) return;
        setRemoteWordPool(getConcreteMeaningFallbackWords(settings.level));
        setWordsError('Somut Anlam kelime havuzu veritabanından yüklenemedi.');
      } finally {
        if (!cancelled) setWordsLoading(false);
      }
    };

    void loadConcreteWords();

    return () => {
      cancelled = true;
    };
  }, [settings.level]);

  const clearTimers = () => {
    if (revealTimeoutRef.current) {
      window.clearTimeout(revealTimeoutRef.current);
      revealTimeoutRef.current = null;
    }
    if (cooldownTimeoutRef.current) {
      window.clearTimeout(cooldownTimeoutRef.current);
      cooldownTimeoutRef.current = null;
    }
    if (transitionTimeoutRef.current) {
      window.clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => () => clearTimers(), []);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    if (phase !== 'countdown' || countdown <= 0) return;

    const timer = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [phase, countdown]);

  const finishSession = () => {
    clearTimers();
    roundOpenRef.current = false;
    currentPromptRef.current = null;
    cooldownEndsAtRef.current = null;
    setCurrentPrompt(null);
    setPhase('results');
  };

  const scheduleNextWord = (delayMs = 120) => {
    transitionTimeoutRef.current = window.setTimeout(() => {
      transitionTimeoutRef.current = null;
      showNextWord();
    }, Math.max(120, delayMs));
  };

  const finalizeRound = (choice: boolean | null) => {
    const activePrompt = currentPromptRef.current;
    if (!roundOpenRef.current || !activePrompt || !['flashing', 'cooldown'].includes(phaseRef.current)) return;

    const activePhase = phaseRef.current;
    const remainingCooldown = activePhase === 'cooldown' && cooldownEndsAtRef.current
      ? Math.max(0, cooldownEndsAtRef.current - Date.now())
      : cooldownDurationRef.current;

    roundOpenRef.current = false;
    currentPromptRef.current = null;
    clearTimers();
    cooldownEndsAtRef.current = null;

    const isCorrect = choice !== null && choice === activePrompt.isConcrete;
    const nextCorrectCount = correctCountRef.current + (isCorrect ? 1 : 0);
    const nextWrongCount = wrongCountRef.current + (isCorrect ? 0 : 1);
    const nextHistory = [
      ...roundHistoryRef.current,
      {
        word: activePrompt.word.toLocaleUpperCase('tr-TR'),
        isConcrete: activePrompt.isConcrete,
        userChoice: choice,
        isCorrect,
      },
    ];

    correctCountRef.current = nextCorrectCount;
    wrongCountRef.current = nextWrongCount;
    roundHistoryRef.current = nextHistory;

    setCorrectCount(nextCorrectCount);
    setWrongCount(nextWrongCount);
    setRoundHistory(nextHistory);
    setCurrentPrompt(null);

    const shouldFinish = shouldStopForWrongGap(nextCorrectCount, nextWrongCount);

    if (shouldFinish) {
      setPhase('results');
      return;
    }

    setPhase('cooldown');
    scheduleNextWord(remainingCooldown);
  };

  const showNextWord = () => {
    if (roundOpenRef.current && currentPromptRef.current) {
      finalizeRound(null);
      return;
    }

    clearTimers();
    if (remoteWordPool.length === 0) {
      setPhase('idle');
      setCurrentPrompt(null);
      return;
    }

    const nextPrompt = remoteWordPool[Math.floor(Math.random() * remoteWordPool.length)] || remoteWordPool[0];
    const effectiveWpm = settings.paceMs ? Math.round(60000 / Math.max(1, settings.paceMs)) : settings.wordsPerMinute;
    const revealDuration = getPilotRevealDuration(effectiveWpm, settings.level);
    const cooldownDuration = getPilotCooldownDuration(effectiveWpm, settings.level);

    cooldownDurationRef.current = cooldownDuration;
    cooldownEndsAtRef.current = null;
    currentPromptRef.current = nextPrompt;
    roundOpenRef.current = true;
    setCurrentPrompt(nextPrompt);
    setPhase('flashing');

    revealTimeoutRef.current = window.setTimeout(() => {
      revealTimeoutRef.current = null;

      if (!roundOpenRef.current) return;

      setCurrentPrompt(null);
      setPhase('cooldown');
      cooldownEndsAtRef.current = Date.now() + cooldownDuration;
      cooldownTimeoutRef.current = window.setTimeout(() => {
        cooldownTimeoutRef.current = null;
        finalizeRound(null);
      }, cooldownDuration);
    }, revealDuration);
  };

  useEffect(() => {
    if (phase !== 'countdown' || countdown !== 0) return;
    showNextWord();
  }, [phase, countdown]);

  useEffect(() => {
    if (!['flashing', 'cooldown'].includes(phase)) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        finalizeRound(false);
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        finalizeRound(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [phase]);

  const handleReset = () => {
    clearTimers();
    roundOpenRef.current = false;
    currentPromptRef.current = null;
    cooldownEndsAtRef.current = null;
    correctCountRef.current = 0;
    wrongCountRef.current = 0;
    roundHistoryRef.current = [];
    setPhase('idle');
    setCountdown(3);
    setCurrentPrompt(null);
    setCorrectCount(0);
    setWrongCount(0);
    setRoundHistory([]);
  };

  const handleCloseExercise = async () => {
    if (document.fullscreenElement === fullscreenContainerRef.current) {
      try {
        await document.exitFullscreen();
      } catch (error) {
        console.error('Tam ekrandan çıkılamadı:', error);
      }
    }

    handleReset();
    onClose();
  };

  const handleToggleFullscreen = async () => {
    const container = fullscreenContainerRef.current;
    if (!container) return;

    try {
      if (document.fullscreenElement === container) {
        await document.exitFullscreen();
        return;
      }

      await container.requestFullscreen();
    } catch (error) {
      console.error('Tam ekran değiştirilemedi:', error);
    }
  };

  const handlePrimaryAction = async () => {
    if (resultsReady) {
      await handleCloseExercise();
      return;
    }

    if (isRunning) {
      finishSession();
      return;
    }

    if (settings.enableCountdown) {
      setCountdown(3);
      setPhase('countdown');
      return;
    }

    showNextWord();
  };

  return (
    <div
      ref={fullscreenContainerRef}
      className={`${isFullscreen ? 'h-screen p-3' : 'h-full p-6'} bg-[#f3f5f7]`}
    >
      <div className={`${isFullscreen ? 'max-w-none' : 'max-w-[1260px]'} mx-auto h-full`}>
        <div className="h-full border border-[#e6edf1] rounded-xl bg-[#f8fafb] px-6 py-5 flex gap-5 relative">
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CircleHelp className="w-[19px] h-[19px]" style={getThemeChromeTextStyle()} />
                <div className="text-[20px] font-bold" style={getThemeChromeTextStyle()}>{mode.name}</div>
              </div>
              <div className="flex items-center gap-3 text-[#afbbc2]">
                <button onClick={() => setMuted((m) => !m)} className="hover:text-[#6b7f8a]">
                  {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
                <button onClick={() => setSettingsOpen(true)} className="hover:text-[#6b7f8a]">
                  <Settings2 className="w-5 h-5" />
                </button>
                <button onClick={() => { void handleToggleFullscreen(); }} className="hover:text-[#6b7f8a]">
                  {isFullscreen ? <Shrink className="w-5 h-5" /> : <Expand className="w-5 h-5" />}
                </button>
                <button onClick={() => { void handleCloseExercise(); }} className="hover:text-[#6b7f8a]">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="relative min-h-0 flex-1 border border-[#e8edf0] rounded-[10px] px-6 py-5 bg-white overflow-hidden">
              {(phase === 'flashing' || phase === 'cooldown') && (
                <>
                  <button
                    onClick={() => finalizeRound(false)}
                    className="absolute inset-y-0 left-0 z-10 w-1/2"
                    aria-label="Somut değil"
                    tabIndex={-1}
                  />
                  <button
                    onClick={() => finalizeRound(true)}
                    className="absolute inset-y-0 right-0 z-10 w-1/2"
                    aria-label="Somut"
                    tabIndex={-1}
                  />
                </>
              )}

              {phase === 'idle' && (
                <div className="h-full flex items-center justify-center">
                  <div className="max-w-[980px] translate-y-12 text-center">
                    {wordsLoading ? (
                      <div className="mx-auto max-w-[900px] flex flex-col items-center gap-4">
                        <div className="w-9 h-9 border-2 border-[#2f5563] border-t-transparent rounded-full animate-spin" />
                        <p className="text-[20px] font-bold leading-[1.55] text-[#2e6276]">İçerik yükleniyor...</p>
                      </div>
                    ) : (
                      introParagraphs.map((paragraph, index) => (
                        <p
                          key={paragraph}
                          className={`mx-auto max-w-[900px] text-[20px] font-bold leading-[1.55] text-[#2e6276] ${index > 0 ? 'mt-8' : ''}`}
                        >
                          {paragraph}
                        </p>
                      ))
                    )}
                    {!wordsLoading && wordsError && (
                      <p className="mx-auto mt-8 max-w-[900px] text-sm font-semibold text-[#5b7785]">
                        {wordsError}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {phase === 'countdown' && (
                <div className="h-full flex items-center justify-center">
                  <span className="text-7xl font-extrabold text-[#18485c]">{countdown}</span>
                </div>
              )}

              {phase === 'flashing' && currentPrompt && (
                <div className="pointer-events-none h-full flex items-center justify-center">
                  <div
                    className="px-6 text-center font-black uppercase"
                    style={{ ...activeWordStyle, color: activeAccent, fontFamily: activeFontFamily, maxWidth: '88%' }}
                  >
                    {currentPrompt.word.toLocaleUpperCase('tr-TR')}
                  </div>
                </div>
              )}

              {phase === 'results' && (
                <div className="h-full min-h-0 py-2">
                  <div className="h-full overflow-y-auto pr-2">
                    <div className="mx-auto flex w-full max-w-[560px] flex-col gap-6 pb-8">
                      {roundHistory.map((round, index) => (
                        <div key={`${round.word}-${index}`} className="grid grid-cols-[52px_1fr] items-center gap-10">
                          <div className="text-[18px] font-black text-[#2f6176]">
                            {String(index + 1).padStart(2, '0')}.
                          </div>
                          <div
                            className={`text-[18px] font-black ${round.isCorrect ? 'text-[#4fcf73]' : 'text-[#ff524e]'}`}
                            style={{ fontFamily: activeFontFamily }}
                          >
                            {round.word}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <aside className="hidden lg:flex w-[320px] shrink-0 h-full min-h-0 rounded-xl border border-[#e0eaee] bg-white p-5 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="flex items-center gap-3">
                {userImage ? (
                  <img src={userImage} alt={userName} className="w-11 h-11 rounded-full object-cover bg-[#daf0f7]" />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-[#daf0f7] text-[#146f8e] flex items-center justify-center font-bold">
                    {userInitials || 'K'}
                  </div>
                )}
                <div>
                  <div className="text-[#355c68] text-sm font-bold">{userName}</div>
                  <div className="text-[#94a8b3] text-xs">1. Seviye</div>
                </div>
              </div>

              {!resultsReady ? (
                <>
                  <div className="mt-6 flex flex-col items-center text-center">
                    <div className="rounded-[2rem] bg-[#f7fafc] px-5 py-4">
                      <ModeIllustration mode={mode} />
                    </div>
                    <h3 className="mt-4 text-[34px] leading-none font-black" style={getThemeChromeTextStyle()}>{mode.name}</h3>
                  </div>

                  <div className="mt-8 grid grid-cols-2 gap-6 text-center">
                    <div>
                      <div className="text-[14px] text-[#b1b8bd]">Doğru</div>
                      <div className="mt-1 text-[20px] leading-none font-black text-[#194e62]">{correctCount}</div>
                    </div>
                    <div>
                      <div className="text-[14px] text-[#b1b8bd]">Yanlış</div>
                      <div className="mt-1 text-[20px] leading-none font-black text-[#194e62]">{wrongCount}</div>
                    </div>
                  </div>

                  {phase === 'idle' && <SidebarPreparationCard paragraphs={introParagraphs} fontFamily={activeFontFamily} />}
                </>
              ) : (
                <div className="mt-28 px-6">
                  <h3 className="text-center text-[24px] font-black" style={getThemeChromeTextStyle()}>Sonuçlar</h3>
                  <div className="mt-8 space-y-4 text-[14px] text-[#b3bcc1]">
                    <div className="flex items-center justify-between border-b border-[#e8edf1] pb-3">
                      <span>Doğru</span>
                      <span className="text-[20px] font-black text-[#194e62]">{correctCount}</span>
                    </div>
                    <div className="flex items-center justify-between border-b border-[#e8edf1] pb-3">
                      <span>Yanlış</span>
                      <span className="text-[20px] font-black text-[#194e62]">{wrongCount}</span>
                    </div>
                    <div className="flex items-center justify-between border-b border-[#e8edf1] pb-3">
                      <span>Kelime / Dakika</span>
                      <span className="text-[20px] font-black text-[#194e62]">{settings.wordsPerMinute}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="shrink-0 pt-4">
              <button
                onClick={() => { void handlePrimaryAction(); }}
                disabled={phase === 'idle' && wordsLoading}
                className="w-full h-12 rounded-lg text-white font-bold text-base transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                style={getThemeChromeButtonStyle()}
              >
                {resultsReady ? null : isRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {resultsReady ? 'Tamamlayınız' : isRunning ? 'Durdur' : 'Başlat'}
              </button>

              {!resultsReady && (isRunning || correctCount > 0 || wrongCount > 0) && (
                <button
                  onClick={handleReset}
                  className="mt-4 w-full h-12 rounded-lg bg-[#f1f3f5] text-[#2d6175] font-bold text-base transition-colors hover:bg-[#e7ecef]"
                >
                  Sıfırla
                </button>
              )}
            </div>
          </aside>

          {isFullscreen && (
            <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 text-[12px] font-medium text-[#96a6af]">
              Tam ekrandan çıkmak için Esc tuşuna basın veya tıklayın
            </div>
          )}

          {settingsOpen && (
            <div className="fixed inset-0 z-[120] bg-black/55 backdrop-blur-[4px] flex items-center justify-center">
              <div className="w-[920px] bg-white rounded-xl shadow-lg p-6 select-none">
                <div className="flex items-center justify-between mb-5">
                  <h4 className="text-xl font-bold" style={getThemedLabelStyle()}>Egzersiz Ayarları</h4>
                  <button className="text-gray-500 hover:text-gray-700" onClick={() => setSettingsOpen(false)}>
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-7">
                  <div className="space-y-5">
                    <SettingsStepperRow
                      label="Gösterim Hızı"
                      value={`${shared.wordsPerMinuteToClosestPaceMs(settings.wordsPerMinute)} ms`}
                      onDecrease={() => {
                        const currentPaceMs = shared.wordsPerMinuteToClosestPaceMs(settings.wordsPerMinute);
                        const currentIndex = shared.PACE_MS_OPTIONS.findIndex((paceMs) => paceMs === currentPaceMs);
                        const nextPaceMs = shared.PACE_MS_OPTIONS[Math.max(0, currentIndex - 1)];
                        setSettings((prev) => ({
                          ...prev,
                          wordsPerMinute: shared.paceMsToWordsPerMinute(nextPaceMs),
                          paceMs: nextPaceMs,
                        }));
                      }}
                      onIncrease={() => {
                        const currentPaceMs = shared.wordsPerMinuteToClosestPaceMs(settings.wordsPerMinute);
                        const currentIndex = shared.PACE_MS_OPTIONS.findIndex((paceMs) => paceMs === currentPaceMs);
                        const nextPaceMs = shared.PACE_MS_OPTIONS[Math.min(shared.PACE_MS_OPTIONS.length - 1, currentIndex + 1)];
                        setSettings((prev) => ({
                          ...prev,
                          wordsPerMinute: shared.paceMsToWordsPerMinute(nextPaceMs),
                          paceMs: nextPaceMs,
                        }));
                      }}
                    />

                    <SettingsStepperRow
                      label="Seviye"
                      value={`${settings.level}`}
                      onDecrease={() => setSettings((prev) => ({ ...prev, level: Math.max(1, prev.level - 1) }))}
                      onIncrease={() => setSettings((prev) => ({ ...prev, level: Math.min(13, prev.level + 1) }))}
                    />

                    <SettingsToggleRow
                      label="Başlamadan önce geriye say"
                      checked={settings.enableCountdown}
                      onChange={() => setSettings((prev) => ({ ...prev, enableCountdown: !prev.enableCountdown }))}
                    />
                  </div>

                  <div className="space-y-5 border-l border-[#edf2f5] pl-7">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Boyutu</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {FONT_SIZE_LABELS[settings.fontSize]}
                        </span>
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        {FONT_SIZE_OPTIONS.map((sizeOption) => {
                          const isActive = settings.fontSize === sizeOption.value;
                          return (
                            <button
                              key={sizeOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, fontSize: sizeOption.value }))}
                              className="h-10 rounded-lg border transition-colors"
                              style={getThemedOptionButtonStyle(isActive)}
                            >
                              <span className={`font-semibold ${sizeOption.preview}`}>{sizeOption.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <p className="mb-2 text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Karakteri</p>
                      <div className="grid grid-cols-2 gap-2">
                        {FONT_FAMILY_OPTIONS.map((fontOption) => {
                          const isActive = settings.fontFamily === fontOption.value;
                          return (
                            <button
                              key={fontOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, fontFamily: fontOption.value }))}
                              className="h-10 rounded-lg border text-sm transition-colors"
                              style={{ ...getThemedOptionButtonStyle(isActive), fontFamily: fontOption.css }}
                            >
                              {fontOption.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Vurgu Rengi</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {ACCENT_COLOR_OPTIONS.find((colorOption) => colorOption.value === settings.accentColor)?.label || 'Varsayılan'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        {ACCENT_COLOR_OPTIONS.map((colorOption) => {
                          const isActive = settings.accentColor === colorOption.value;
                          return (
                            <button
                              key={colorOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, accentColor: colorOption.value }))}
                              className="h-7 w-7 rounded-full border-2 transition-transform hover:scale-105"
                              style={{ ...getThemedSwatchStyle(isActive, colorOption.hex), backgroundColor: colorOption.hex }}
                              title={colorOption.label}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const MatchingExerciseView: React.FC<{
  profile: any;
  mode: ModeConfig;
  onClose: () => void;
}> = ({ profile, mode, onClose }) => {
  const { user } = useAuth();
  const fullscreenContainerRef = useRef<HTMLDivElement | null>(null);
  const revealTimeoutRef = useRef<number | null>(null);
  const cooldownTimeoutRef = useRef<number | null>(null);
  const completedRoundsRef = useRef(0);
  const roundOpenRef = useRef(false);
  const currentRoundRef = useRef<{ left: string; right: string; isMatch: boolean } | null>(null);
  const phaseRef = useRef<'idle' | 'countdown' | 'flashing' | 'cooldown' | 'results'>('idle');
  const cooldownDurationRef = useRef(0);
  const cooldownEndsAtRef = useRef<number | null>(null);
  const correctCountRef = useRef(0);
  const wrongCountRef = useRef(0);
  const pairFetchDebounceRef = useRef<number | null>(null);
  const usedPairKeysRef = useRef<Set<string>>(new Set());
  const usedWordCountsRef = useRef<Record<string, number>>({});
  const generatedMatchCountRef = useRef(0);
  const generatedNonMatchCountRef = useRef(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [muted, setMuted] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [settings, setSettings] = useState<PilotSettings>({
    wordsPerMinute: 275,
    level: 1,
    fontSize: 'buyuk',
    fontFamily: 'GoogleSans',
    accentColor: 'default',
    enableCountdown: true,
  });
  const [phase, setPhase] = useState<'idle' | 'countdown' | 'flashing' | 'cooldown' | 'results'>('idle');
  const [countdown, setCountdown] = useState(3);
  const [leftWord, setLeftWord] = useState('');
  const [rightWord, setRightWord] = useState('');
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [roundHistory, setRoundHistory] = useState<MatchingRoundResult[]>([]);
  const [remotePairPool, setRemotePairPool] = useState<WordPair[]>([]);
  const [remoteWordPool, setRemoteWordPool] = useState<string[]>([]);
  const [pairsLoading, setPairsLoading] = useState(false);
  const [pairsError, setPairsError] = useState<string | null>(null);

  const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || profile?.name || 'Kullanıcı';
  const userImage = user?.avatar || user?.media?.url || user?.profilePhoto?.url || null;
  const userInitials = userName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part.charAt(0).toUpperCase())
    .join('');
  const modeKey = getModeCanonicalKey(mode);
  const isMultiFocusMode = modeKey === 'multi-fokus';
  const isAntonymMode = modeKey === 'zit-anlam';
  const isSynonymMode = modeKey === 'es-anlam';
  const activeWordDifficultyLevel = isMultiFocusMode ? normalizeWordReadingDifficultyLevel(settings.level) : settings.level;
  const matchingWordType = getMatchingWordTypeByModeName(mode.canonicalMode || mode.name);
  const introParagraphs = isSynonymMode
    ? SYNONYM_INFO_PARAGRAPHS
    : isAntonymMode
      ? ANTONYM_INFO_PARAGRAPHS
      : isMultiFocusMode
        ? MULTIFOCUS_INFO_PARAGRAPHS
        : MATCHING_INFO_PARAGRAPHS;
  const isRunning = phase === 'countdown' || phase === 'flashing' || phase === 'cooldown';
  const activeAccent = resolveAccentColor(settings.accentColor);
  const activeFontFamily = FONT_FAMILY_OPTIONS.find((item) => item.value === settings.fontFamily)?.css || settings.fontFamily;
  const leftWordStyle = getAdaptiveFlashWordStyle(settings.fontSize, leftWord, 'center');
  const rightWordStyle = getAdaptiveFlashWordStyle(settings.fontSize, rightWord, 'center');
  const totalRoundCount = Math.max(1, Math.ceil(settings.wordsPerMinute / 2));
  const resultsReady = phase === 'results';
  const hasUsablePool = matchingWordType ? remotePairPool.length > 0 : remoteWordPool.length > 0;
  const matchingPaceMs = shared.wordsPerMinuteToClosestPaceMs(settings.wordsPerMinute);
  const matchingPaceIndex = shared.PACE_MS_OPTIONS.findIndex((paceMs) => paceMs === matchingPaceMs);
  const isPrimaryDisabled = (
    (!resultsReady && matchingWordType && (pairsLoading || !hasUsablePool))
    || (!resultsReady && !matchingWordType && (pairsLoading || !hasUsablePool))
  );
  useEffect(() => {
    setSettings((prev) => {
      const sharedSettings = applySharedExerciseSettings(prev);
      return isMultiFocusMode
        ? { ...sharedSettings, level: normalizeWordReadingDifficultyLevel(sharedSettings.level) }
        : sharedSettings;
    });
  }, [isMultiFocusMode]);

  useEffect(() => {
    if (!settingsOpen) return;
    persistSharedExerciseSettings(settings);
  }, [settings, settingsOpen]);

  useEffect(() => {
    let mounted = true;

    if (!matchingWordType) {
      setRemotePairPool([]);
      setPairsError(null);
      setPairsLoading(false);
      return;
    }

    if (pairFetchDebounceRef.current) {
      window.clearTimeout(pairFetchDebounceRef.current);
      pairFetchDebounceRef.current = null;
    }

    pairFetchDebounceRef.current = window.setTimeout(() => {
      setPairsLoading(true);
      setPairsError(null);

      ApiService.getApi(`speed-reading/exercises/matching/words?type=${matchingWordType}&level=${settings.level}&limit=300`)
        .then((response: MatchingWordApiResponse) => {
          if (!mounted) return;
          const mapped = mapMatchingApiItemsToPairs(Array.isArray(response?.items) ? response.items : []);
          const fallbackPairs = getMatchingPairFallback(mode.canonicalMode || mode.name, settings.level);
          const mergedPairs = mapped.length > 0 ? mapped : fallbackPairs;
          setRemotePairPool(mergedPairs);
          setPairsError(mergedPairs.length === 0 ? 'Veritabanında bu seviyeye uygun kelime bulunamadı.' : null);
        })
        .catch((error) => {
          if (!mounted) return;
          console.error('Eş/Zıt anlam kelimeleri yüklenemedi:', error);
          setRemotePairPool(getMatchingPairFallback(mode.canonicalMode || mode.name, settings.level));
          setPairsError('Kelimeler veritabanından yüklenemedi.');
        })
        .finally(() => {
          if (!mounted) return;
          setPairsLoading(false);
        });
    }, 180);

    return () => {
      mounted = false;
      if (pairFetchDebounceRef.current) {
        window.clearTimeout(pairFetchDebounceRef.current);
        pairFetchDebounceRef.current = null;
      }
    };
  }, [matchingWordType, mode.canonicalMode, mode.name, settings.level]);

  useEffect(() => {
    let mounted = true;

    if (matchingWordType || (!isMultiFocusMode && modeKey !== 'eslestirme')) {
      setRemoteWordPool([]);
      return;
    }

    setPairsLoading(true);
    setPairsError(null);

    const poolType = isMultiFocusMode ? 'multi-fokus' : 'eslestirme';
    ApiService.getApi(`speed-reading/exercises/word-pools?type=${poolType}&level=${activeWordDifficultyLevel}&limit=300`)
      .then((response: any) => {
        if (!mounted) return;
        const words = Array.isArray(response?.items)
          ? response.items.map((item: any) => String(item?.word || '').trim()).filter(Boolean)
          : [];
        const fallbackWords = isMultiFocusMode ? getMultifocusFallbackWords(activeWordDifficultyLevel) : (PILOT_TEST_LEVEL_WORDS[settings.level] || []);
        const mergedWords = [...words, ...fallbackWords].filter((word, index, list) => {
          const key = String(word || '').trim().toLocaleLowerCase('tr-TR');
          return key && list.findIndex((candidate) => String(candidate || '').trim().toLocaleLowerCase('tr-TR') === key) === index;
        });
        setRemoteWordPool(isMultiFocusMode ? filterWordsByReadingDifficulty(mergedWords, activeWordDifficultyLevel) : mergedWords);
        if (mergedWords.length === 0) {
          setPairsError('Kelime havuzu veritabanında boş.');
        }
      })
      .catch((error) => {
        if (!mounted) return;
        console.error('Kelime havuzu yüklenemedi:', error);
        setRemoteWordPool(isMultiFocusMode ? getMultifocusFallbackWords(activeWordDifficultyLevel) : (PILOT_TEST_LEVEL_WORDS[settings.level] || []));
        setPairsError('Kelime havuzu veritabanından yüklenemedi.');
      })
      .finally(() => {
        if (!mounted) return;
        setPairsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [activeWordDifficultyLevel, isMultiFocusMode, matchingWordType, modeKey, settings.level]);

  const clearRoundTimeouts = () => {
    if (revealTimeoutRef.current) {
      window.clearTimeout(revealTimeoutRef.current);
      revealTimeoutRef.current = null;
    };

    if (cooldownTimeoutRef.current) {
      window.clearTimeout(cooldownTimeoutRef.current);
      cooldownTimeoutRef.current = null;
    };
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => () => clearRoundTimeouts(), []);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    if (phase !== 'countdown' || countdown <= 0) return;

    const timer = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [phase, countdown]);

  useEffect(() => {
    if (phase !== 'countdown' || countdown !== 0) return;
    showNextPair();
  }, [phase, countdown]);

  useEffect(() => {
    if (!['flashing', 'cooldown'].includes(phase)) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handleAnswer(false);
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleAnswer(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [phase]);

  const finishSession = () => {
    clearRoundTimeouts();
    roundOpenRef.current = false;
    currentRoundRef.current = null;
    cooldownEndsAtRef.current = null;
    setLeftWord('');
    setRightWord('');
    setPhase('results');
  };

  const startCooldown = (delayMs: number) => {
    setLeftWord('');
    setRightWord('');
    setPhase('cooldown');
    cooldownTimeoutRef.current = window.setTimeout(() => {
      cooldownTimeoutRef.current = null;
      showNextPair();
    }, Math.max(120, delayMs));
  };

  const showNextPair = () => {
    if (roundOpenRef.current && currentRoundRef.current) {
      finalizeRound(null);
      return;
    }

    if (completedRoundsRef.current >= totalRoundCount) {
      finishSession();
      return;
    }

    clearRoundTimeouts();
    const desiredMatchCount = Math.floor(totalRoundCount / 2);
    const desiredNonMatchCount = totalRoundCount - desiredMatchCount;
    const roundsLeftIncludingCurrent = totalRoundCount - completedRoundsRef.current;
    const matchesNeeded = desiredMatchCount - generatedMatchCountRef.current;
    const nonMatchesNeeded = desiredNonMatchCount - generatedNonMatchCountRef.current;
    let forcedMatch: boolean | undefined;

    if (matchesNeeded >= roundsLeftIncludingCurrent) {
      forcedMatch = true;
    } else if (nonMatchesNeeded >= roundsLeftIncludingCurrent) {
      forcedMatch = false;
    } else if (matchesNeeded <= 0) {
      forcedMatch = false;
    } else if (nonMatchesNeeded <= 0) {
      forcedMatch = true;
    }

    const pair = getMatchingPair(activeWordDifficultyLevel, mode.canonicalMode || mode.name, remotePairPool, {
      usedPairKeys: usedPairKeysRef.current,
      usedWordCounts: usedWordCountsRef.current,
      forcedMatch,
      dynamicWords: remoteWordPool,
    });
    if (!pair) {
      setPhase('idle');
      setLeftWord('');
      setRightWord('');
      setPairsError('Bu egzersiz için veritabanında kullanılabilir kayıt bulunamadı.');
      return;
    }
    setPairsError(null);
    if (pair.isMatch) generatedMatchCountRef.current += 1;
    else generatedNonMatchCountRef.current += 1;
    const pairKey = buildMatchingRoundKey(pair.left, pair.right, mode.canonicalMode || mode.name);
    usedPairKeysRef.current.add(pairKey);
    const leftKey = normalizeMatchingWordKey(pair.left);
    const rightKey = normalizeMatchingWordKey(pair.right);
    usedWordCountsRef.current[leftKey] = (usedWordCountsRef.current[leftKey] || 0) + 1;
    usedWordCountsRef.current[rightKey] = (usedWordCountsRef.current[rightKey] || 0) + 1;
    const upperLeft = pair.left.toLocaleUpperCase('tr-TR');
    const upperRight = pair.right.toLocaleUpperCase('tr-TR');
    const cooldownDuration = getPilotCooldownDuration(settings.wordsPerMinute, activeWordDifficultyLevel);
    currentRoundRef.current = { left: upperLeft, right: upperRight, isMatch: pair.isMatch };
    cooldownDurationRef.current = cooldownDuration;
    cooldownEndsAtRef.current = null;
    setLeftWord(upperLeft);
    setRightWord(upperRight);
    setPhase('flashing');
    roundOpenRef.current = true;

    revealTimeoutRef.current = window.setTimeout(() => {
      revealTimeoutRef.current = null;

      if (!roundOpenRef.current) return;

      setLeftWord('');
      setRightWord('');
      setPhase('cooldown');
      cooldownEndsAtRef.current = Date.now() + cooldownDuration;
      cooldownTimeoutRef.current = window.setTimeout(() => {
        cooldownTimeoutRef.current = null;
        finalizeRound(null);
      }, cooldownDuration);
    }, getMatchingRevealDuration(settings.wordsPerMinute, activeWordDifficultyLevel));
  };

  const handleReset = () => {
    clearRoundTimeouts();
    completedRoundsRef.current = 0;
    roundOpenRef.current = false;
    currentRoundRef.current = null;
    cooldownEndsAtRef.current = null;
    setPhase('idle');
    setCountdown(3);
    setLeftWord('');
    setRightWord('');
    setCorrectCount(0);
    setWrongCount(0);
    correctCountRef.current = 0;
    wrongCountRef.current = 0;
    usedPairKeysRef.current = new Set();
    usedWordCountsRef.current = {};
    generatedMatchCountRef.current = 0;
    generatedNonMatchCountRef.current = 0;
    setRoundHistory([]);
    setPairsError(null);
  };

  const handleCloseExercise = async () => {
    if (document.fullscreenElement === fullscreenContainerRef.current) {
      try {
        await document.exitFullscreen();
      } catch (error) {
        console.error('Tam ekrandan çıkılamadı:', error);
      }
    }

    handleReset();
    onClose();
  };

  const handleToggleFullscreen = async () => {
    const container = fullscreenContainerRef.current;
    if (!container) return;

    try {
      if (document.fullscreenElement === container) {
        await document.exitFullscreen();
        return;
      }

      await container.requestFullscreen();
    } catch (error) {
      console.error('Tam ekran değiştirilemedi:', error);
    }
  };

  const handlePrimaryAction = async () => {
    if (!resultsReady && (pairsLoading || !hasUsablePool)) return;

    if (resultsReady) {
      await handleCloseExercise();
      return;
    }

    if (isRunning) {
      handleReset();
      return;
    }

    if (settings.enableCountdown) {
      setCountdown(3);
      setPhase('countdown');
      return;
    }

    showNextPair();
  };

  const finalizeRound = (choice: boolean | null) => {
    const activeRound = currentRoundRef.current;
    if (!activeRound || !roundOpenRef.current || !['flashing', 'cooldown'].includes(phaseRef.current)) return;

    const activePhase = phaseRef.current;
    const remainingCooldown = activePhase === 'cooldown' && cooldownEndsAtRef.current
      ? Math.max(0, cooldownEndsAtRef.current - Date.now())
      : cooldownDurationRef.current;

    roundOpenRef.current = false;
    currentRoundRef.current = null;
    cooldownEndsAtRef.current = null;
    if (revealTimeoutRef.current) {
      window.clearTimeout(revealTimeoutRef.current);
      revealTimeoutRef.current = null;
    }
    if (cooldownTimeoutRef.current) {
      window.clearTimeout(cooldownTimeoutRef.current);
      cooldownTimeoutRef.current = null;
    }

    setLeftWord('');
    setRightWord('');

    const isCorrect = choice !== null && choice === activeRound.isMatch;
    const nextCompletedRounds = completedRoundsRef.current + 1;
    const nextCorrectCount = correctCountRef.current + (isCorrect ? 1 : 0);
    const nextWrongCount = wrongCountRef.current + (isCorrect ? 0 : 1);
    completedRoundsRef.current = nextCompletedRounds;
    correctCountRef.current = nextCorrectCount;
    wrongCountRef.current = nextWrongCount;

    setCorrectCount(nextCorrectCount);
    setWrongCount(nextWrongCount);

    setRoundHistory((prev) => [
      ...prev,
      {
        left: activeRound.left,
        right: activeRound.right,
        isMatch: activeRound.isMatch,
        userChoice: choice,
        isCorrect,
      },
    ]);

    if (nextCompletedRounds >= totalRoundCount) {
      finishSession();
      return;
    }

    startCooldown(remainingCooldown);
  };

  const handleAnswer = (choice: boolean) => {
    finalizeRound(choice);
  };

  return (
    <div
      ref={fullscreenContainerRef}
      className={`${isFullscreen ? 'h-screen p-3' : 'h-full p-6'} bg-[#f3f5f7]`}
    >
      <div className={`${isFullscreen ? 'max-w-none' : 'max-w-[1260px]'} mx-auto h-full`}>
        <div className="h-full border border-[#e6edf1] rounded-xl bg-[#f8fafb] px-6 py-5 flex gap-5 relative">
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ChevronLeft className="w-[19px] h-[19px]" style={getThemeChromeTextStyle()} />
                <div className="text-[20px] font-bold" style={getThemeChromeTextStyle()}>{mode.name}</div>
              </div>
              <div className="flex items-center gap-3 text-[#afbbc2]">
                <button onClick={() => setMuted((m) => !m)} className="hover:text-[#6b7f8a]">
                  {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
                <button onClick={() => setSettingsOpen(true)} className="hover:text-[#6b7f8a]">
                  <Settings2 className="w-5 h-5" />
                </button>
                <button onClick={() => { void handleToggleFullscreen(); }} className="hover:text-[#6b7f8a]">
                  {isFullscreen ? <Shrink className="w-5 h-5" /> : <Expand className="w-5 h-5" />}
                </button>
                <button onClick={() => { void handleCloseExercise(); }} className="hover:text-[#6b7f8a]">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="relative min-h-0 flex-1 border border-[#e8edf0] rounded-[10px] px-6 py-5 bg-white overflow-hidden">
              {(phase === 'flashing' || phase === 'cooldown') && (
                <>
                  <button
                    onClick={() => handleAnswer(false)}
                    className="absolute inset-y-0 left-0 z-10 w-1/2"
                    aria-label="Sol seçim"
                    tabIndex={-1}
                  />
                  <button
                    onClick={() => handleAnswer(true)}
                    className="absolute inset-y-0 right-0 z-10 w-1/2"
                    aria-label="Sağ seçim"
                    tabIndex={-1}
                  />
                </>
              )}

              {phase === 'idle' && (
                <div className="h-full flex items-center justify-center">
                  <div className="max-w-[980px] translate-y-12 text-center">
                    {introParagraphs.map((paragraph, index) => (
                      <p
                        key={paragraph}
                        className={`mx-auto max-w-[900px] text-[20px] font-bold leading-[1.55] text-[#2e6276] ${index > 0 ? 'mt-8' : ''}`}
                      >
                        {paragraph}
                      </p>
                    ))}
                    {pairsError ? (
                      <p className="mx-auto mt-8 max-w-[780px] rounded-2xl border border-[#f3d6d6] bg-[#fff5f5] px-5 py-4 text-[15px] font-semibold leading-7 text-[#a44b4b]">
                        {pairsError}
                      </p>
                    ) : null}
                  </div>
                </div>
              )}

              {phase === 'countdown' && (
                <div className="h-full flex items-center justify-center">
                  <span className="text-7xl font-extrabold text-[#18485c]">{countdown}</span>
                </div>
              )}

              {phase === 'flashing' && (
                <div className="pointer-events-none h-full flex items-center justify-center">
                  <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-8 w-full max-w-[760px] px-10">
                    <div
                      className="text-center font-black"
                      style={{ ...leftWordStyle, color: activeAccent, fontFamily: activeFontFamily }}
                    >
                      {leftWord}
                    </div>
                    <span className="h-10 w-px bg-[#d7e2e8]" />
                    <div
                      className="text-center font-black"
                      style={{ ...rightWordStyle, color: activeAccent, fontFamily: activeFontFamily }}
                    >
                      {rightWord}
                    </div>
                  </div>
                </div>
              )}

              {phase === 'results' && (
                <div className="h-full min-h-0 py-2">
                  <div className="h-full overflow-y-auto pr-2">
                    <div className="mx-auto flex w-full max-w-[560px] flex-col gap-6 pb-8">
                      {roundHistory.map((round, index) => {
                        const toneClass = round.isCorrect ? 'text-[#56d673]' : 'text-[#ff524e]';
                        return (
                          <div key={`${round.left}-${round.right}-${index}`} className="grid grid-cols-[52px_1fr_1fr] items-center gap-10">
                            <div className="text-[18px] font-black text-[#2f6176]">
                              {String(index + 1).padStart(2, '0')}.
                            </div>
                            <div className={`text-[18px] font-black ${toneClass}`} style={{ fontFamily: activeFontFamily }}>
                              {round.left}
                            </div>
                            <div className={`text-[18px] font-black ${toneClass}`} style={{ fontFamily: activeFontFamily }}>
                              {round.right}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {phase === 'cooldown' && (
                <div className="h-full" />
              )}
            </div>
          </div>

          <aside className="hidden lg:flex w-[320px] shrink-0 h-full min-h-0 rounded-xl border border-[#e0eaee] bg-white p-5 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="flex items-center gap-3">
                {userImage ? (
                  <img src={userImage} alt={userName} className="w-11 h-11 rounded-full object-cover bg-[#daf0f7]" />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-[#daf0f7] text-[#146f8e] flex items-center justify-center font-bold">
                    {userInitials || 'K'}
                  </div>
                )}
                <div>
                  <div className="text-[#355c68] text-sm font-bold">{userName}</div>
                  <div className="text-[#94a8b3] text-xs">{activeWordDifficultyLevel}. Seviye</div>
                </div>
              </div>

              {!resultsReady ? (
                <>
                  <div className="mt-6 flex flex-col items-center text-center">
                    <div className="rounded-[2rem] bg-[#f7fafc] px-5 py-4">
                      <ModeIllustration mode={mode} />
                    </div>
                    <h3 className="mt-4 text-[34px] leading-none font-black" style={getThemeChromeTextStyle()}>{mode.name}</h3>
                  </div>

                  <div className="mt-8 grid grid-cols-2 gap-6 text-center">
                    <div>
                      <div className="text-[14px] text-[#b1b8bd]">Doğru</div>
                      <div className="mt-1 text-[20px] leading-none font-black text-[#194e62]">{correctCount}</div>
                    </div>
                    <div>
                      <div className="text-[14px] text-[#b1b8bd]">Yanlış</div>
                      <div className="mt-1 text-[20px] leading-none font-black text-[#194e62]">{wrongCount}</div>
                    </div>
                  </div>

                  {phase === 'idle' && <SidebarPreparationCard paragraphs={introParagraphs} fontFamily={activeFontFamily} />}
                </>
              ) : (
                <div className="mt-28 px-6">
                  <h3 className="text-center text-[24px] font-black" style={getThemeChromeTextStyle()}>Sonuçlar</h3>

                  <div className="mt-8 space-y-4 text-[14px] text-[#b3bcc1]">
                    <div className="flex items-center justify-between border-b border-[#e8edf1] pb-3">
                      <span>Doğru</span>
                      <span className="text-[20px] font-black text-[#194e62]">{correctCount}</span>
                    </div>
                    <div className="flex items-center justify-between border-b border-[#e8edf1] pb-3">
                      <span>Yanlış</span>
                      <span className="text-[20px] font-black text-[#194e62]">{wrongCount}</span>
                    </div>
                    <div className="flex items-center justify-between border-b border-[#e8edf1] pb-3">
                      <span>Kelime / Dakika</span>
                      <span className="text-[20px] font-black text-[#194e62]">{settings.wordsPerMinute}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="shrink-0 pt-4">
              <button
                onClick={() => { void handlePrimaryAction(); }}
                disabled={isPrimaryDisabled}
                className={`w-full h-12 rounded-lg font-bold text-base transition-colors inline-flex items-center justify-center gap-2 ${isPrimaryDisabled ? 'cursor-not-allowed bg-[#dfe7ea] text-[#7f929c]' : 'text-white'}`}
                style={isPrimaryDisabled ? undefined : getThemeChromeButtonStyle()}
              >
                {resultsReady ? null : isRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {resultsReady ? 'Tamamlayınız' : isRunning ? 'Durdur' : 'Başlat'}
              </button>

              {!resultsReady && (isRunning || correctCount > 0 || wrongCount > 0) && (
                <button
                  onClick={handleReset}
                  className="mt-4 w-full h-12 rounded-lg bg-[#f1f3f5] text-[#2d6175] font-bold text-base transition-colors hover:bg-[#e7ecef]"
                >
                  Sıfırla
                </button>
              )}
            </div>
          </aside>

          {isFullscreen && (
            <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 text-[12px] font-medium text-[#96a6af]">
              Tam ekrandan çıkmak için Esc tuşuna basın veya tıklayın
            </div>
          )}

          {settingsOpen && (
            <div className="fixed inset-0 z-[120] bg-black/55 backdrop-blur-[4px] flex items-center justify-center">
              <div className="w-[920px] bg-white rounded-xl shadow-lg p-6 select-none">
                <div className="flex items-center justify-between mb-5">
                  <h4 className="text-xl font-bold" style={getThemedLabelStyle()}>
                    Egzersiz Ayarları
                  </h4>
                  <button className="text-gray-500 hover:text-gray-700" onClick={() => setSettingsOpen(false)}>
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-7">
                  <div className="space-y-5">
                    <SettingsStepperRow
                      label="Gösterim Hızı"
                      value={`${matchingPaceMs} ms`}
                      onDecrease={() => {
                        const nextPaceMs = shared.PACE_MS_OPTIONS[Math.max(0, matchingPaceIndex - 1)];
                        setSettings((prev) => ({
                          ...prev,
                          wordsPerMinute: shared.paceMsToWordsPerMinute(nextPaceMs),
                          paceMs: nextPaceMs,
                        }));
                      }}
                      onIncrease={() => {
                        const nextPaceMs = shared.PACE_MS_OPTIONS[Math.min(shared.PACE_MS_OPTIONS.length - 1, matchingPaceIndex + 1)];
                        setSettings((prev) => ({
                          ...prev,
                          wordsPerMinute: shared.paceMsToWordsPerMinute(nextPaceMs),
                          paceMs: nextPaceMs,
                        }));
                      }}
                    />

                    <SettingsStepperRow
                      label={isMultiFocusMode ? 'Okunuş Zorluğu' : 'Seviye'}
                      value={`${activeWordDifficultyLevel}. Seviye`}
                      onDecrease={() => setSettings((prev) => ({
                        ...prev,
                        level: isMultiFocusMode
                          ? normalizeWordReadingDifficultyLevel(prev.level - 1)
                          : Math.max(1, prev.level - 1),
                      }))}
                      onIncrease={() => setSettings((prev) => ({
                        ...prev,
                        level: isMultiFocusMode
                          ? normalizeWordReadingDifficultyLevel(prev.level + 1)
                          : Math.min(13, prev.level + 1),
                      }))}
                    />

                    <SettingsToggleRow
                      label="Başlamadan önce geriye say"
                      checked={settings.enableCountdown}
                      onChange={() => setSettings((prev) => ({ ...prev, enableCountdown: !prev.enableCountdown }))}
                    />
                  </div>

                  <div className="space-y-5 border-l border-[#edf2f5] pl-7">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Boyutu</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {FONT_SIZE_LABELS[settings.fontSize]}
                        </span>
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        {FONT_SIZE_OPTIONS.map((sizeOption) => {
                          const isActive = settings.fontSize === sizeOption.value;
                          return (
                            <button
                              key={sizeOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, fontSize: sizeOption.value }))}
                              className="h-10 rounded-lg border transition-colors"
                              style={getThemedOptionButtonStyle(isActive)}
                            >
                              <span className={`font-semibold ${sizeOption.preview}`}>{sizeOption.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <p className="mb-2 text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Karakteri</p>
                      <div className="grid grid-cols-2 gap-2">
                        {FONT_FAMILY_OPTIONS.map((fontOption) => {
                          const isActive = settings.fontFamily === fontOption.value;
                          return (
                            <button
                              key={fontOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, fontFamily: fontOption.value }))}
                              className="h-10 rounded-lg border text-sm transition-colors"
                              style={{ ...getThemedOptionButtonStyle(isActive), fontFamily: fontOption.css }}
                            >
                              {fontOption.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Vurgu Rengi</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {ACCENT_COLOR_OPTIONS.find((colorOption) => colorOption.value === settings.accentColor)?.label || 'Varsayılan'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        {ACCENT_COLOR_OPTIONS.map((colorOption) => {
                          const isActive = settings.accentColor === colorOption.value;
                          return (
                            <button
                              key={colorOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, accentColor: colorOption.value }))}
                              className="h-7 w-7 rounded-full border-2 transition-transform hover:scale-105"
                              style={{ ...getThemedSwatchStyle(isActive, colorOption.hex), backgroundColor: colorOption.hex }}
                              title={colorOption.label}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

type TextSearchSettings = Pick<PilotSettings, 'fontSize' | 'fontFamily' | 'accentColor' | 'enableCountdown'> & {
  durationSeconds: number;
  coveragePercent: 60 | 70 | 80 | 90 | 100;
  targetWordCount: 5 | 6 | 7 | 8 | 9;
};

type TextSearchToken = {
  id: string;
  text: string;
  normalized: string;
  isWord: boolean;
};

type TextSearchTarget = {
  key: string;
  display: string;
  occurrenceIds: string[];
  firstPosition: number;
  count: number;
  score: number;
};

const TEXT_SEARCH_DURATION_OPTIONS = [60, 90, 120, 150, 180, 210, 240, 270, 300] as const;
const TEXT_SEARCH_COVERAGE_OPTIONS = [60, 70, 80, 90, 100] as const;
const TEXT_SEARCH_TARGET_COUNT_OPTIONS = [5, 6, 7, 8, 9] as const;
const TEXT_SEARCH_WORD_LIMIT_BY_COVERAGE: Record<TextSearchSettings['coveragePercent'], number> = {
  60: 48,
  70: 62,
  80: 76,
  90: 92,
  100: 110,
};
const TEXT_SEARCH_STOP_WORDS = new Set([
  've', 'veya', 'ile', 'ilede', 'de', 'da', 'bir', 'bu', 'şu', 'o', 'ama', 'fakat', 'ancak', 'gibi', 'için',
  'ile', 'çok', 'daha', 'göre', 'olarak', 'olan', 'olur', 'oldu', 'olduğu', 'ise', 'mi', 'mı', 'mu', 'mü',
  'ya', 'ya da', 'ki', 'ne', 'nasıl', 'niçin', 'neden', 'hem', 'en', 'her', 'hiç', 'ile', 'kadar', 'sonra',
  'önce', 'bile', 'kez', 'şeklinde', 'üzere', 'burada', 'orada', 'bunu', 'buna', 'böyle', 'çünkü',
]);

const formatTextSearchDurationLabel = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

const stripTextSearchWord = (value: string) => String(value || '')
  .replace(/^[^0-9A-Za-zÇĞİÖŞÜçğıöşü]+|[^0-9A-Za-zÇĞİÖŞÜçğıöşü]+$/g, '')
  .trim();

const buildTextSearchDisplayText = (rawText: string, coveragePercent: TextSearchSettings['coveragePercent']) => {
  const maxWords = TEXT_SEARCH_WORD_LIMIT_BY_COVERAGE[coveragePercent] || TEXT_SEARCH_WORD_LIMIT_BY_COVERAGE[100];
  const paragraphs = String(rawText || '')
    .split(/\n+/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  if (paragraphs.length === 0) return '';

  let remainingWords = maxWords;
  const selectedParagraphs: string[] = [];

  for (const paragraph of paragraphs) {
    if (remainingWords <= 0) break;
    const words = paragraph.split(/\s+/).filter(Boolean);
    if (words.length === 0) continue;
    selectedParagraphs.push(words.slice(0, remainingWords).join(' '));
    remainingWords -= Math.min(remainingWords, words.length);
  }

  return selectedParagraphs.join('\n\n').trim();
};

const tokenizeTextSearchText = (text: string): TextSearchToken[][] => (
  String(text || '')
    .split(/\n+/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((paragraph, paragraphIndex) => {
      const pieces = paragraph.match(/\S+|\s+/g) || [];
      return pieces.map((piece, pieceIndex) => {
        const normalized = normalizeMatchingWordKey(piece);
        return {
          id: `${paragraphIndex}-${pieceIndex}-${normalized || 'space'}`,
          text: piece,
          normalized,
          isWord: Boolean(normalized),
        };
      });
    })
);

const getTextSearchCandidateScore = (wordKey: string, count: number, firstPosition: number) => {
  const length = wordKey.length;
  const distinctLetterCount = new Set(wordKey.split('')).size;
  const frequencyScore = count === 2 ? 8 : count === 3 ? 7 : count === 1 ? 6 : count === 4 ? 4 : count === 5 ? 2 : 0;
  const lengthScore = length >= 6 && length <= 11 ? 6 : length >= 4 ? 4 : length >= 2 ? 2 : 0;
  const stopWordPenalty = TEXT_SEARCH_STOP_WORDS.has(wordKey) ? 8 : 0;
  const crowdedPenalty = count >= 6 ? 5 : 0;
  const veryShortPenalty = length <= 2 ? 6 : length === 3 ? 2 : 0;
  const earlyBonus = Math.max(0, 4 - Math.floor(firstPosition / 18));

  return frequencyScore + lengthScore + Math.min(4, distinctLetterCount) + earlyBonus - stopWordPenalty - crowdedPenalty - veryShortPenalty;
};

const buildTextSearchTargets = (
  paragraphs: TextSearchToken[][],
  targetWordCount: TextSearchSettings['targetWordCount'],
  randomSeed = Math.random()
): TextSearchTarget[] => {
  const entries = new Map<string, TextSearchTarget>();
  let position = 0;

  paragraphs.flat().forEach((token) => {
    if (!token.isWord) return;
    const cleanDisplay = stripTextSearchWord(token.text);
    if (!cleanDisplay) return;

    position += 1;
    const existing = entries.get(token.normalized);
    if (existing) {
      existing.count += 1;
      existing.occurrenceIds.push(token.id);
      existing.score = getTextSearchCandidateScore(existing.key, existing.count, existing.firstPosition);
      return;
    }

    entries.set(token.normalized, {
      key: token.normalized,
      display: cleanDisplay,
      occurrenceIds: [token.id],
      firstPosition: position,
      count: 1,
      score: getTextSearchCandidateScore(token.normalized, 1, position),
    });
  });

  const allCandidates = Array.from(entries.values());
  const preferredCandidates = allCandidates.filter((candidate) => (
    !TEXT_SEARCH_STOP_WORDS.has(candidate.key)
    && candidate.count <= 5
    && candidate.display.length >= 3
  ));
  const relaxedCandidates = allCandidates.filter((candidate) => candidate.count <= 6 && candidate.display.length >= 2);
  const fallbackCandidates = allCandidates.filter((candidate) => candidate.display.length >= 2);
  const pool = (preferredCandidates.length >= targetWordCount
    ? preferredCandidates
    : [...preferredCandidates, ...relaxedCandidates, ...fallbackCandidates]
  ).filter((candidate, index, list) => (
    list.findIndex((other) => other.key === candidate.key) === index
  ));

  const totalWordCount = Math.max(1, position);
  const spreadGap = Math.max(8, Math.floor(totalWordCount / Math.max(2, targetWordCount + 1)));
  const randomizedPool = pool
    .map((candidate, index) => ({
      candidate,
      randomScore: Math.sin((candidate.firstPosition + 1) * 12.9898 + (index + 1) * 78.233 + randomSeed * 43758.5453),
    }))
    .sort((left, right) => {
      if (right.candidate.score !== left.candidate.score) return right.candidate.score - left.candidate.score;
      return right.randomScore - left.randomScore;
    })
    .map((entry) => entry.candidate);

  const selected: TextSearchTarget[] = [];

  randomizedPool.forEach((candidate) => {
    if (selected.length >= targetWordCount) return;
    const isFarEnough = selected.every((picked) => Math.abs(picked.firstPosition - candidate.firstPosition) >= spreadGap);
    if (isFarEnough) {
      selected.push(candidate);
    }
  });

  if (selected.length < targetWordCount) {
    randomizedPool.forEach((candidate) => {
      if (selected.length >= targetWordCount) return;
      if (selected.some((picked) => picked.key === candidate.key)) return;
      selected.push(candidate);
    });
  }

  return selected
    .sort((left, right) => {
      const leftRandom = Math.sin((left.firstPosition + 3) * 19.19 + randomSeed * 91.7);
      const rightRandom = Math.sin((right.firstPosition + 3) * 19.19 + randomSeed * 91.7);
      return rightRandom - leftRandom;
    })
    .slice(0, targetWordCount);
};

export const TextSearchExerciseView: React.FC<{
  profile: any;
  mode: ModeConfig;
  recommendedContent?: RecommendedStoryContent | null;
  contentLoading?: boolean;
  onClose: () => void;
}> = ({ profile, mode, recommendedContent, contentLoading = false, onClose }) => {
  const { user } = useAuth();
  const fullscreenContainerRef = useRef<HTMLDivElement | null>(null);
  const targetAdvanceTimeoutRef = useRef<number | null>(null);
  const localContentRequestRef = useRef(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [muted, setMuted] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'countdown' | 'running' | 'review'>('idle');
  const [countdown, setCountdown] = useState(3);
  const [remainingSeconds, setRemainingSeconds] = useState(60);
  const [activeTargetIndex, setActiveTargetIndex] = useState(0);
  const [currentFoundIds, setCurrentFoundIds] = useState<string[]>([]);
  const [foundCounts, setFoundCounts] = useState<Record<string, number>>({});
  const [activeContent, setActiveContent] = useState<RecommendedStoryContent | null>(() => (
    recommendedContent ? shared.normalizeRecommendedStoryContent(recommendedContent) : null
  ));
  const [localContentLoading, setLocalContentLoading] = useState(false);
  const [targetWords, setTargetWords] = useState<TextSearchTarget[]>([]);
  const [settings, setSettings] = useState<TextSearchSettings>(() => {
    const sharedDefaults = applySharedExerciseSettings<{
      fontSize: PilotSettings['fontSize'];
      fontFamily: PilotSettings['fontFamily'];
      accentColor: PilotSettings['accentColor'];
      enableCountdown: PilotSettings['enableCountdown'];
      exerciseDurationSeconds: number;
    }>({
      fontSize: 'orta',
      fontFamily: 'GoogleSans',
      accentColor: 'default',
      enableCountdown: true,
      exerciseDurationSeconds: 60,
    });

    return {
      durationSeconds: TEXT_SEARCH_DURATION_OPTIONS.includes(Number(sharedDefaults.exerciseDurationSeconds) as any)
        ? Number(sharedDefaults.exerciseDurationSeconds)
        : 60,
      coveragePercent: 100,
      targetWordCount: 6,
      fontSize: sharedDefaults.fontSize,
      fontFamily: sharedDefaults.fontFamily,
      accentColor: sharedDefaults.accentColor,
      enableCountdown: Boolean(sharedDefaults.enableCountdown ?? true),
    };
  });

  const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || profile?.name || 'Kullanıcı';
  const userImage = user?.avatar || user?.media?.url || user?.profilePhoto?.url || null;
  const userInitials = userName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part.charAt(0).toUpperCase())
    .join('');
  const introParagraphs = [
    'Bu egzersiz, bir metin içindeki önemli kelimeleri hızlı seçebilme ve görsel tarama becerisini geliştirmek için tasarlandı.',
    'Üst şeritte gösterilen kelimeyi metin içinde tarayın ve buldukça üzerine tıklayın. Sistem, metin içinden gerçekten aranabilir hedefleri seçer.',
  ];
  const effectiveContent = activeContent || (recommendedContent ? shared.normalizeRecommendedStoryContent(recommendedContent) : null);
  const effectiveContentLoading = contentLoading || localContentLoading;
  const rawText = (effectiveContent?.content || CAPTURE_SAMPLE_TEXT).trim();
  const displayText = useMemo(
    () => buildTextSearchDisplayText(rawText, settings.coveragePercent),
    [rawText, settings.coveragePercent],
  );
  const tokenizedParagraphs = useMemo(
    () => tokenizeTextSearchText(displayText),
    [displayText],
  );
  const activeTarget = targetWords[activeTargetIndex] || null;
  const activeAccent = resolveAccentColor(settings.accentColor);
  const activeFontFamily = FONT_FAMILY_OPTIONS.find((item) => item.value === settings.fontFamily)?.css || settings.fontFamily;
  const textClass = settings.fontSize === 'kucuk'
    ? 'text-[20px] leading-[1.85]'
    : settings.fontSize === 'orta'
      ? 'text-[22px] leading-[1.9]'
      : settings.fontSize === 'buyuk'
        ? 'text-[24px] leading-[1.95]'
        : 'text-[27px] leading-[2]';
  const completedWordCount = targetWords.filter((target) => (
    (foundCounts[target.key] || 0) >= target.occurrenceIds.length
  )).length;
  const isExerciseRunning = phase === 'running' || phase === 'countdown';
  const primaryLabel = phase === 'review' ? 'Yeniden Başlat' : isExerciseRunning ? 'Durdur' : 'Başlat';
  const overlayWord = activeTarget?.display?.toLocaleLowerCase('tr-TR') || 'kelime';
  const primaryActionDisabled = effectiveContentLoading || targetWords.length === 0;
  const foundTargetCount = completedWordCount;
  const activeTargetLabel = activeTarget?.display?.toLocaleLowerCase('tr-TR') || '-';
  const regenerateTargetWords = useCallback(() => {
    const nextTargets = buildTextSearchTargets(tokenizedParagraphs, settings.targetWordCount, Math.random());
    setTargetWords(nextTargets);
    setActiveTargetIndex(0);
    setCurrentFoundIds([]);
    setFoundCounts({});
    return nextTargets;
  }, [settings.targetWordCount, tokenizedParagraphs]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => () => {
    if (targetAdvanceTimeoutRef.current) {
      window.clearTimeout(targetAdvanceTimeoutRef.current);
      targetAdvanceTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (phase === 'running') return;
    if (!targetAdvanceTimeoutRef.current) return;
    window.clearTimeout(targetAdvanceTimeoutRef.current);
    targetAdvanceTimeoutRef.current = null;
  }, [phase]);

  useEffect(() => {
    setSettings((prev) => ({
      ...prev,
      ...applySharedExerciseSettings({
        fontSize: prev.fontSize,
        fontFamily: prev.fontFamily,
        accentColor: prev.accentColor,
        enableCountdown: prev.enableCountdown,
        exerciseDurationSeconds: prev.durationSeconds,
      }),
    }));
  }, []);

  useEffect(() => {
    const normalized = recommendedContent ? shared.normalizeRecommendedStoryContent(recommendedContent) : null;
    if (!normalized) return;
    setActiveContent(normalized);
  }, [recommendedContent]);

  useEffect(() => {
    if (recommendedContent || contentLoading) return;
    const requestId = localContentRequestRef.current + 1;
    localContentRequestRef.current = requestId;
    setLocalContentLoading(true);

    ApiService.getApi(`speed-reading/content/recommended?mode=${encodeURIComponent(getModeQueryValue(mode))}&includeAnswers=true`)
      .then((response: any) => {
        if (localContentRequestRef.current !== requestId) return;
        const normalized = shared.normalizeRecommendedStoryContent(response);
        if (normalized) {
          setActiveContent(normalized);
        }
      })
      .catch((error) => {
        if (localContentRequestRef.current !== requestId) return;
        console.error('Metin Arama içeriği yüklenemedi:', error);
      })
      .finally(() => {
        if (localContentRequestRef.current !== requestId) return;
        setLocalContentLoading(false);
      });
  }, [contentLoading, mode, recommendedContent]);

  useEffect(() => {
    if (!settingsOpen) return;
    persistSharedExerciseSettings({
      fontSize: settings.fontSize,
      fontFamily: settings.fontFamily,
      accentColor: settings.accentColor,
      enableCountdown: settings.enableCountdown,
      exerciseDurationSeconds: settings.durationSeconds,
    });
  }, [settings, settingsOpen]);

  useEffect(() => {
    if (phase !== 'idle') return;
    setRemainingSeconds(settings.durationSeconds);
  }, [phase, settings.durationSeconds]);

  useEffect(() => {
    regenerateTargetWords();
    setRemainingSeconds(settings.durationSeconds);
  }, [displayText, regenerateTargetWords, settings.durationSeconds]);

  useEffect(() => {
    if (phase !== 'countdown' || countdown <= 0) return;

    const timer = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [countdown, phase]);

  useEffect(() => {
    if (phase !== 'countdown' || countdown !== 0) return;
    setPhase('running');
  }, [countdown, phase]);

  useEffect(() => {
    if (phase !== 'running') return;

    const timer = window.setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          setPhase('review');
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [phase]);

  const handleReset = () => {
    if (targetAdvanceTimeoutRef.current) {
      window.clearTimeout(targetAdvanceTimeoutRef.current);
      targetAdvanceTimeoutRef.current = null;
    }
    setPhase('idle');
    setCountdown(3);
    setRemainingSeconds(settings.durationSeconds);
    setActiveTargetIndex(0);
    setCurrentFoundIds([]);
    setFoundCounts({});
  };

  const handleCloseExercise = async () => {
    if (document.fullscreenElement === fullscreenContainerRef.current) {
      try {
        await document.exitFullscreen();
      } catch (error) {
        console.error('Tam ekrandan çıkılamadı:', error);
      }
    }

    handleReset();
    onClose();
  };

  const handleToggleFullscreen = async () => {
    const container = fullscreenContainerRef.current;
    if (!container) return;

    try {
      if (document.fullscreenElement === container) {
        await document.exitFullscreen();
        return;
      }

      await container.requestFullscreen();
    } catch (error) {
      console.error('Tam ekran değiştirilemedi:', error);
    }
  };

  const handleStart = () => {
    if (primaryActionDisabled) return;
    const nextTargets = regenerateTargetWords();
    if (nextTargets.length === 0) return;
    if (targetAdvanceTimeoutRef.current) {
      window.clearTimeout(targetAdvanceTimeoutRef.current);
      targetAdvanceTimeoutRef.current = null;
    }
    setPhase('idle');
    setCountdown(3);
    setRemainingSeconds(settings.durationSeconds);

    if (settings.enableCountdown) {
      setCountdown(3);
      setPhase('countdown');
      return;
    }

    setPhase('running');
  };

  const handleWordClick = (token: TextSearchToken) => {
    if (phase !== 'running' || !activeTarget || !token.isWord) return;
    if (token.normalized !== activeTarget.key) return;
    if (currentFoundIds.includes(token.id)) return;

    setCurrentFoundIds((prev) => {
      const next = [...prev, token.id];
      setFoundCounts((current) => ({
        ...current,
        [activeTarget.key]: next.length,
      }));

      if (next.length >= activeTarget.occurrenceIds.length) {
        if (targetAdvanceTimeoutRef.current) {
          window.clearTimeout(targetAdvanceTimeoutRef.current);
        }

        targetAdvanceTimeoutRef.current = window.setTimeout(() => {
          targetAdvanceTimeoutRef.current = null;

          if (activeTargetIndex >= targetWords.length - 1) {
            setPhase('review');
            return;
          }

          setActiveTargetIndex((index) => Math.min(index + 1, targetWords.length - 1));
          setCurrentFoundIds([]);
        }, 320);
      }

      return next;
    });
  };

  return (
    <div
      ref={fullscreenContainerRef}
      className={`${isFullscreen ? 'h-screen p-3' : 'h-full p-6'} bg-[#f3f5f7]`}
    >
      <div className={`${isFullscreen ? 'max-w-none' : 'max-w-[1260px]'} mx-auto h-full`}>
        <div className="h-full border border-[#e6edf1] rounded-xl bg-[#f8fafb] px-6 py-5 flex gap-5 relative">
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ScanSearch className="w-[19px] h-[19px]" style={getThemeChromeTextStyle()} />
                <div className="text-[20px] font-bold" style={getThemeChromeTextStyle()}>{mode.name}</div>
              </div>
              <div className="flex items-center gap-3 text-[#afbbc2]">
                <ExerciseTextSourceControls
                  mode={mode}
                  disabled={isExerciseRunning}
                  onSelect={(content) => {
                    setActiveContent(content);
                    setPhase('idle');
                    setCountdown(3);
                    setActiveTargetIndex(0);
                    setCurrentFoundIds([]);
                    setFoundCounts({});
                    setTargetWords([]);
                  }}
                  onError={(message) => console.error(message)}
                />
                <button onClick={() => setMuted((current) => !current)} className="hover:text-[#6b7f8a]">
                  {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => setSettingsOpen(true)}
                  disabled={isExerciseRunning}
                  className="hover:text-[#6b7f8a] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Settings2 className="w-5 h-5" />
                </button>
                <button onClick={() => { void handleToggleFullscreen(); }} className="hover:text-[#6b7f8a]">
                  {isFullscreen ? <Shrink className="w-5 h-5" /> : <Expand className="w-5 h-5" />}
                </button>
                <button onClick={() => { void handleCloseExercise(); }} className="hover:text-[#6b7f8a]">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-hidden rounded-[10px] border border-[#e8edf0] bg-white flex flex-col">
              <div className="relative min-h-0 flex-1 bg-[#fbfbfb]">
                {(phase === 'idle' || phase === 'running' || phase === 'countdown') && (
                  <div className="absolute inset-0 overflow-hidden">
                    <div
                      className="mx-auto h-full px-5 py-5 transition-all duration-300"
                      style={{ width: `${settings.coveragePercent}%` }}
                    >
                      <div
                        className={`h-full text-[#606870] ${textClass}`}
                        style={{
                          fontFamily: activeFontFamily,
                          filter: phase === 'idle' || phase === 'countdown' ? 'blur(3.6px)' : 'none',
                          opacity: effectiveContentLoading ? 0.4 : 1,
                        }}
                      >
                        {tokenizedParagraphs.map((paragraph, paragraphIndex) => (
                          <p
                            key={`text-search-paragraph-${paragraphIndex}`}
                            className={`whitespace-pre-wrap break-words ${paragraphIndex > 0 ? 'mt-4' : ''}`}
                          >
                            {paragraph.map((token) => {
                              const isCurrentTarget = phase === 'running' && activeTarget?.key === token.normalized;
                              const isFound = currentFoundIds.includes(token.id);

                              if (!token.isWord) {
                                return <React.Fragment key={token.id}>{token.text}</React.Fragment>;
                              }

                              return (
                                <span
                                  key={token.id}
                                  onClick={() => handleWordClick(token)}
                                  className={`rounded-[4px] transition-colors ${isCurrentTarget ? 'cursor-pointer' : 'cursor-text'}`}
                                  style={isFound ? {
                                    backgroundColor: activeAccent,
                                    color: '#ffffff',
                                    boxShadow: `0 0 0 2px ${activeAccent}22`,
                                  } : undefined}
                                >
                                  {token.text}
                                </span>
                              );
                            })}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {effectiveContentLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/82 backdrop-blur-[2px]">
                    <div className="flex flex-col items-center gap-4">
                      <div className="h-9 w-9 animate-spin rounded-full border-2 border-[#2f5563] border-t-transparent" />
                      <p className="text-[18px] font-bold text-[#2e6276]">İçerik yükleniyor...</p>
                    </div>
                  </div>
                )}

                {phase === 'countdown' && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/76 backdrop-blur-[1px]">
                    <span className="text-7xl font-extrabold text-[#18485c]">{countdown}</span>
                  </div>
                )}

                {phase === 'review' && (
                  <div className="absolute inset-0 overflow-y-auto bg-white px-8 py-8">
                    <div className="mx-auto max-w-[900px]">
                      <div className="rounded-2xl border border-[#e1e8ed] bg-[#f8fbfc] p-6">
                        <div className="space-y-3">
                          {targetWords.map((target) => {
                            const foundCount = foundCounts[target.key] || 0;
                            const finished = foundCount >= target.occurrenceIds.length;
                            return (
                              <div
                                key={target.key}
                                className={`flex items-center justify-between rounded-xl border px-4 py-3 ${finished ? 'border-[#cfe6d5] bg-[#f4fbf6]' : 'border-[#f0d7d7] bg-[#fff7f7]'}`}
                              >
                                <div className={`text-[17px] font-black ${finished ? 'text-[#33835a]' : 'text-[#a14f4f]'}`}>
                                  {target.display.toLocaleLowerCase('tr-TR')}
                                </div>
                                <div className={`text-[16px] font-bold ${finished ? 'text-[#33835a]' : 'text-[#a14f4f]'}`}>
                                  {finished ? 'Doğru' : 'Yanlış'}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <aside className="hidden lg:flex w-[320px] shrink-0 h-full min-h-0 rounded-xl border border-[#e0eaee] bg-white p-5 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="flex items-center gap-3">
                {userImage ? (
                  <img src={userImage} alt={userName} className="w-11 h-11 rounded-full object-cover bg-[#daf0f7]" />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-[#daf0f7] text-[#146f8e] flex items-center justify-center font-bold">
                    {userInitials || 'K'}
                  </div>
                )}
                <div>
                  <div className="text-[#355c68] text-sm font-bold">{userName}</div>
                  <div className="text-[#94a8b3] text-xs">Metin tarama egzersizi</div>
                </div>
              </div>

              <div className="mt-6 flex flex-col items-center text-center">
                <div className="rounded-[2rem] bg-[#f7fafc] px-5 py-4">
                  <ModeIllustration mode={mode} />
                </div>
                <h3 className="mt-4 text-[34px] leading-none font-black" style={getThemeChromeTextStyle()}>{mode.name}</h3>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-6 text-center">
                <div>
                  <div className="text-[14px] text-[#b1b8bd]">Bulunan</div>
                  <div className="mt-1 text-[20px] leading-none font-black text-[#194e62]">{foundTargetCount}/{targetWords.length || 0}</div>
                </div>
                <div>
                  <div className="text-[14px] text-[#b1b8bd]">Aktif Hedef</div>
                  <div className="mt-1 text-[20px] leading-none font-black text-[#194e62]">{activeTargetLabel}</div>
                </div>
              </div>

              {phase !== 'idle' && (
                <div className="mt-8 rounded-2xl border border-[#e8eef2] bg-[#f8fbfc] p-4">
                  <div className="flex items-start gap-3">
                    <CircleHelp className="mt-0.5 h-4 w-4 text-[#6d8b98]" />
                    <div className="text-sm leading-6 text-[#65808c]">
                      {phase === 'review'
                        ? 'Tamamlanan hedefleri içeride özet halinde görebilirsiniz.'
                        : `Şu an aranacak kelime ${activeTarget ? `"${overlayWord}"` : 'hazırlanıyor'}. Tüm geçişleri bulunca sıradaki hedefe geçilir.`}
                    </div>
                  </div>
                </div>
              )}

              {phase === 'idle' && <SidebarPreparationCard paragraphs={introParagraphs} fontFamily={activeFontFamily} />}
            </div>

            <div className="shrink-0 pt-4">
              <p className="text-xs text-[#98a8b1]">Kalan Süre</p>
              <p className="text-[44px] leading-none font-black mt-1" style={getThemeChromeTextStyle()}>
                {formatClock(phase === 'review' ? 0 : remainingSeconds)}
              </p>

              <button
                onClick={() => {
                  if (phase === 'review') handleStart();
                  else if (isExerciseRunning) handleReset();
                  else handleStart();
                }}
                disabled={primaryActionDisabled}
                className={`mt-4 w-full h-12 rounded-lg font-bold text-base transition-colors inline-flex items-center justify-center gap-2 ${primaryActionDisabled
                  ? 'bg-[#dfe7ea] text-[#7f929c] cursor-not-allowed'
                  : 'text-white'
                  }`}
                style={primaryActionDisabled ? undefined : getThemeChromeButtonStyle()}
              >
                {phase === 'review' ? null : isExerciseRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {primaryLabel}
              </button>

              {phase !== 'review' && (
                <button
                  onClick={handleReset}
                  className="mt-4 w-full h-12 rounded-lg bg-[#f1f3f5] text-[#2d6175] font-bold text-base transition-colors hover:bg-[#e7ecef]"
                >
                  Sıfırla
                </button>
              )}
            </div>
          </aside>

          {isFullscreen && (
            <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 text-[12px] font-medium text-[#96a6af]">
              Tam ekrandan çıkmak için Esc tuşuna basın veya üst araç çubuğunu kullanın
            </div>
          )}

          {settingsOpen && (
            <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/55 backdrop-blur-[4px]">
              <div className="w-[920px] rounded-xl bg-white p-6 shadow-lg">
                <div className="mb-5 flex items-center justify-between">
                  <h4 className="text-xl font-bold" style={getThemedLabelStyle()}>
                    Egzersiz Ayarları
                  </h4>
                  <button className="text-gray-500 hover:text-gray-700" onClick={() => setSettingsOpen(false)}>
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-7">
                  <div className="space-y-5">
                    <SettingsStepperRow
                      label="Süre"
                      value={formatTextSearchDurationLabel(settings.durationSeconds)}
                      onDecrease={() => {
                        const index = TEXT_SEARCH_DURATION_OPTIONS.indexOf(settings.durationSeconds as any);
                        const nextValue = TEXT_SEARCH_DURATION_OPTIONS[Math.max(0, index - 1)];
                        setSettings((prev) => ({ ...prev, durationSeconds: nextValue }));
                      }}
                      onIncrease={() => {
                        const index = TEXT_SEARCH_DURATION_OPTIONS.indexOf(settings.durationSeconds as any);
                        const nextValue = TEXT_SEARCH_DURATION_OPTIONS[Math.min(TEXT_SEARCH_DURATION_OPTIONS.length - 1, index + 1)];
                        setSettings((prev) => ({ ...prev, durationSeconds: nextValue }));
                      }}
                    />

                    <SettingsStepperRow
                      label="Kaplama"
                      value={`%${settings.coveragePercent}`}
                      onDecrease={() => {
                        const index = TEXT_SEARCH_COVERAGE_OPTIONS.indexOf(settings.coveragePercent);
                        const nextValue = TEXT_SEARCH_COVERAGE_OPTIONS[Math.max(0, index - 1)];
                        setSettings((prev) => ({ ...prev, coveragePercent: nextValue }));
                      }}
                      onIncrease={() => {
                        const index = TEXT_SEARCH_COVERAGE_OPTIONS.indexOf(settings.coveragePercent);
                        const nextValue = TEXT_SEARCH_COVERAGE_OPTIONS[Math.min(TEXT_SEARCH_COVERAGE_OPTIONS.length - 1, index + 1)];
                        setSettings((prev) => ({ ...prev, coveragePercent: nextValue }));
                      }}
                    />

                    <SettingsStepperRow
                      label="Kelime Sayısı"
                      value={`${settings.targetWordCount}`}
                      onDecrease={() => {
                        const index = TEXT_SEARCH_TARGET_COUNT_OPTIONS.indexOf(settings.targetWordCount);
                        const nextValue = TEXT_SEARCH_TARGET_COUNT_OPTIONS[Math.max(0, index - 1)];
                        setSettings((prev) => ({ ...prev, targetWordCount: nextValue }));
                      }}
                      onIncrease={() => {
                        const index = TEXT_SEARCH_TARGET_COUNT_OPTIONS.indexOf(settings.targetWordCount);
                        const nextValue = TEXT_SEARCH_TARGET_COUNT_OPTIONS[Math.min(TEXT_SEARCH_TARGET_COUNT_OPTIONS.length - 1, index + 1)];
                        setSettings((prev) => ({ ...prev, targetWordCount: nextValue }));
                      }}
                    />

                    <SettingsToggleRow
                      label="Başlamadan önce geriye say"
                      checked={settings.enableCountdown}
                      onChange={() => setSettings((prev) => ({ ...prev, enableCountdown: !prev.enableCountdown }))}
                    />
                  </div>

                  <div className="space-y-5 border-l border-[#edf2f5] pl-7">
                    <div>
                      <div className="mb-2 flex items-center justify-between">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Boyutu</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {FONT_SIZE_LABELS[settings.fontSize]}
                        </span>
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        {FONT_SIZE_OPTIONS.map((sizeOption) => {
                          const isActive = settings.fontSize === sizeOption.value;
                          return (
                            <button
                              key={sizeOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, fontSize: sizeOption.value }))}
                              className="h-10 rounded-lg border transition-colors"
                              style={getThemedOptionButtonStyle(isActive)}
                            >
                              <span className={`font-semibold ${sizeOption.preview}`}>{sizeOption.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <p className="mb-2 text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Karakteri</p>
                      <div className="grid grid-cols-2 gap-2">
                        {FONT_FAMILY_OPTIONS.map((fontOption) => {
                          const isActive = settings.fontFamily === fontOption.value;
                          return (
                            <button
                              key={fontOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, fontFamily: fontOption.value }))}
                              className="h-10 rounded-lg border text-sm transition-colors"
                              style={{ ...getThemedOptionButtonStyle(isActive), fontFamily: fontOption.css }}
                            >
                              {fontOption.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <div className="mb-2 flex items-center justify-between">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Vurgu Rengi</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {ACCENT_COLOR_OPTIONS.find((colorOption) => colorOption.value === settings.accentColor)?.label || 'Varsayılan'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        {ACCENT_COLOR_OPTIONS.map((colorOption) => {
                          const isActive = settings.accentColor === colorOption.value;
                          return (
                            <button
                              key={colorOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, accentColor: colorOption.value }))}
                              className="h-7 w-7 rounded-full border-2 transition-transform hover:scale-105"
                              style={{ ...getThemedSwatchStyle(isActive, colorOption.hex), backgroundColor: colorOption.hex }}
                              title={colorOption.label}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

type SimilarWordsSettings = Pick<PilotSettings, 'fontSize' | 'fontFamily' | 'accentColor' | 'enableCountdown'> & {
  durationSeconds: number;
  targetWordCount: number;
};

type SimilarWordsCell = {
  id: string;
  topWord: string;
  bottomWord: string;
  isDifferent: boolean;
};

type SimilarWordsBoard = {
  id: number;
  cells: SimilarWordsCell[];
};

type SimilarWordsResult = {
  id: string;
  topWord: string;
  bottomWord: string;
  found: boolean;
};

const SIMILAR_WORDS_DURATION_OPTIONS = [60, 90, 120, 150, 180, 210, 240, 270, 300] as const;
const SIMILAR_WORDS_TARGET_COUNT_OPTIONS = [4, 5, 6, 7, 8] as const;
const SIMILAR_WORDS_BOARD_SIZE = 20;
const SIMILAR_WORDS_BOARD_COLUMNS = 4;
const SIMILAR_WORDS_MUTATION_MAP: Record<string, string[]> = {
  a: ['e', 'ı', 'o'],
  e: ['a', 'i'],
  i: ['ı', 'e', 'l'],
  ı: ['i', 'a'],
  o: ['ö', 'u', 'a'],
  ö: ['o', 'ü'],
  u: ['ü', 'o'],
  ü: ['u', 'ö'],
  b: ['d', 'p'],
  c: ['ç'],
  ç: ['c'],
  d: ['t', 'b'],
  f: ['v'],
  g: ['ğ', 'k'],
  ğ: ['g'],
  h: ['k'],
  k: ['g', 'h', 'l'],
  l: ['i', 'k'],
  m: ['n'],
  n: ['m'],
  p: ['b'],
  r: ['n'],
  s: ['ş', 'z'],
  ş: ['s'],
  t: ['d'],
  v: ['f', 'y'],
  y: ['v'],
  z: ['s'],
};
const SIMILAR_WORDS_MIN_WORD_LENGTH = 5;
const SIMILAR_WORDS_MAX_WORD_LENGTH = 14;

const getSimilarWordsTextClass = (fontSize: PilotSettings['fontSize']) => (
  fontSize === 'kucuk'
    ? 'text-[17px] leading-[1.45]'
    : fontSize === 'orta'
      ? 'text-[19px] leading-[1.48]'
      : fontSize === 'buyuk'
        ? 'text-[21px] leading-[1.52]'
        : 'text-[23px] leading-[1.56]'
);

const getSimilarWordsWordScore = (word: string) => {
  const uniqueLetterCount = new Set(word.split('')).size;
  const repeatedLetterPenalty = Math.max(0, word.length - uniqueLetterCount - 1);
  const lengthScore = word.length >= 9 ? 10 : word.length >= 7 ? 8 : word.length >= 5 ? 5 : 0;
  return lengthScore + uniqueLetterCount - repeatedLetterPenalty;
};

const getSimilarWordsPool = (words: string[]) => {
  const pool = Array.from(
    new Set(
      words
        .map((word) => sanitizeWordSearchWord(word).toLocaleLowerCase('tr-TR'))
        .filter((word) => word.length >= SIMILAR_WORDS_MIN_WORD_LENGTH && word.length <= SIMILAR_WORDS_MAX_WORD_LENGTH)
        .filter((word) => !TEXT_SEARCH_STOP_WORDS.has(normalizeMatchingWordKey(word))),
    ),
  );

  return shuffleWordSearchItems(pool).sort((left, right) => {
    const scoreDelta = getSimilarWordsWordScore(right) - getSimilarWordsWordScore(left);
    if (scoreDelta !== 0) return scoreDelta;
    return right.length - left.length;
  });
};

const getSimilarWordsDistance = (left: string, right: string) => {
  const maxLength = Math.max(left.length, right.length);
  let score = Math.abs(left.length - right.length);
  for (let index = 0; index < Math.min(left.length, right.length); index += 1) {
    if (left[index] !== right[index]) score += 1;
  }
  return score + Math.max(0, maxLength - Math.min(left.length, right.length));
};

const findSimilarWordsVariant = (baseWord: string, pool: string[]) => {
  const baseKey = normalizeMatchingWordKey(baseWord);
  const candidates = pool
    .filter((word) => normalizeMatchingWordKey(word) !== baseKey)
    .map((word) => ({
      word,
      distance: getSimilarWordsDistance(baseWord, word),
      lengthDistance: Math.abs(baseWord.length - word.length),
      sameFirstLetter: word[0] === baseWord[0],
      sameLastLetter: word[word.length - 1] === baseWord[baseWord.length - 1],
      samePrefix: word.slice(0, 2) === baseWord.slice(0, 2),
      sameSuffix: word.slice(-2) === baseWord.slice(-2),
    }))
    .filter((candidate) => candidate.distance === 1)
    .filter((candidate) => candidate.lengthDistance === 0)
    .filter((candidate) => (
      (candidate.sameFirstLetter && candidate.sameLastLetter)
      || candidate.samePrefix
      || candidate.sameSuffix
    ));

  if (candidates.length > 0) {
    const best = shuffleWordSearchItems(candidates).sort((left, right) => {
      const prefixWeightLeft = Number(left.samePrefix) + Number(left.sameSuffix) + Number(left.sameFirstLetter && left.sameLastLetter);
      const prefixWeightRight = Number(right.samePrefix) + Number(right.sameSuffix) + Number(right.sameFirstLetter && right.sameLastLetter);
      if (prefixWeightRight !== prefixWeightLeft) return prefixWeightRight - prefixWeightLeft;
      return left.word.localeCompare(right.word, 'tr');
    })[0];
    if (best?.word) return best.word;
  }

  const letters = baseWord.split('');
  const mutationAttempts: string[] = [];

  for (let index = 1; index < Math.max(1, letters.length - 1); index += 1) {
    const current = letters[index];
    const replacements = SIMILAR_WORDS_MUTATION_MAP[current] || [];
    replacements.forEach((replacement) => {
      const variantLetters = [...letters];
      variantLetters[index] = replacement;
      mutationAttempts.push(variantLetters.join(''));
    });
  }

  for (let index = 1; index < letters.length - 2; index += 1) {
    const swapped = [...letters];
    [swapped[index], swapped[index + 1]] = [swapped[index + 1], swapped[index]];
    mutationAttempts.push(swapped.join(''));
  }

  const variant = shuffleWordSearchItems(mutationAttempts).find((candidate) => {
    if (normalizeMatchingWordKey(candidate) === baseKey) return false;
    return getSimilarWordsDistance(baseWord, candidate) === 1;
  });
  return variant || `${baseWord.slice(0, -1)}${baseWord.slice(-1) === 'a' ? 'e' : 'a'}`;
};

const buildSimilarWordsBoard = (
  sourceWords: string[],
  targetWordCount: SimilarWordsSettings['targetWordCount'],
  boardId: number,
  excludedWords: string[] = [],
): SimilarWordsBoard | null => {
  const pool = getSimilarWordsPool(sourceWords);
  if (pool.length < SIMILAR_WORDS_BOARD_SIZE) return null;
  const excludedSet = new Set(excludedWords.map((word) => normalizeMatchingWordKey(word)));
  const preferredPool = pool.filter((word) => !excludedSet.has(normalizeMatchingWordKey(word)));
  const activePool = preferredPool.length >= SIMILAR_WORDS_BOARD_SIZE ? preferredPool : pool;
  const shuffledPool = shuffleWordSearchItems(activePool.slice(0, Math.max(SIMILAR_WORDS_BOARD_SIZE * 3, 40))).slice(0, SIMILAR_WORDS_BOARD_SIZE);
  const targetIndexes = new Set(shuffleWordSearchItems(Array.from({ length: SIMILAR_WORDS_BOARD_SIZE }, (_, index) => index)).slice(0, targetWordCount));
  const cells = shuffledPool.map((baseWord, index) => {
    const differentWord = targetIndexes.has(index) ? findSimilarWordsVariant(baseWord, pool) : baseWord;
    return {
      id: `${boardId}-${index}`,
      topWord: baseWord,
      bottomWord: differentWord,
      isDifferent: normalizeMatchingWordKey(baseWord) !== normalizeMatchingWordKey(differentWord),
    };
  });

  if (cells.filter((cell) => cell.isDifferent).length !== targetWordCount) {
    return null;
  }

  return { id: boardId, cells };
};

export const SimilarWordsExerciseView: React.FC<{
  profile: any;
  mode: ModeConfig;
  onClose: () => void;
}> = ({ profile, mode, onClose }) => {
  const { user } = useAuth();
  const fullscreenContainerRef = useRef<HTMLDivElement | null>(null);
  const wrongBoxTimeoutsRef = useRef<number[]>([]);
  const boardAdvanceTimeoutRef = useRef<number | null>(null);
  const boardSerialRef = useRef(1);
  const committedBoardIdsRef = useRef<Set<number>>(new Set());
  const usedBoardWordsRef = useRef<Set<string>>(new Set());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [muted, setMuted] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'countdown' | 'running' | 'review'>('idle');
  const [countdown, setCountdown] = useState(3);
  const [remainingSeconds, setRemainingSeconds] = useState(60);
  const [remoteWordPool, setRemoteWordPool] = useState<string[]>([]);
  const [wordsLoading, setWordsLoading] = useState(true);
  const [wordsError, setWordsError] = useState<string | null>(null);
  const [board, setBoard] = useState<SimilarWordsBoard | null>(null);
  const [selectedCorrectIds, setSelectedCorrectIds] = useState<string[]>([]);
  const [wrongBoxIds, setWrongBoxIds] = useState<string[]>([]);
  const [sessionResults, setSessionResults] = useState<SimilarWordsResult[]>([]);
  const [totalFoundCount, setTotalFoundCount] = useState(0);
  const [wrongTapCount, setWrongTapCount] = useState(0);
  const [completedBoardCount, setCompletedBoardCount] = useState(0);
  const [settings, setSettings] = useState<SimilarWordsSettings>(() => {
    const sharedDefaults = applySharedExerciseSettings<{
      fontSize: PilotSettings['fontSize'];
      fontFamily: PilotSettings['fontFamily'];
      accentColor: PilotSettings['accentColor'];
      enableCountdown: PilotSettings['enableCountdown'];
      exerciseDurationSeconds: number;
    }>({
      fontSize: 'orta',
      fontFamily: 'GoogleSans',
      accentColor: 'default',
      enableCountdown: true,
      exerciseDurationSeconds: 60,
    });

    return {
      durationSeconds: SIMILAR_WORDS_DURATION_OPTIONS.includes(Number(sharedDefaults.exerciseDurationSeconds) as any)
        ? Number(sharedDefaults.exerciseDurationSeconds)
        : 60,
      targetWordCount: 5,
      fontSize: sharedDefaults.fontSize,
      fontFamily: sharedDefaults.fontFamily,
      accentColor: sharedDefaults.accentColor,
      enableCountdown: Boolean(sharedDefaults.enableCountdown ?? true),
    };
  });

  const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || profile?.name || 'Kullanıcı';
  const userImage = user?.avatar || user?.media?.url || user?.profilePhoto?.url || null;
  const userInitials = userName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part.charAt(0).toUpperCase())
    .join('');
  const introParagraphs = [
    'Kutu içindeki iki kelimeye bakın. Bazı kutularda kelimeler aynıdır, bazı kutularda ise harf farkı vardır.',
    'Farklı olan kutuları seçin. Yanlış seçilen kutu kısa süre kırmızı olur, doğru seçilen kutu vurgu renginde işaretlenir.',
    'Süre dolmadan tüm farklı kutular bulunursa yeni bir tahta otomatik hazırlanır ve süre akmaya devam eder.',
  ];
  const fallbackWords = useMemo(() => (
    Array.from(new Set(Object.values(MULTIFOCUS_LEVEL_WORDS).flat().map((word) => sanitizeWordSearchWord(word)).filter(Boolean)))
  ), []);
  const effectiveWordPool = useMemo(() => {
    const source = remoteWordPool.length > 0 ? remoteWordPool : fallbackWords;
    return Array.from(new Set(source.map((word) => sanitizeWordSearchWord(word)).filter(Boolean)));
  }, [fallbackWords, remoteWordPool]);
  const activeAccent = resolveAccentColor(settings.accentColor);
  const activeFontFamily = FONT_FAMILY_OPTIONS.find((item) => item.value === settings.fontFamily)?.css || settings.fontFamily;
  const textClass = getSimilarWordsTextClass(settings.fontSize);
  const isExerciseRunning = phase === 'running' || phase === 'countdown';
  const primaryLabel = phase === 'review' ? 'Yeniden Başlat' : isExerciseRunning ? 'Durdur' : 'Başlat';
  const primaryActionDisabled = wordsLoading || effectiveWordPool.length < SIMILAR_WORDS_BOARD_SIZE || !board;
  const remainingTargetCount = Math.max(0, (board?.cells.filter((cell) => cell.isDifferent).length || 0) - selectedCorrectIds.length);

  const clearWrongBoxTimeouts = () => {
    wrongBoxTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
    wrongBoxTimeoutsRef.current = [];
  };

  const clearBoardAdvanceTimeout = () => {
    if (boardAdvanceTimeoutRef.current) {
      window.clearTimeout(boardAdvanceTimeoutRef.current);
      boardAdvanceTimeoutRef.current = null;
    }
  };

  const commitBoardResults = useCallback((activeBoard: SimilarWordsBoard | null, foundIds: string[]) => {
    if (!activeBoard || committedBoardIdsRef.current.has(activeBoard.id)) return;
    committedBoardIdsRef.current.add(activeBoard.id);
    const foundSet = new Set(foundIds);
    const boardResults = activeBoard.cells
      .filter((cell) => cell.isDifferent)
      .map((cell) => ({
        id: cell.id,
        topWord: cell.topWord,
        bottomWord: cell.bottomWord,
        found: foundSet.has(cell.id),
      }));
    setSessionResults((prev) => [...prev, ...boardResults]);
  }, []);

  const prepareBoard = useCallback(() => {
    let nextBoard = buildSimilarWordsBoard(
      effectiveWordPool,
      settings.targetWordCount,
      boardSerialRef.current,
      Array.from(usedBoardWordsRef.current),
    );

    if (!nextBoard && usedBoardWordsRef.current.size > 0) {
      usedBoardWordsRef.current = new Set();
      nextBoard = buildSimilarWordsBoard(effectiveWordPool, settings.targetWordCount, boardSerialRef.current);
    }

    boardSerialRef.current += 1;
    setBoard(nextBoard);
    setSelectedCorrectIds([]);
    setWrongBoxIds([]);
    if (nextBoard) {
      nextBoard.cells.forEach((cell) => usedBoardWordsRef.current.add(normalizeMatchingWordKey(cell.topWord)));
    }
    return nextBoard;
  }, [effectiveWordPool, settings.targetWordCount]);

  const handleReset = useCallback(() => {
    clearWrongBoxTimeouts();
    clearBoardAdvanceTimeout();
    committedBoardIdsRef.current = new Set();
    boardSerialRef.current = 1;
    usedBoardWordsRef.current = new Set();
    setPhase('idle');
    setCountdown(3);
    setRemainingSeconds(settings.durationSeconds);
    setBoard(null);
    setSelectedCorrectIds([]);
    setWrongBoxIds([]);
    setSessionResults([]);
    setTotalFoundCount(0);
    setWrongTapCount(0);
    setCompletedBoardCount(0);
  }, [settings.durationSeconds]);

  useEffect(() => {
    setSettings((prev) => ({
      ...prev,
      ...applySharedExerciseSettings({
        fontSize: prev.fontSize,
        fontFamily: prev.fontFamily,
        accentColor: prev.accentColor,
        enableCountdown: prev.enableCountdown,
        exerciseDurationSeconds: prev.durationSeconds,
      }),
    }));
  }, []);

  useEffect(() => {
    if (!settingsOpen) return;
    persistSharedExerciseSettings({
      fontSize: settings.fontSize,
      fontFamily: settings.fontFamily,
      accentColor: settings.accentColor,
      enableCountdown: settings.enableCountdown,
      exerciseDurationSeconds: settings.durationSeconds,
    });
  }, [settings, settingsOpen]);

  useEffect(() => {
    let mounted = true;
    setWordsLoading(true);
    setWordsError(null);

    ApiService.getApi('speed-reading/exercises/word-pools?type=multi-fokus&limit=500')
      .then((response: any) => {
        if (!mounted) return;
        const words = Array.isArray(response?.items)
          ? response.items.map((item: any) => String(item?.word || '').trim()).filter(Boolean)
          : [];
        setRemoteWordPool(words);
        setWordsError(words.length > 0 ? null : 'Benzer kelimeler için uygun havuz bulunamadı.');
      })
      .catch((error) => {
        if (!mounted) return;
        console.error('Benzer Kelimeler havuzu yüklenemedi:', error);
        setRemoteWordPool([]);
        setWordsError('Kelime havuzu veritabanından yüklenemedi. Yerel yedek kelimeler kullanılıyor.');
      })
      .finally(() => {
        if (!mounted) return;
        setWordsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => () => {
    clearWrongBoxTimeouts();
    clearBoardAdvanceTimeout();
  }, []);

  useEffect(() => {
    if (phase !== 'idle') return;
    setRemainingSeconds(settings.durationSeconds);
    prepareBoard();
  }, [phase, prepareBoard, settings.durationSeconds]);

  useEffect(() => {
    if (phase !== 'countdown' || countdown <= 0) return;

    const timer = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [countdown, phase]);

  useEffect(() => {
    if (phase !== 'countdown' || countdown !== 0) return;
    setPhase('running');
  }, [countdown, phase]);

  useEffect(() => {
    if (phase !== 'running') return;

    const timer = window.setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          setPhase('review');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'running' || !board) return;
    const totalDifferent = board.cells.filter((cell) => cell.isDifferent).length;
    if (selectedCorrectIds.length < totalDifferent || totalDifferent === 0) return;

    commitBoardResults(board, selectedCorrectIds);
    clearBoardAdvanceTimeout();
    boardAdvanceTimeoutRef.current = window.setTimeout(() => {
      boardAdvanceTimeoutRef.current = null;
      setCompletedBoardCount((prev) => prev + 1);
      const nextBoard = prepareBoard();
      if (!nextBoard) {
        setPhase('review');
      }
    }, 420);

    return () => clearBoardAdvanceTimeout();
  }, [board, commitBoardResults, phase, prepareBoard, selectedCorrectIds]);

  useEffect(() => {
    if (phase !== 'review') return;
    commitBoardResults(board, selectedCorrectIds);
  }, [board, commitBoardResults, phase, selectedCorrectIds]);

  const handleCloseExercise = async () => {
    if (document.fullscreenElement === fullscreenContainerRef.current) {
      try {
        await document.exitFullscreen();
      } catch (error) {
        console.error('Tam ekrandan çıkılamadı:', error);
      }
    }

    handleReset();
    onClose();
  };

  const handleToggleFullscreen = async () => {
    const container = fullscreenContainerRef.current;
    if (!container) return;

    try {
      if (document.fullscreenElement === container) {
        await document.exitFullscreen();
        return;
      }

      await container.requestFullscreen();
    } catch (error) {
      console.error('Tam ekran değiştirilemedi:', error);
    }
  };

  const handleStart = () => {
    let nextBoard = board;
    if (!nextBoard) {
      nextBoard = prepareBoard();
    }
    if (!nextBoard) return;

    clearWrongBoxTimeouts();
    clearBoardAdvanceTimeout();
    committedBoardIdsRef.current = new Set();
    boardSerialRef.current = nextBoard.id + 1;
    usedBoardWordsRef.current = new Set(nextBoard.cells.map((cell) => normalizeMatchingWordKey(cell.topWord)));
    setSelectedCorrectIds([]);
    setWrongBoxIds([]);
    setSessionResults([]);
    setTotalFoundCount(0);
    setWrongTapCount(0);
    setCompletedBoardCount(0);
    setRemainingSeconds(settings.durationSeconds);

    if (settings.enableCountdown) {
      setCountdown(3);
      setPhase('countdown');
      return;
    }

    setPhase('running');
  };

  const handleCellClick = (cell: SimilarWordsCell) => {
    if (phase !== 'running') return;

    if (cell.isDifferent) {
      setSelectedCorrectIds((prev) => {
        if (prev.includes(cell.id)) return prev;
        setTotalFoundCount((count) => count + 1);
        return [...prev, cell.id];
      });
      return;
    }

    setWrongTapCount((prev) => prev + 1);
    setWrongBoxIds((prev) => (prev.includes(cell.id) ? prev : [...prev, cell.id]));
    const timeoutId = window.setTimeout(() => {
      setWrongBoxIds((prev) => prev.filter((id) => id !== cell.id));
    }, 460);
    wrongBoxTimeoutsRef.current.push(timeoutId);
  };

  return (
    <div
      ref={fullscreenContainerRef}
      className={`${isFullscreen ? 'h-screen p-3' : 'h-full p-6'} bg-[#f3f5f7]`}
    >
      <div className={`${isFullscreen ? 'max-w-none' : 'max-w-[1260px]'} mx-auto h-full`}>
        <div className="h-full border border-[#e6edf1] rounded-xl bg-[#f8fafb] px-6 py-5 flex gap-5 relative">
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ScanSearch className="w-[19px] h-[19px]" style={getThemeChromeTextStyle()} />
                <div className="text-[20px] font-bold" style={getThemeChromeTextStyle()}>{mode.name}</div>
              </div>
              <div className="flex items-center gap-3 text-[#afbbc2]">
                <button onClick={() => setMuted((current) => !current)} className="hover:text-[#6b7f8a]">
                  {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => setSettingsOpen(true)}
                  disabled={isExerciseRunning}
                  className="hover:text-[#6b7f8a] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Settings2 className="w-5 h-5" />
                </button>
                <button onClick={() => { void handleToggleFullscreen(); }} className="hover:text-[#6b7f8a]">
                  {isFullscreen ? <Shrink className="w-5 h-5" /> : <Expand className="w-5 h-5" />}
                </button>
                <button onClick={() => { void handleCloseExercise(); }} className="hover:text-[#6b7f8a]">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-hidden rounded-[10px] border border-[#e8edf0] bg-white flex flex-col">
              <div className="relative min-h-0 flex-1 bg-[#fbfbfb]">
                {(phase === 'idle' || phase === 'countdown' || phase === 'running') && (
                  <div className="absolute inset-0 overflow-y-auto px-6 py-5">
                    <div
                      className="mx-auto grid min-h-full content-center max-w-[660px] gap-1.5"
                      style={{
                        gridTemplateColumns: `repeat(${SIMILAR_WORDS_BOARD_COLUMNS}, minmax(0, 1fr))`,
                        filter: phase === 'idle' || phase === 'countdown' ? 'blur(2.8px)' : 'none',
                        opacity: wordsLoading ? 0.45 : 1,
                      }}
                    >
                      {(board?.cells || []).map((cell) => {
                        const isCorrect = selectedCorrectIds.includes(cell.id);
                        const isWrong = wrongBoxIds.includes(cell.id);

                        return (
                          <button
                            key={cell.id}
                            type="button"
                            onClick={() => handleCellClick(cell)}
                            className={`min-h-[90px] rounded-[20px] border px-4 py-4 text-center transition-colors ${textClass}`}
                            style={{
                              backgroundColor: isWrong ? '#fff1f2' : isCorrect ? activeAccent : 'transparent',
                              borderColor: isWrong ? '#d66a72' : isCorrect ? activeAccent : `${activeAccent}88`,
                              borderWidth: isWrong || isCorrect ? '3px' : '2px',
                              borderStyle: isWrong ? 'dashed' : isCorrect ? 'solid' : 'dashed',
                              color: isWrong ? '#b5545d' : isCorrect ? '#ffffff' : '#555d63',
                              fontFamily: activeFontFamily,
                              fontWeight: 700,
                              boxShadow: isCorrect ? `0 0 0 1px ${activeAccent}22 inset` : 'none',
                            }}
                          >
                            <div>{cell.topWord}</div>
                            <div className="mt-1">{cell.bottomWord}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {wordsLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/82 backdrop-blur-[2px]">
                    <div className="flex flex-col items-center gap-4">
                      <div className="h-9 w-9 animate-spin rounded-full border-2 border-[#2f5563] border-t-transparent" />
                      <p className="text-[18px] font-bold text-[#2e6276]">Kutular hazırlanıyor...</p>
                    </div>
                  </div>
                )}

                {phase === 'countdown' && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/58 backdrop-blur-[1px]">
                    <span className="text-7xl font-extrabold text-[#18485c]">{countdown}</span>
                  </div>
                )}

                {phase === 'review' && (
                  <div className="absolute inset-0 overflow-y-auto bg-white px-8 py-8">
                    <div className="mx-auto max-w-[900px] space-y-5">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="rounded-2xl border border-[#dce8ed] bg-[#f8fbfc] px-5 py-4">
                          <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[#8aa0ab]">Bulunan</div>
                          <div className="mt-2 text-[30px] font-black text-[#194e62]">{totalFoundCount}</div>
                        </div>
                        <div className="rounded-2xl border border-[#dce8ed] bg-[#f8fbfc] px-5 py-4">
                          <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[#8aa0ab]">Tamamlanan Tur</div>
                          <div className="mt-2 text-[30px] font-black text-[#194e62]">{completedBoardCount}</div>
                        </div>
                        <div className="rounded-2xl border border-[#dce8ed] bg-[#f8fbfc] px-5 py-4">
                          <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[#8aa0ab]">Yanlış Tıklama</div>
                          <div className="mt-2 text-[30px] font-black text-[#194e62]">{wrongTapCount}</div>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-[#e1e8ed] bg-[#f8fbfc] p-6">
                        <div className="space-y-3">
                          {sessionResults.map((result) => (
                            <div
                              key={result.id}
                              className={`flex items-center justify-between rounded-xl border px-4 py-3 ${result.found ? 'border-[#cfe6d5] bg-[#f4fbf6]' : 'border-[#f0d7d7] bg-[#fff7f7]'}`}
                            >
                              <div className={`text-[17px] font-black ${result.found ? 'text-[#33835a]' : 'text-[#a14f4f]'}`}>
                                {result.topWord} / {result.bottomWord}
                              </div>
                              <div className={`text-[16px] font-bold ${result.found ? 'text-[#33835a]' : 'text-[#a14f4f]'}`}>
                                {result.found ? 'Doğru' : 'Yanlış'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <aside className="hidden lg:flex w-[320px] shrink-0 h-full min-h-0 rounded-xl border border-[#e0eaee] bg-white p-5 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="flex items-center gap-3">
                {userImage ? (
                  <img src={userImage} alt={userName} className="w-11 h-11 rounded-full object-cover bg-[#daf0f7]" />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-[#daf0f7] text-[#146f8e] flex items-center justify-center font-bold">
                    {userInitials || 'K'}
                  </div>
                )}
                <div>
                  <div className="text-[#355c68] text-sm font-bold">{userName}</div>
                  <div className="text-[#94a8b3] text-xs">Benzer kelime egzersizi</div>
                </div>
              </div>

              <div className="mt-6 flex flex-col items-center text-center">
                <div className="rounded-[2rem] bg-[#f7fafc] px-5 py-4">
                  <ModeIllustration mode={mode} />
                </div>
                <h3 className="mt-4 text-[34px] leading-none font-black" style={getThemeChromeTextStyle()}>{mode.name}</h3>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-6 text-center">
                <div>
                  <div className="text-[14px] text-[#b1b8bd]">Bulunan</div>
                  <div className="mt-1 text-[20px] leading-none font-black text-[#194e62]">{totalFoundCount}</div>
                </div>
                <div>
                  <div className="text-[14px] text-[#b1b8bd]">Kalan</div>
                  <div className="mt-1 text-[20px] leading-none font-black text-[#194e62]">{remainingTargetCount}</div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-6 text-center">
                <div>
                  <div className="text-[14px] text-[#b1b8bd]">Bu Tur</div>
                  <div className="mt-1 text-[20px] leading-none font-black text-[#194e62]">{selectedCorrectIds.length}/{settings.targetWordCount}</div>
                </div>
                <div>
                  <div className="text-[14px] text-[#b1b8bd]">Yanlış</div>
                  <div className="mt-1 text-[20px] leading-none font-black text-[#194e62]">{wrongTapCount}</div>
                </div>
              </div>

              {phase === 'review' && (
                <div className="mt-8 rounded-2xl border border-[#e8eef2] bg-[#f8fbfc] p-4">
                  <div className="flex items-start gap-3">
                    <CircleHelp className="mt-0.5 h-4 w-4 text-[#6d8b98]" />
                    <div className="text-sm leading-6 text-[#65808c]">
                      Süre doldu. İçeride bu oturum boyunca çıkan farklı kutuların doğru ve yanlış durumunu görebilirsiniz.
                    </div>
                  </div>
                </div>
              )}

              {phase === 'idle' && <SidebarPreparationCard paragraphs={introParagraphs} fontFamily={activeFontFamily} />}

              {wordsError ? (
                <div className="mt-6 rounded-xl border border-[#f3d6d6] bg-[#fff5f5] px-4 py-3 text-[13px] font-semibold leading-6 text-[#a44b4b]">
                  {wordsError}
                </div>
              ) : null}
            </div>

            <div className="shrink-0 pt-4">
              <p className="text-xs text-[#98a8b1]">Kalan Süre</p>
              <p className="text-[44px] leading-none font-black mt-1" style={getThemeChromeTextStyle()}>
                {formatClock(phase === 'review' ? 0 : remainingSeconds)}
              </p>

              <button
                onClick={() => {
                  if (phase === 'review') handleStart();
                  else if (isExerciseRunning) handleReset();
                  else handleStart();
                }}
                disabled={primaryActionDisabled}
                className={`mt-4 w-full h-12 rounded-lg font-bold text-base transition-colors inline-flex items-center justify-center gap-2 ${primaryActionDisabled
                  ? 'bg-[#dfe7ea] text-[#7f929c] cursor-not-allowed'
                  : 'text-white'
                  }`}
                style={primaryActionDisabled ? undefined : getThemeChromeButtonStyle()}
              >
                {phase === 'review' ? null : isExerciseRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {primaryLabel}
              </button>

              {phase !== 'review' && (
                <button
                  onClick={handleReset}
                  className="mt-4 w-full h-12 rounded-lg bg-[#f1f3f5] text-[#2d6175] font-bold text-base transition-colors hover:bg-[#e7ecef]"
                >
                  Sıfırla
                </button>
              )}
            </div>
          </aside>

          {isFullscreen && (
            <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 text-[12px] font-medium text-[#96a6af]">
              Tam ekrandan çıkmak için Esc tuşuna basın veya üst araç çubuğunu kullanın
            </div>
          )}

          {settingsOpen && (
            <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/55 backdrop-blur-[4px]">
              <div className="w-[920px] rounded-xl bg-white p-6 shadow-lg">
                <div className="mb-5 flex items-center justify-between">
                  <h4 className="text-xl font-bold" style={getThemedLabelStyle()}>
                    Egzersiz Ayarları
                  </h4>
                  <button className="text-gray-500 hover:text-gray-700" onClick={() => setSettingsOpen(false)}>
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-7">
                  <div className="space-y-5">
                    <SettingsStepperRow
                      label="Süre"
                      value={formatTextSearchDurationLabel(settings.durationSeconds)}
                      onDecrease={() => {
                        const index = SIMILAR_WORDS_DURATION_OPTIONS.indexOf(settings.durationSeconds as any);
                        const nextValue = SIMILAR_WORDS_DURATION_OPTIONS[Math.max(0, index - 1)];
                        setSettings((prev) => ({ ...prev, durationSeconds: nextValue }));
                      }}
                      onIncrease={() => {
                        const index = SIMILAR_WORDS_DURATION_OPTIONS.indexOf(settings.durationSeconds as any);
                        const nextValue = SIMILAR_WORDS_DURATION_OPTIONS[Math.min(SIMILAR_WORDS_DURATION_OPTIONS.length - 1, index + 1)];
                        setSettings((prev) => ({ ...prev, durationSeconds: nextValue }));
                      }}
                    />

                    <SettingsStepperRow
                      label="Kelime Sayısı"
                      value={`${settings.targetWordCount}`}
                      onDecrease={() => {
                        const index = SIMILAR_WORDS_TARGET_COUNT_OPTIONS.indexOf(settings.targetWordCount as any);
                        const nextValue = SIMILAR_WORDS_TARGET_COUNT_OPTIONS[Math.max(0, index - 1)];
                        setSettings((prev) => ({ ...prev, targetWordCount: nextValue }));
                      }}
                      onIncrease={() => {
                        const index = SIMILAR_WORDS_TARGET_COUNT_OPTIONS.indexOf(settings.targetWordCount as any);
                        const nextValue = SIMILAR_WORDS_TARGET_COUNT_OPTIONS[Math.min(SIMILAR_WORDS_TARGET_COUNT_OPTIONS.length - 1, index + 1)];
                        setSettings((prev) => ({ ...prev, targetWordCount: nextValue }));
                      }}
                    />

                    <SettingsToggleRow
                      label="Başlamadan önce geriye say"
                      checked={settings.enableCountdown}
                      onChange={() => setSettings((prev) => ({ ...prev, enableCountdown: !prev.enableCountdown }))}
                    />
                  </div>

                  <div className="space-y-5 border-l border-[#edf2f5] pl-7">
                    <div>
                      <div className="mb-2 flex items-center justify-between">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Boyutu</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {FONT_SIZE_LABELS[settings.fontSize]}
                        </span>
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        {FONT_SIZE_OPTIONS.map((sizeOption) => {
                          const isActive = settings.fontSize === sizeOption.value;
                          return (
                            <button
                              key={sizeOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, fontSize: sizeOption.value }))}
                              className="h-10 rounded-lg border transition-colors"
                              style={getThemedOptionButtonStyle(isActive)}
                            >
                              <span className={`font-semibold ${sizeOption.preview}`}>{sizeOption.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <p className="mb-2 text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Karakteri</p>
                      <div className="grid grid-cols-2 gap-2">
                        {FONT_FAMILY_OPTIONS.map((fontOption) => {
                          const isActive = settings.fontFamily === fontOption.value;
                          return (
                            <button
                              key={fontOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, fontFamily: fontOption.value }))}
                              className="h-10 rounded-lg border text-sm transition-colors"
                              style={{ ...getThemedOptionButtonStyle(isActive), fontFamily: fontOption.css }}
                            >
                              {fontOption.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <div className="mb-2 flex items-center justify-between">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Vurgu Rengi</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {ACCENT_COLOR_OPTIONS.find((colorOption) => colorOption.value === settings.accentColor)?.label || 'Varsayılan'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        {ACCENT_COLOR_OPTIONS.map((colorOption) => {
                          const isActive = settings.accentColor === colorOption.value;
                          return (
                            <button
                              key={colorOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, accentColor: colorOption.value }))}
                              className="h-7 w-7 rounded-full border-2 transition-transform hover:scale-105"
                              style={{ ...getThemedSwatchStyle(isActive, colorOption.hex), backgroundColor: colorOption.hex }}
                              title={colorOption.label}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

type WordSearchSettings = Pick<PilotSettings, 'fontSize' | 'fontFamily' | 'accentColor' | 'enableCountdown'> & {
  durationSeconds: number;
  gridSize: number;
  targetWordCount: number;
  allowedDirections: WordSearchDirectionKey[];
};

type WordSearchDirectionKey = 'yatay' | 'dikey' | 'ters' | 'caprazlama';

type WordSearchTarget = {
  key: string;
  display: string;
  positions: string[];
};

type WordSearchCell = {
  id: string;
  letter: string;
  wordKeys: string[];
};

type WordSearchBoard = {
  cells: WordSearchCell[][];
  targets: WordSearchTarget[];
};

const WORD_SEARCH_DURATION_OPTIONS = [60, 90, 120, 150, 180, 210, 240, 270, 300] as const;
const WORD_SEARCH_GRID_SIZE_OPTIONS = [12, 13, 14, 15, 16, 17, 18, 19, 20] as const;
const WORD_SEARCH_TARGET_COUNT_OPTIONS = [2, 3, 4, 5, 6, 7, 8, 9] as const;
const WORD_SEARCH_ALPHABET = ['A', 'B', 'C', 'Ç', 'D', 'E', 'F', 'G', 'Ğ', 'H', 'I', 'İ', 'J', 'K', 'L', 'M', 'N', 'O', 'Ö', 'P', 'R', 'S', 'Ş', 'T', 'U', 'Ü', 'V', 'Y', 'Z'];
const WORD_SEARCH_DIRECTION_OPTIONS: Array<{ key: WordSearchDirectionKey; label: string }> = [
  { key: 'yatay', label: 'Yatay' },
  { key: 'dikey', label: 'Dikey' },
  { key: 'ters', label: 'Ters' },
  { key: 'caprazlama', label: 'Çaprazlama' },
];
const WORD_SEARCH_DIRECTION_MAP: Record<WordSearchDirectionKey, Array<{ rowStep: number; colStep: number }>> = {
  yatay: [{ rowStep: 0, colStep: 1 }],
  dikey: [{ rowStep: 1, colStep: 0 }],
  ters: [{ rowStep: 0, colStep: -1 }, { rowStep: -1, colStep: 0 }],
  caprazlama: [
    { rowStep: 1, colStep: 1 },
    { rowStep: 1, colStep: -1 },
    { rowStep: -1, colStep: 1 },
    { rowStep: -1, colStep: -1 },
  ],
};

const sanitizeWordSearchWord = (value: string) => stripTextSearchWord(String(value || ''))
  .replace(/[^0-9A-Za-zÇĞİÖŞÜçğıöşü]/g, '')
  .trim();

const getWordSearchCandidateScore = (word: string) => {
  const normalized = normalizeMatchingWordKey(word);
  const uniqueLetterCount = new Set(word.split('')).size;
  const lengthScore = word.length >= 6 && word.length <= 10 ? 10 : word.length >= 4 ? 7 : 4;
  const uniqueLetterScore = Math.min(6, uniqueLetterCount);
  const repetitionPenalty = Math.max(0, word.length - uniqueLetterCount - 1);
  const stopWordPenalty = TEXT_SEARCH_STOP_WORDS.has(normalized) ? 20 : 0;
  return lengthScore + uniqueLetterScore - repetitionPenalty - stopWordPenalty;
};

const shuffleWordSearchItems = <T,>(items: T[]) => (
  items
    .map((item) => ({ item, order: Math.random() }))
    .sort((left, right) => left.order - right.order)
    .map((entry) => entry.item)
);

const buildWordSearchCandidates = (
  sourceWords: string[],
  gridSize: number,
  targetWordCount: number,
  excludedKeys: string[] = [],
) => {
  const excludedKeySet = new Set(excludedKeys);
  const seen = new Set<string>();
  const candidates = sourceWords
    .map((word) => sanitizeWordSearchWord(word).toLocaleUpperCase('tr-TR'))
    .filter((word) => word.length >= 3 && word.length <= gridSize)
    .filter((word) => {
      const key = normalizeMatchingWordKey(word);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((word) => ({
      key: normalizeMatchingWordKey(word),
      display: word,
      score: getWordSearchCandidateScore(word),
    }));

  const availableCandidates = candidates.filter((candidate) => !excludedKeySet.has(candidate.key));
  const candidatePool = availableCandidates.length >= targetWordCount ? availableCandidates : candidates;
  const weighted = shuffleWordSearchItems(candidatePool)
    .sort((left, right) => {
      if (right.score !== left.score) return right.score - left.score;
      return right.display.length - left.display.length;
    });

  return shuffleWordSearchItems(weighted.slice(0, Math.max(targetWordCount * 8, targetWordCount + 10)));
};

const buildWordSearchBoard = (
  sourceWords: string[],
  gridSize: WordSearchSettings['gridSize'],
  targetWordCount: WordSearchSettings['targetWordCount'],
  allowedDirections: WordSearchSettings['allowedDirections'],
  excludedKeys: string[] = [],
): WordSearchBoard | null => {
  const candidates = buildWordSearchCandidates(sourceWords, gridSize, targetWordCount, excludedKeys);
  if (candidates.length < targetWordCount) return null;
  const directions = Array.from(
    new Map(
      (allowedDirections.length > 0 ? allowedDirections : ['yatay', 'dikey'] as WordSearchDirectionKey[])
        .flatMap((directionKey) => WORD_SEARCH_DIRECTION_MAP[directionKey] || [])
        .map((direction) => [`${direction.rowStep}:${direction.colStep}`, direction]),
    ).values(),
  );
  if (directions.length === 0) return null;

  for (let boardAttempt = 0; boardAttempt < 36; boardAttempt += 1) {
    const selectedTargets = shuffleWordSearchItems(candidates)
      .slice(0, targetWordCount)
      .sort((left, right) => right.display.length - left.display.length);
    const cells: Array<Array<{ letter: string; wordKeys: Set<string> }>> = Array.from({ length: gridSize }, () => (
      Array.from({ length: gridSize }, () => ({ letter: '', wordKeys: new Set<string>() }))
    ));
    const targets: WordSearchTarget[] = [];
    let canBuildBoard = true;

    selectedTargets.forEach((target) => {
      if (!canBuildBoard) return;
      const letters = target.display.split('');
      let placed = false;

      for (let attempt = 0; attempt < 180 && !placed; attempt += 1) {
        const direction = shuffleWordSearchItems(directions)[0];
        const minRow = direction.rowStep === -1 ? letters.length - 1 : 0;
        const maxRow = direction.rowStep === 1 ? gridSize - letters.length : gridSize - 1;
        const minCol = direction.colStep === -1 ? letters.length - 1 : 0;
        const maxCol = direction.colStep === 1 ? gridSize - letters.length : gridSize - 1;

        if (maxRow < minRow || maxCol < minCol) continue;

        const startRow = minRow + Math.floor(Math.random() * (maxRow - minRow + 1));
        const startCol = minCol + Math.floor(Math.random() * (maxCol - minCol + 1));
        const positions = letters.map((_, index) => ({
          row: startRow + direction.rowStep * index,
          col: startCol + direction.colStep * index,
        }));

        const fits = positions.every((position, index) => {
          const cell = cells[position.row]?.[position.col];
          return Boolean(cell) && (!cell.letter || cell.letter === letters[index]);
        });

        if (!fits) continue;

        positions.forEach((position, index) => {
          const cell = cells[position.row][position.col];
          cell.letter = letters[index];
          cell.wordKeys.add(target.key);
        });

        targets.push({
          key: target.key,
          display: target.display,
          positions: positions.map((position) => `${position.row}-${position.col}`),
        });
        placed = true;
      }

      if (!placed) {
        canBuildBoard = false;
      }
    });

    if (!canBuildBoard || targets.length !== targetWordCount) continue;

    const finalizedCells: WordSearchCell[][] = cells.map((row, rowIndex) => (
      row.map((cell, colIndex) => ({
        id: `${rowIndex}-${colIndex}`,
        letter: cell.letter || WORD_SEARCH_ALPHABET[Math.floor(Math.random() * WORD_SEARCH_ALPHABET.length)],
        wordKeys: Array.from(cell.wordKeys),
      }))
    ));

    return { cells: finalizedCells, targets };
  }

  return null;
};

const getWordSearchCellFontSize = (gridSize: number) => {
  if (gridSize >= 19) return 'clamp(0.72rem, 1vw, 0.95rem)';
  if (gridSize >= 17) return 'clamp(0.78rem, 1.05vw, 1rem)';
  if (gridSize >= 15) return 'clamp(0.86rem, 1.1vw, 1.08rem)';
  return 'clamp(0.95rem, 1.2vw, 1.18rem)';
};

export const WordSearchExerciseView: React.FC<{
  profile: any;
  mode: ModeConfig;
  onClose: () => void;
}> = ({ profile, mode, onClose }) => {
  const { user } = useAuth();
  const fullscreenContainerRef = useRef<HTMLDivElement | null>(null);
  const wrongCellTimeoutsRef = useRef<number[]>([]);
  const boardTransitionTimeoutRef = useRef<number | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [muted, setMuted] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'countdown' | 'running' | 'review'>('idle');
  const [countdown, setCountdown] = useState(3);
  const [remainingSeconds, setRemainingSeconds] = useState(60);
  const [remoteWordPool, setRemoteWordPool] = useState<string[]>([]);
  const [wordPoolLoading, setWordPoolLoading] = useState(true);
  const [wordPoolError, setWordPoolError] = useState<string | null>(null);
  const [board, setBoard] = useState<WordSearchBoard | null>(null);
  const [foundWordKeys, setFoundWordKeys] = useState<string[]>([]);
  const [wrongCellIds, setWrongCellIds] = useState<string[]>([]);
  const [reviewResults, setReviewResults] = useState<Array<{ key: string; display: string; found: boolean }>>([]);
  const [sessionWordKeys, setSessionWordKeys] = useState<string[]>([]);
  const [totalFoundCount, setTotalFoundCount] = useState(0);
  const [wrongTapCount, setWrongTapCount] = useState(0);
  const [completedBoardCount, setCompletedBoardCount] = useState(0);
  const [settings, setSettings] = useState<WordSearchSettings>(() => {
    const sharedDefaults = applySharedExerciseSettings<{
      fontSize: PilotSettings['fontSize'];
      fontFamily: PilotSettings['fontFamily'];
      accentColor: PilotSettings['accentColor'];
      enableCountdown: PilotSettings['enableCountdown'];
      exerciseDurationSeconds: number;
    }>({
      fontSize: 'orta',
      fontFamily: 'GoogleSans',
      accentColor: 'default',
      enableCountdown: true,
      exerciseDurationSeconds: 60,
    });

    return {
      durationSeconds: WORD_SEARCH_DURATION_OPTIONS.includes(Number(sharedDefaults.exerciseDurationSeconds) as any)
        ? Number(sharedDefaults.exerciseDurationSeconds)
        : 60,
      gridSize: 12,
      targetWordCount: 4,
      allowedDirections: ['yatay', 'dikey'],
      fontSize: sharedDefaults.fontSize,
      fontFamily: sharedDefaults.fontFamily,
      accentColor: sharedDefaults.accentColor,
      enableCountdown: Boolean(sharedDefaults.enableCountdown ?? true),
    };
  });

  const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || profile?.name || 'Kullanıcı';
  const userImage = user?.avatar || user?.media?.url || user?.profilePhoto?.url || null;
  const userInitials = userName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part.charAt(0).toUpperCase())
    .join('');
  const introParagraphs = [
    'Üst şeritte verilen hedef kelimeleri kısa süre tarayın ve tablo içindeki harf alanında yerlerini bulun.',
    'Doğru hücreye bastığınızda o kelime tamamlanır. Yanlış seçilen hücre kısa süre kırmızı yanar ve oyun akışı bozulmadan devam eder.',
    'Bir tabloyu süre bitmeden tamamen temizlerseniz yeni kelimelerle yeni bir tablo otomatik açılır; bulunan toplamı ve kalan süre korunur.',
  ];
  const fallbackWordPool = useMemo(() => (
    Array.from(
      new Set(
        Object.values(PILOT_TEST_LEVEL_WORDS)
          .flat()
          .map((word) => sanitizeWordSearchWord(word))
          .filter(Boolean),
      ),
    )
  ), []);
  const allAvailableWords = useMemo(() => {
    const source = remoteWordPool.length > 0 ? remoteWordPool : fallbackWordPool;
    return Array.from(
      new Set(
        source
          .map((word) => sanitizeWordSearchWord(word))
          .filter(Boolean),
      ),
    );
  }, [fallbackWordPool, remoteWordPool]);
  const activeAccent = resolveAccentColor(settings.accentColor);
  const activeFontFamily = FONT_FAMILY_OPTIONS.find((item) => item.value === settings.fontFamily)?.css || settings.fontFamily;
  const isExerciseRunning = phase === 'running' || phase === 'countdown';
  const primaryLabel = phase === 'review' ? 'Yeniden Başlat' : isExerciseRunning ? 'Durdur' : 'Başlat';
  const primaryActionDisabled = wordPoolLoading || allAvailableWords.length < settings.targetWordCount || !board;
  const foundWordSet = useMemo(() => new Set(foundWordKeys), [foundWordKeys]);
  const foundInCurrentBoard = board?.targets.filter((target) => foundWordSet.has(target.key)).length || 0;
  const activeTargetWords = board?.targets.map((target) => target.display) || [];
  const pendingTargetWords = board?.targets
    .filter((target) => !foundWordSet.has(target.key))
    .map((target) => target.display) || [];
  const activeTargetLabel = pendingTargetWords[0] || '-';
  const boardCellFontSize = getWordSearchCellFontSize(settings.gridSize);

  const clearWrongCellTimeouts = () => {
    wrongCellTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
    wrongCellTimeoutsRef.current = [];
  };

  const clearBoardTransitionTimeout = () => {
    if (boardTransitionTimeoutRef.current) {
      window.clearTimeout(boardTransitionTimeoutRef.current);
      boardTransitionTimeoutRef.current = null;
    }
  };

  const prepareBoard = useCallback(() => {
    const nextBoard = buildWordSearchBoard(
      allAvailableWords,
      settings.gridSize,
      settings.targetWordCount,
      settings.allowedDirections,
      sessionWordKeys,
    );
    setBoard(nextBoard);
    setFoundWordKeys([]);
    setWrongCellIds([]);
    if (nextBoard) {
      setSessionWordKeys((prev) => {
        const merged = new Set(prev);
        nextBoard.targets.forEach((target) => merged.add(target.key));
        return Array.from(merged);
      });
    }
    return nextBoard;
  }, [allAvailableWords, sessionWordKeys, settings.allowedDirections, settings.gridSize, settings.targetWordCount]);

  const handleReset = useCallback(() => {
    clearBoardTransitionTimeout();
    clearWrongCellTimeouts();
    setPhase('idle');
    setCountdown(3);
    setRemainingSeconds(settings.durationSeconds);
    setFoundWordKeys([]);
    setWrongCellIds([]);
    setReviewResults([]);
    setSessionWordKeys([]);
    setTotalFoundCount(0);
    setWrongTapCount(0);
    setCompletedBoardCount(0);
    setBoard(null);
  }, [settings.durationSeconds]);

  useEffect(() => {
    setSettings((prev) => ({
      ...prev,
      ...applySharedExerciseSettings({
        fontSize: prev.fontSize,
        fontFamily: prev.fontFamily,
        accentColor: prev.accentColor,
        enableCountdown: prev.enableCountdown,
        exerciseDurationSeconds: prev.durationSeconds,
      }),
    }));
  }, []);

  useEffect(() => {
    if (!settingsOpen) return;
    persistSharedExerciseSettings({
      fontSize: settings.fontSize,
      fontFamily: settings.fontFamily,
      accentColor: settings.accentColor,
      enableCountdown: settings.enableCountdown,
      exerciseDurationSeconds: settings.durationSeconds,
    });
  }, [settings, settingsOpen]);

  useEffect(() => {
    let mounted = true;
    setWordPoolLoading(true);

    ApiService.getApi('speed-reading/exercises/word-pools?type=takistoskop&limit=500')
      .then((response: any) => {
        if (!mounted) return;
        const words = Array.isArray(response?.items)
          ? response.items.map((item: any) => String(item?.word || '').trim()).filter(Boolean)
          : [];
        setRemoteWordPool(words);
        setWordPoolError(words.length > 0 ? null : 'Kelime havuzunda kullanılabilir kayıt bulunamadı.');
      })
      .catch((error) => {
        if (!mounted) return;
        console.error('Kelime Arama kelime havuzu yüklenemedi:', error);
        setRemoteWordPool([]);
        setWordPoolError('Kelime havuzu veritabanından yüklenemedi. Yerel yedek kelimeler kullanılıyor.');
      })
      .finally(() => {
        if (!mounted) return;
        setWordPoolLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => () => {
    clearBoardTransitionTimeout();
    clearWrongCellTimeouts();
  }, []);

  useEffect(() => {
    if (phase !== 'idle') return;
    setRemainingSeconds(settings.durationSeconds);
  }, [phase, settings.durationSeconds]);

  useEffect(() => {
    if (phase !== 'idle') return;
    prepareBoard();
  }, [phase, prepareBoard]);

  useEffect(() => {
    if (phase !== 'countdown' || countdown <= 0) return;

    const timer = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [countdown, phase]);

  useEffect(() => {
    if (phase !== 'countdown' || countdown !== 0) return;
    setPhase('running');
  }, [countdown, phase]);

  useEffect(() => {
    if (phase !== 'running') return;

    const timer = window.setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          setPhase('review');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'review' || !board) return;
    setReviewResults((prev) => {
      const merged = new Map(prev.map((item) => [item.key, item]));
      board.targets.forEach((target) => {
        const previous = merged.get(target.key);
        merged.set(target.key, {
          key: target.key,
          display: target.display,
          found: previous?.found || foundWordSet.has(target.key),
        });
      });
      return Array.from(merged.values());
    });
  }, [board, foundWordSet, phase]);

  useEffect(() => {
    if (phase !== 'running' || !board) return;
    if (board.targets.length === 0 || foundWordKeys.length < board.targets.length) return;

    setReviewResults((prev) => {
      const merged = new Map(prev.map((item) => [item.key, item]));
      board.targets.forEach((target) => {
        merged.set(target.key, {
          key: target.key,
          display: target.display,
          found: true,
        });
      });
      return Array.from(merged.values());
    });

    clearBoardTransitionTimeout();
    boardTransitionTimeoutRef.current = window.setTimeout(() => {
      boardTransitionTimeoutRef.current = null;
      setCompletedBoardCount((prev) => prev + 1);
      const nextBoard = prepareBoard();
      if (!nextBoard) {
        setPhase('review');
      }
    }, 420);

    return () => clearBoardTransitionTimeout();
  }, [board, foundWordKeys.length, phase, prepareBoard]);

  const handleCloseExercise = async () => {
    if (document.fullscreenElement === fullscreenContainerRef.current) {
      try {
        await document.exitFullscreen();
      } catch (error) {
        console.error('Tam ekrandan çıkılamadı:', error);
      }
    }

    handleReset();
    onClose();
  };

  const handleToggleFullscreen = async () => {
    const container = fullscreenContainerRef.current;
    if (!container) return;

    try {
      if (document.fullscreenElement === container) {
        await document.exitFullscreen();
        return;
      }

      await container.requestFullscreen();
    } catch (error) {
      console.error('Tam ekran değiştirilemedi:', error);
    }
  };

  const handleStart = () => {
    let nextBoard = board;
    if (!nextBoard) {
      nextBoard = prepareBoard();
    }
    if (!nextBoard) return;

    clearBoardTransitionTimeout();
    clearWrongCellTimeouts();
    setReviewResults([]);
    setSessionWordKeys([]);
    setFoundWordKeys([]);
    setWrongCellIds([]);
    setTotalFoundCount(0);
    setWrongTapCount(0);
    setCompletedBoardCount(0);
    setRemainingSeconds(settings.durationSeconds);

    if (settings.enableCountdown) {
      setCountdown(3);
      setPhase('countdown');
      return;
    }

    setPhase('running');
  };

  const flashWrongCell = (cellId: string) => {
    setWrongTapCount((prev) => prev + 1);
    setWrongCellIds((prev) => (prev.includes(cellId) ? prev : [...prev, cellId]));
    const timeoutId = window.setTimeout(() => {
      setWrongCellIds((prev) => prev.filter((id) => id !== cellId));
    }, 460);
    wrongCellTimeoutsRef.current.push(timeoutId);
  };

  const handleCellClick = (cell: WordSearchCell) => {
    if (phase !== 'running') return;
    const unresolvedWordKey = cell.wordKeys.find((wordKey) => !foundWordSet.has(wordKey));

    if (!unresolvedWordKey) {
      flashWrongCell(cell.id);
      return;
    }

    setFoundWordKeys((prev) => {
      if (prev.includes(unresolvedWordKey)) return prev;
      return [...prev, unresolvedWordKey];
    });
    setTotalFoundCount((prev) => prev + 1);
  };

  return (
    <div
      ref={fullscreenContainerRef}
      className={`${isFullscreen ? 'h-screen p-3' : 'h-full p-6'} bg-[#f3f5f7]`}
    >
      <div className={`${isFullscreen ? 'max-w-none' : 'max-w-[1260px]'} mx-auto h-full`}>
        <div className="h-full border border-[#e6edf1] rounded-xl bg-[#f8fafb] px-6 py-5 flex gap-5 relative">
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ScanSearch className="w-[19px] h-[19px]" style={getThemeChromeTextStyle()} />
                <div className="text-[20px] font-bold" style={getThemeChromeTextStyle()}>{mode.name}</div>
              </div>
              <div className="flex items-center gap-3 text-[#afbbc2]">
                <button onClick={() => setMuted((current) => !current)} className="hover:text-[#6b7f8a]">
                  {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => setSettingsOpen(true)}
                  disabled={isExerciseRunning}
                  className="hover:text-[#6b7f8a] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Settings2 className="w-5 h-5" />
                </button>
                <button onClick={() => { void handleToggleFullscreen(); }} className="hover:text-[#6b7f8a]">
                  {isFullscreen ? <Shrink className="w-5 h-5" /> : <Expand className="w-5 h-5" />}
                </button>
                <button onClick={() => { void handleCloseExercise(); }} className="hover:text-[#6b7f8a]">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-hidden rounded-[10px] border border-[#e8edf0] bg-white flex flex-col">
              <div className="relative min-h-0 flex-1 bg-[#fbfbfb]">
                {(phase === 'idle' || phase === 'countdown' || phase === 'running') && (
                  <div className="absolute inset-0 flex items-center justify-center p-5">
                    <div
                      className="grid aspect-square w-full max-w-[860px] rounded-[22px] border border-[#e2eaee] bg-white p-3 shadow-[0_18px_45px_rgba(18,71,94,0.06)] transition-all duration-300"
                      style={{
                        gridTemplateColumns: `repeat(${settings.gridSize}, minmax(0, 1fr))`,
                        filter: phase === 'idle' || phase === 'countdown' ? 'blur(2.8px)' : 'none',
                        opacity: wordPoolLoading ? 0.5 : 1,
                      }}
                    >
                      {(board?.cells || []).flat().map((cell) => {
                        const isFound = cell.wordKeys.some((wordKey) => foundWordSet.has(wordKey));
                        const isWrong = wrongCellIds.includes(cell.id);

                        return (
                          <button
                            key={cell.id}
                            type="button"
                            onClick={() => handleCellClick(cell)}
                            className="flex aspect-square items-center justify-center rounded-[10px] border transition-all duration-200"
                            style={{
                              fontSize: boardCellFontSize,
                              fontWeight: 800,
                              color: isFound ? '#ffffff' : '#37505c',
                              backgroundColor: isWrong ? '#ef7d7d' : isFound ? activeAccent : '#f7fafc',
                              borderColor: isWrong ? '#ef7d7d' : isFound ? activeAccent : '#e2eaee',
                              boxShadow: isFound ? `0 0 0 1px ${activeAccent}22` : 'none',
                              cursor: phase === 'running' ? 'pointer' : 'default',
                              fontFamily: activeFontFamily,
                            }}
                          >
                            {cell.letter}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {wordPoolLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/82 backdrop-blur-[2px]">
                    <div className="flex flex-col items-center gap-4">
                      <div className="h-9 w-9 animate-spin rounded-full border-2 border-[#2f5563] border-t-transparent" />
                      <p className="text-[18px] font-bold text-[#2e6276]">Kelime alanı hazırlanıyor...</p>
                    </div>
                  </div>
                )}

                {phase === 'countdown' && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/58 backdrop-blur-[1px]">
                    <span className="text-7xl font-extrabold text-[#18485c]">{countdown}</span>
                  </div>
                )}

                {phase === 'review' && (
                  <div className="absolute inset-0 overflow-y-auto bg-white px-8 py-8">
                    <div className="mx-auto max-w-[900px] space-y-5">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="rounded-2xl border border-[#dce8ed] bg-[#f8fbfc] px-5 py-4">
                          <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[#8aa0ab]">Bulunan</div>
                          <div className="mt-2 text-[30px] font-black text-[#194e62]">{totalFoundCount}</div>
                        </div>
                        <div className="rounded-2xl border border-[#dce8ed] bg-[#f8fbfc] px-5 py-4">
                          <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[#8aa0ab]">Tamamlanan Tur</div>
                          <div className="mt-2 text-[30px] font-black text-[#194e62]">{completedBoardCount}</div>
                        </div>
                        <div className="rounded-2xl border border-[#dce8ed] bg-[#f8fbfc] px-5 py-4">
                          <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[#8aa0ab]">Yanlış Tıklama</div>
                          <div className="mt-2 text-[30px] font-black text-[#194e62]">{wrongTapCount}</div>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-[#e1e8ed] bg-[#f8fbfc] p-6">
                        <div className="space-y-3">
                          {reviewResults.map((result) => (
                            <div
                              key={result.key}
                              className={`flex items-center justify-between rounded-xl border px-4 py-3 ${result.found ? 'border-[#cfe6d5] bg-[#f4fbf6]' : 'border-[#f0d7d7] bg-[#fff7f7]'}`}
                            >
                              <div className={`text-[17px] font-black ${result.found ? 'text-[#33835a]' : 'text-[#a14f4f]'}`}>
                                {result.display}
                              </div>
                              <div className={`text-[16px] font-bold ${result.found ? 'text-[#33835a]' : 'text-[#a14f4f]'}`}>
                                {result.found ? 'Doğru' : 'Yanlış'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <aside className="hidden lg:flex w-[320px] shrink-0 h-full min-h-0 rounded-xl border border-[#e0eaee] bg-white p-5 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="flex items-center gap-3">
                {userImage ? (
                  <img src={userImage} alt={userName} className="w-11 h-11 rounded-full object-cover bg-[#daf0f7]" />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-[#daf0f7] text-[#146f8e] flex items-center justify-center font-bold">
                    {userInitials || 'K'}
                  </div>
                )}
                <div>
                  <div className="text-[#355c68] text-sm font-bold">{userName}</div>
                  <div className="text-[#94a8b3] text-xs">Kelime tarama egzersizi</div>
                </div>
              </div>

              <div className="mt-6 flex flex-col items-center text-center">
                <div className="rounded-[2rem] bg-[#f7fafc] px-5 py-4">
                  <ModeIllustration mode={mode} />
                </div>
                <h3 className="mt-4 text-[34px] leading-none font-black" style={getThemeChromeTextStyle()}>{mode.name}</h3>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-6 text-center">
                <div>
                  <div className="text-[14px] text-[#b1b8bd]">Bulunan</div>
                  <div className="mt-1 text-[20px] leading-none font-black text-[#194e62]">{totalFoundCount}</div>
                </div>
                <div>
                  <div className="text-[14px] text-[#b1b8bd]">Bu Tur</div>
                  <div className="mt-1 text-[20px] leading-none font-black text-[#194e62]">{foundInCurrentBoard}/{board?.targets.length || 0}</div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-6 text-center">
                <div>
                  <div className="text-[14px] text-[#b1b8bd]">Aktif Hedef</div>
                  <div className="mt-1 text-[20px] leading-none font-black text-[#194e62]">{activeTargetLabel}</div>
                </div>
                <div>
                  <div className="text-[14px] text-[#b1b8bd]">Yanlış</div>
                  <div className="mt-1 text-[20px] leading-none font-black text-[#194e62]">{wrongTapCount}</div>
                </div>
              </div>

              {phase === 'review' && (
                <div className="mt-8 rounded-2xl border border-[#e8eef2] bg-[#f8fbfc] p-4">
                  <div className="flex items-start gap-3">
                    <CircleHelp className="mt-0.5 h-4 w-4 text-[#6d8b98]" />
                    <div className="text-sm leading-6 text-[#65808c]">
                      Süre doldu. İçeride bu oturum boyunca çıkan kelimelerin doğru ve yanlış durumunu görebilirsiniz.
                    </div>
                  </div>
                </div>
              )}

              {phase === 'idle' && <SidebarPreparationCard paragraphs={introParagraphs} />}

              <div className="mt-6 rounded-2xl border border-[#e8eef2] bg-[#fbfdfe] p-4">
                <div className="flex items-center gap-2 text-[#6a8390]">
                  <Target className="h-4 w-4" />
                  <span className="text-xs font-semibold uppercase tracking-[0.08em]">Hedef Kelimeler</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {activeTargetWords.map((word) => {
                    const isFound = foundWordSet.has(normalizeMatchingWordKey(word));
                    return (
                      <span
                        key={`sidebar-${word}`}
                        className={`rounded-full px-3 py-1 text-[12px] font-bold ${isFound ? 'bg-[#e6f6ea] text-[#2f8657]' : 'bg-[#eef4f7] text-[#476270]'}`}
                      >
                        {word}
                      </span>
                    );
                  })}
                </div>
              </div>

              {wordPoolError ? (
                <div className="mt-6 rounded-xl border border-[#f3d6d6] bg-[#fff5f5] px-4 py-3 text-[13px] font-semibold leading-6 text-[#a44b4b]">
                  {wordPoolError}
                </div>
              ) : null}
            </div>

            <div className="shrink-0 pt-4">
              <p className="text-xs text-[#98a8b1]">Kalan Süre</p>
              <p className="text-[44px] leading-none font-black mt-1" style={getThemeChromeTextStyle()}>
                {formatClock(phase === 'review' ? 0 : remainingSeconds)}
              </p>

              <button
                onClick={() => {
                  if (phase === 'review') handleStart();
                  else if (isExerciseRunning) handleReset();
                  else handleStart();
                }}
                disabled={primaryActionDisabled}
                className={`mt-4 w-full h-12 rounded-lg font-bold text-base transition-colors inline-flex items-center justify-center gap-2 ${primaryActionDisabled
                  ? 'bg-[#dfe7ea] text-[#7f929c] cursor-not-allowed'
                  : 'text-white'
                  }`}
                style={primaryActionDisabled ? undefined : getThemeChromeButtonStyle()}
              >
                {phase === 'review' ? null : isExerciseRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {primaryLabel}
              </button>

              {phase !== 'review' && (
                <button
                  onClick={handleReset}
                  className="mt-4 w-full h-12 rounded-lg bg-[#f1f3f5] text-[#2d6175] font-bold text-base transition-colors hover:bg-[#e7ecef]"
                >
                  Sıfırla
                </button>
              )}
            </div>
          </aside>

          {isFullscreen && (
            <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 text-[12px] font-medium text-[#96a6af]">
              Tam ekrandan çıkmak için Esc tuşuna basın veya üst araç çubuğunu kullanın
            </div>
          )}

          {settingsOpen && (
            <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/55 backdrop-blur-[4px]">
              <div className="w-[920px] rounded-xl bg-white p-6 shadow-lg">
                <div className="mb-5 flex items-center justify-between">
                  <h4 className="text-xl font-bold" style={getThemedLabelStyle()}>
                    Egzersiz Ayarları
                  </h4>
                  <button className="text-gray-500 hover:text-gray-700" onClick={() => setSettingsOpen(false)}>
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-7">
                  <div className="space-y-5">
                    <SettingsStepperRow
                      label="Süre"
                      value={formatTextSearchDurationLabel(settings.durationSeconds)}
                      onDecrease={() => {
                        const index = WORD_SEARCH_DURATION_OPTIONS.indexOf(settings.durationSeconds as any);
                        const nextValue = WORD_SEARCH_DURATION_OPTIONS[Math.max(0, index - 1)];
                        setSettings((prev) => ({ ...prev, durationSeconds: nextValue }));
                      }}
                      onIncrease={() => {
                        const index = WORD_SEARCH_DURATION_OPTIONS.indexOf(settings.durationSeconds as any);
                        const nextValue = WORD_SEARCH_DURATION_OPTIONS[Math.min(WORD_SEARCH_DURATION_OPTIONS.length - 1, index + 1)];
                        setSettings((prev) => ({ ...prev, durationSeconds: nextValue }));
                      }}
                    />

                    <SettingsStepperRow
                      label="Kutu Boyutu"
                      value={`${settings.gridSize} x ${settings.gridSize}`}
                      onDecrease={() => {
                        const index = WORD_SEARCH_GRID_SIZE_OPTIONS.indexOf(settings.gridSize as any);
                        const nextValue = WORD_SEARCH_GRID_SIZE_OPTIONS[Math.max(0, index - 1)];
                        setSettings((prev) => ({ ...prev, gridSize: nextValue }));
                      }}
                      onIncrease={() => {
                        const index = WORD_SEARCH_GRID_SIZE_OPTIONS.indexOf(settings.gridSize as any);
                        const nextValue = WORD_SEARCH_GRID_SIZE_OPTIONS[Math.min(WORD_SEARCH_GRID_SIZE_OPTIONS.length - 1, index + 1)];
                        setSettings((prev) => ({ ...prev, gridSize: nextValue }));
                      }}
                    />

                    <SettingsStepperRow
                      label="Kelime Sayısı"
                      value={`${settings.targetWordCount}`}
                      onDecrease={() => {
                        const index = WORD_SEARCH_TARGET_COUNT_OPTIONS.indexOf(settings.targetWordCount as any);
                        const nextValue = WORD_SEARCH_TARGET_COUNT_OPTIONS[Math.max(0, index - 1)];
                        setSettings((prev) => ({ ...prev, targetWordCount: nextValue }));
                      }}
                      onIncrease={() => {
                        const index = WORD_SEARCH_TARGET_COUNT_OPTIONS.indexOf(settings.targetWordCount as any);
                        const nextValue = WORD_SEARCH_TARGET_COUNT_OPTIONS[Math.min(WORD_SEARCH_TARGET_COUNT_OPTIONS.length - 1, index + 1)];
                        setSettings((prev) => ({ ...prev, targetWordCount: nextValue }));
                      }}
                    />

                    <SettingsToggleRow
                      label="Başlamadan önce geriye say"
                      checked={settings.enableCountdown}
                      onChange={() => setSettings((prev) => ({ ...prev, enableCountdown: !prev.enableCountdown }))}
                    />

                    <div className="overflow-hidden rounded-xl border border-[#d9e6eb] bg-white">
                      <div className="border-b border-[#e7eef2] px-4 py-3 text-sm font-bold uppercase tracking-[0.08em] text-[#8b9ca5]">
                        Egzersiz Zorluğu
                      </div>
                      <div className="flex flex-wrap gap-3 px-4 py-4">
                        {WORD_SEARCH_DIRECTION_OPTIONS.map((directionOption) => {
                          const isActive = settings.allowedDirections.includes(directionOption.key);
                          return (
                            <button
                              key={directionOption.key}
                              type="button"
                              onClick={() => {
                                setSettings((prev) => {
                                  const exists = prev.allowedDirections.includes(directionOption.key);
                                  if (exists) {
                                    if (prev.allowedDirections.length === 1) return prev;
                                    return {
                                      ...prev,
                                      allowedDirections: prev.allowedDirections.filter((item) => item !== directionOption.key),
                                    };
                                  }

                                  return {
                                    ...prev,
                                    allowedDirections: [...prev.allowedDirections, directionOption.key],
                                  };
                                });
                              }}
                              className="rounded-xl border px-4 py-2 text-sm font-bold transition-colors"
                              style={getThemedOptionButtonStyle(isActive)}
                            >
                              {directionOption.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <SettingsToggleRow
                      label="Başlamadan önce geriye say"
                      checked={settings.enableCountdown}
                      onChange={() => setSettings((prev) => ({ ...prev, enableCountdown: !prev.enableCountdown }))}
                    />
                  </div>

                  <div className="space-y-5 border-l border-[#edf2f5] pl-7">
                    <div>
                      <div className="mb-2 flex items-center justify-between">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Boyutu</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {FONT_SIZE_LABELS[settings.fontSize]}
                        </span>
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        {FONT_SIZE_OPTIONS.map((sizeOption) => {
                          const isActive = settings.fontSize === sizeOption.value;
                          return (
                            <button
                              key={sizeOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, fontSize: sizeOption.value }))}
                              className="h-10 rounded-lg border transition-colors"
                              style={getThemedOptionButtonStyle(isActive)}
                            >
                              <span className={`font-semibold ${sizeOption.preview}`}>{sizeOption.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <p className="mb-2 text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Karakteri</p>
                      <div className="grid grid-cols-2 gap-2">
                        {FONT_FAMILY_OPTIONS.map((fontOption) => {
                          const isActive = settings.fontFamily === fontOption.value;
                          return (
                            <button
                              key={fontOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, fontFamily: fontOption.value }))}
                              className="h-10 rounded-lg border text-sm transition-colors"
                              style={{ ...getThemedOptionButtonStyle(isActive), fontFamily: fontOption.css }}
                            >
                              {fontOption.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <div className="mb-2 flex items-center justify-between">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Vurgu Rengi</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {ACCENT_COLOR_OPTIONS.find((colorOption) => colorOption.value === settings.accentColor)?.label || 'Varsayılan'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        {ACCENT_COLOR_OPTIONS.map((colorOption) => {
                          const isActive = settings.accentColor === colorOption.value;
                          return (
                            <button
                              key={colorOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, accentColor: colorOption.value }))}
                              className="h-7 w-7 rounded-full border-2 transition-transform hover:scale-105"
                              style={{ ...getThemedSwatchStyle(isActive, colorOption.hex), backgroundColor: colorOption.hex }}
                              title={colorOption.label}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const CaptureExerciseView: React.FC<{
  profile: any;
  mode: ModeConfig;
  recommendedContent?: RecommendedStoryContent | null;
  contentLoading?: boolean;
  variant?: 'vertical' | 'horizontal';
  onClose: () => void;
}> = ({ profile, mode, recommendedContent, contentLoading = false, variant = 'vertical', onClose }) => {
  const { user } = useAuth();
  const fullscreenContainerRef = useRef<HTMLDivElement | null>(null);
  const captureTextContainerRef = useRef<HTMLDivElement | null>(null);
  const captureWordRefs = useRef<Record<number, HTMLSpanElement | null>>({});
  const captureRunStartRef = useRef<number | null>(null);
  const captureRunFrameRef = useRef<number | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState<RecommendedStoryContent | null>(null);
  const [muted, setMuted] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'countdown' | 'running' | 'review'>('idle');
  const [countdown, setCountdown] = useState(3);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [captureProgressRatio, setCaptureProgressRatio] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, 'A' | 'B' | 'C' | 'D' | 'E'>>({});
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [settings, setSettings] = useState<CaptureSettings>({
    wordsPerMinute: Math.max(275, Number(profile?.currentSpeed) || 275),
    level: 1,
    disappearWordCount: 1,
    fontSize: 'buyuk',
    fontFamily: 'GoogleSans',
    accentColor: 'default',
    enableCountdown: true,
  });

  // Kullanıcı kütüphaneden metin seçebilir; seçmediyse öneri metni kullanılır.
  const activeCaptureContent = selectedContent || recommendedContent;
  const captureText = (activeCaptureContent?.content || CAPTURE_SAMPLE_TEXT).trim();
  // Metnin KENDİ sorularının TAMAMI sorulur. Eskiden yalnızca questions[0] alınıyordu,
  // bu yüzden 5 soruluk metinlerde tek soru görünüyordu.
  const captureReviewQuestions = activeCaptureContent?.questions?.length
    ? activeCaptureContent.questions.filter((question) => question.options?.length)
    : [CAPTURE_REVIEW_QUESTION];
  const hasAnsweredAllCaptureQuestions = captureReviewQuestions.every((question) => Boolean(selectedAnswers[question.id]));
  const captureCorrectCount = captureReviewQuestions.filter((question) => selectedAnswers[question.id] === question.correctKey).length;

  const captureWordCount = captureText
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean).length;
  const isHorizontalVariant = variant === 'horizontal';
  const infoParagraphs = isHorizontalVariant ? DISAPPEARING_TEXT_INFO_PARAGRAPHS : CAPTURE_INFO_PARAGRAPHS;
  const disappearingParagraphWords = useMemo(() => (
    captureText
      .split(/\n{2,}/)
      .map((paragraph) => paragraph.replace(/\s+/g, ' ').trim().split(' ').filter(Boolean))
      .filter((paragraphWords) => paragraphWords.length > 0)
  ), [captureText]);
  const effectiveDisappearWordCount = Math.max(1, Math.min(5, Math.round(Number(settings.disappearWordCount) || 1)));
  const disappearingStepCount = Math.max(1, Math.ceil(captureWordCount / effectiveDisappearWordCount));
  const disappearingStepMs = Math.max(80, Math.round((effectiveDisappearWordCount * 60000) / Math.max(1, settings.wordsPerMinute)));
  const totalDurationMs = isHorizontalVariant
    ? Math.max(1000, disappearingStepCount * disappearingStepMs)
    : Math.max(1000, getCaptureDurationSeconds(settings.wordsPerMinute, settings.level, captureWordCount) * 1000);
  const totalDurationSeconds = Math.max(1, Math.ceil(totalDurationMs / 1000));
  const coverProgress = Math.min(100, Math.max(0, captureProgressRatio * 100));
  const hiddenWordCount = isHorizontalVariant
    ? Math.min(captureWordCount, Math.floor(captureProgressRatio * disappearingStepCount) * effectiveDisappearWordCount)
    : 0;
  const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || profile?.name || 'Kullanıcı';
  const userImage = user?.avatar || user?.media?.url || user?.profilePhoto?.url || null;
  const userInitials = userName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part.charAt(0).toUpperCase())
    .join('');
  const activeAccent = resolveAccentColor(settings.accentColor);
  const activeFontFamily = FONT_FAMILY_OPTIONS.find((item) => item.value === settings.fontFamily)?.css || settings.fontFamily;
  const textClass = settings.fontSize === 'kucuk'
    ? 'text-[22px] leading-[1.95]'
    : settings.fontSize === 'orta'
      ? 'text-[24px] leading-[2]'
      : settings.fontSize === 'buyuk'
        ? 'text-[26px] leading-[2.05]'
        : 'text-[29px] leading-[2.1]';
  const isRunning = phase === 'running' || phase === 'countdown';
  const primaryLabel = phase === 'review'
    ? (reviewSubmitted ? 'Tamamlayınız' : 'Cevapları Kaydet')
    : isRunning
      ? 'Durdur'
      : 'Başlat';
  const isPrimaryDisabled = phase === 'review' && !reviewSubmitted && !hasAnsweredAllCaptureQuestions;
  useEffect(() => {
    setSettings((prev) => applySharedExerciseSettings(prev));
  }, []);

  useEffect(() => {
    if (!settingsOpen) return;
    persistSharedExerciseSettings(settings);
  }, [settings, settingsOpen]);

  useEffect(() => {
    if (phase !== 'idle') return;
    setElapsedSeconds(0);
    setCaptureProgressRatio(0);
  }, [phase, totalDurationSeconds]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    if (phase !== 'countdown' || countdown <= 0) return;
    const timer = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [phase, countdown]);

  useEffect(() => {
    if (phase !== 'countdown' || countdown !== 0) return;
    setPhase('running');
  }, [phase, countdown]);

  useEffect(() => {
    if (phase !== 'running') {
      if (captureRunFrameRef.current) {
        window.cancelAnimationFrame(captureRunFrameRef.current);
        captureRunFrameRef.current = null;
      }
      captureRunStartRef.current = null;
      return;
    }

    captureRunStartRef.current = performance.now();
    setCaptureProgressRatio(0);
    setElapsedSeconds(0);

    if (captureTextContainerRef.current) {
      captureTextContainerRef.current.scrollTop = 0;
    }

    const tick = (now: number) => {
      if (!captureRunStartRef.current) return;
      const elapsedMs = now - captureRunStartRef.current;
      const nextRatio = Math.min(1, Math.max(0, elapsedMs / totalDurationMs));

      setCaptureProgressRatio(nextRatio);
      setElapsedSeconds(Math.min(totalDurationSeconds, Math.floor(elapsedMs / 1000)));

      if (nextRatio >= 1) {
        setElapsedSeconds(totalDurationSeconds);
        setPhase('review');
        return;
      }

      captureRunFrameRef.current = window.requestAnimationFrame(tick);
    };

    captureRunFrameRef.current = window.requestAnimationFrame(tick);

    return () => {
      if (captureRunFrameRef.current) {
        window.cancelAnimationFrame(captureRunFrameRef.current);
        captureRunFrameRef.current = null;
      }
      captureRunStartRef.current = null;
    };
  }, [phase, totalDurationMs, totalDurationSeconds]);

  useEffect(() => {
    if (phase !== 'running' || isHorizontalVariant) return;
    const container = captureTextContainerRef.current;
    if (!container) return;

    const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
    container.scrollTop = maxScrollTop * captureProgressRatio;
  }, [captureProgressRatio, isHorizontalVariant, phase]);

  useEffect(() => {
    if (phase !== 'running' || !isHorizontalVariant) return;
    const container = captureTextContainerRef.current;
    const nextVisibleWord = captureWordRefs.current[Math.min(hiddenWordCount, Math.max(0, captureWordCount - 1))];
    if (!container || !nextVisibleWord) return;

    const targetTop = Math.max(0, nextVisibleWord.offsetTop - (container.clientHeight * 0.36));
    if (Math.abs(container.scrollTop - targetTop) > 4) {
      container.scrollTo({ top: targetTop, behavior: 'smooth' });
    }
  }, [captureWordCount, hiddenWordCount, isHorizontalVariant, phase]);

  const handleReset = () => {
    if (captureRunFrameRef.current) {
      window.cancelAnimationFrame(captureRunFrameRef.current);
      captureRunFrameRef.current = null;
    }
    captureRunStartRef.current = null;
    setPhase('idle');
    setCountdown(3);
    setElapsedSeconds(0);
    setCaptureProgressRatio(0);
    if (captureTextContainerRef.current) {
      captureTextContainerRef.current.scrollTop = 0;
    }
    setSelectedAnswers({});
    setReviewSubmitted(false);
  };

  const handlePrimaryAction = async () => {
    if (phase === 'review') {
      if (!reviewSubmitted) {
        if (!hasAnsweredAllCaptureQuestions) return;
        setReviewSubmitted(true);
        return;
      }
      await handleCloseExercise();
      return;
    }

    if (isRunning) {
      handleReset();
      return;
    }

    setSelectedAnswers({});
    setReviewSubmitted(false);
    setElapsedSeconds(0);
    if (settings.enableCountdown) {
      setCountdown(3);
      setPhase('countdown');
      return;
    }
    setPhase('running');
  };

  const handleCloseExercise = async () => {
    if (document.fullscreenElement === fullscreenContainerRef.current) {
      try {
        await document.exitFullscreen();
      } catch (error) {
        console.error('Tam ekrandan çıkılamadı:', error);
      }
    }
    handleReset();
    onClose();
  };

  const handleToggleFullscreen = async () => {
    const container = fullscreenContainerRef.current;
    if (!container) return;

    try {
      if (document.fullscreenElement === container) {
        await document.exitFullscreen();
        return;
      }
      await container.requestFullscreen();
    } catch (error) {
      console.error('Tam ekran değiştirilemedi:', error);
    }
  };

  return (
    <div
      ref={fullscreenContainerRef}
      className={`${isFullscreen ? 'h-screen p-3' : 'h-full p-6'} bg-[#f3f5f7]`}
    >
      <div className={`${isFullscreen ? 'max-w-none' : 'max-w-[1260px]'} mx-auto h-full`}>
        <div className="h-full border border-[#e6edf1] rounded-xl bg-[#f8fafb] px-6 py-5 flex gap-5 relative">
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {isHorizontalVariant
                  ? <ArrowRight className="w-[19px] h-[19px]" style={getThemeChromeTextStyle()} />
                  : <ArrowDown className="w-[19px] h-[19px]" style={getThemeChromeTextStyle()} />}
                <div className="text-[20px] font-bold" style={getThemeChromeTextStyle()}>{mode.name}</div>
              </div>
              <div className="flex items-center gap-3 text-[#afbbc2]">
                <ExerciseTextSourceControls
                  mode={mode}
                  disabled={phase === 'running' || phase === 'countdown'}
                  onSelect={(content) => {
                    // Metin değişince çalışma baştan başlamalı.
                    setSelectedContent(content);
                    setPhase('idle');
                    setElapsedSeconds(0);
                    setCaptureProgressRatio(0);
                    setSelectedAnswers({});
                    setReviewSubmitted(false);
                  }}
                  onError={(message) => console.error(message)}
                />
                <button onClick={() => setMuted((m) => !m)} className="hover:text-[#6b7f8a]">
                  {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
                <button onClick={() => setSettingsOpen(true)} className="hover:text-[#6b7f8a]">
                  <Settings2 className="w-5 h-5" />
                </button>
                <button onClick={() => { void handleToggleFullscreen(); }} className="hover:text-[#6b7f8a]">
                  {isFullscreen ? <Shrink className="w-5 h-5" /> : <Expand className="w-5 h-5" />}
                </button>
                <button onClick={() => { void handleCloseExercise(); }} className="hover:text-[#6b7f8a]">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="relative min-h-0 flex-1 border border-[#e8edf0] rounded-[10px] px-6 py-5 bg-white overflow-hidden">
              {phase === 'idle' && (
                <div className="h-full flex items-center justify-center">
                  <div className="max-w-[980px] translate-y-12 text-center">
                    {contentLoading ? (
                      <div className="mx-auto max-w-[900px] flex flex-col items-center gap-4">
                        <div className="w-9 h-9 border-2 border-[#2f5563] border-t-transparent rounded-full animate-spin" />
                        <p className="text-[20px] font-bold leading-[1.55] text-[#2e6276]">İçerik yükleniyor...</p>
                      </div>
                    ) : infoParagraphs.map((paragraph, index) => (
                      <p
                        key={paragraph}
                        className={`mx-auto max-w-[900px] text-[20px] font-bold leading-[1.55] text-[#2e6276] ${index > 0 ? 'mt-8' : ''}`}
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {phase === 'countdown' && (
                <div className="h-full flex items-center justify-center">
                  <span className="text-7xl font-extrabold text-[#18485c]">{countdown}</span>
                </div>
              )}

              {(phase === 'running' || phase === 'review') && (
                <div className="relative h-full min-h-0">
                  {phase === 'running' && !isHorizontalVariant && (
                    <div
                      className={`pointer-events-none absolute z-10 ${isHorizontalVariant ? 'inset-y-0 left-0' : 'left-0 right-0 top-0'}`}
                      style={isHorizontalVariant
                        ? { width: `${coverProgress}%`, backgroundColor: activeAccent }
                        : { height: `${coverProgress}%`, backgroundColor: activeAccent }}
                    />
                  )}

                  {phase === 'running' && (
                    <div
                      ref={captureTextContainerRef}
                      className={`h-full overflow-hidden px-6 py-3 font-semibold text-[#111111] ${textClass}`}
                      style={{ fontFamily: activeFontFamily }}
                    >
                      {isHorizontalVariant ? (() => {
                        let wordCursor = 0;
                        return disappearingParagraphWords.map((paragraphWords, paragraphIndex) => (
                          <p key={`disappearing-paragraph-${paragraphIndex}`} className={paragraphIndex > 0 ? 'mt-6' : ''}>
                            {paragraphWords.map((word) => {
                              const wordIndex = wordCursor;
                              wordCursor += 1;
                              const isHidden = wordIndex < hiddenWordCount;

                              return (
                                <React.Fragment key={`${word}-${wordIndex}`}>
                                  <span
                                    ref={(el) => {
                                      captureWordRefs.current[wordIndex] = el;
                                    }}
                                    className="inline-block transition-opacity duration-100"
                                    style={{
                                      opacity: isHidden ? 0 : 1,
                                      visibility: isHidden ? 'hidden' : 'visible',
                                    }}
                                  >
                                    {word}
                                  </span>{' '}
                                </React.Fragment>
                              );
                            })}
                          </p>
                        ));
                      })() : captureText.split('\n\n').map((paragraph, index) => (
                        <p key={`${paragraph.slice(0, 20)}-${index}`} className={index > 0 ? 'mt-6' : ''}>
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  )}

                  {phase === 'review' && (
                    <div className="h-full overflow-y-auto p-6">
                      <div className="mx-auto max-w-[1120px] space-y-4">
                        {reviewSubmitted && (
                          <div className="rounded-2xl border border-[#dceaf0] bg-white px-6 py-4 text-[16px] font-bold text-[#295567]">
                            Doğru cevap: {captureCorrectCount} / {captureReviewQuestions.length}
                          </div>
                        )}
                        {captureReviewQuestions.map((question, questionIndex) => (
                        <div key={question.id} className="rounded-2xl border border-[#e4edf1] bg-[#f7fafc] p-8">
                        <div className="text-[13px] font-bold uppercase tracking-[0.12em] text-[#8aa0aa]">
                          Soru {questionIndex + 1} / {captureReviewQuestions.length}
                        </div>
                        <h4 className="mt-2 text-[22px] leading-8 font-bold text-[#295567]" style={{ fontFamily: activeFontFamily }}>
                          {question.prompt}
                        </h4>
                        <div className="mt-5 space-y-3">
                          {question.options.map((option) => {
                            const isSelected = selectedAnswers[question.id] === option.key;
                            const isCorrect = option.key === question.correctKey;
                            const showCorrect = reviewSubmitted && isCorrect;
                            const showWrong = reviewSubmitted && isSelected && !isCorrect;
                            const isPendingSelection = isSelected && !reviewSubmitted;

                            return (
                              <button
                                key={option.key}
                                onClick={() => {
                                  if (reviewSubmitted) return;
                                  setSelectedAnswers((prev) => ({ ...prev, [question.id]: option.key }));
                                }}
                                className={`w-full rounded-xl border px-4 py-4 text-left transition-colors ${showCorrect
                                  ? 'border-[#6acb86] bg-[#f0fbf3]'
                                  : showWrong
                                    ? 'border-[#f28c8c] bg-[#fff3f3]'
                                    : isSelected
                                      ? ''
                                      : 'border-[#dbe7ec] bg-white hover:border-[#c3d5de]'
                                  }`}
                                style={getThemedReviewSelectionStyle(isPendingSelection)}
                              >
                                <div className="flex items-center gap-4">
                                  <span className={`text-[30px] font-black ${showCorrect ? 'text-[#46b96a]' : showWrong ? 'text-[#ef5350]' : 'text-[#2d7087]'
                                    }`} style={getThemedReviewSelectionTextStyle(isPendingSelection)}>
                                    {option.key}
                                  </span>
                                  <span className={`h-8 w-px ${showCorrect ? 'bg-[#7dd39a]' : showWrong ? 'bg-[#ef9a9a]' : 'bg-[#d4e2e8]'
                                    }`} style={getThemedReviewSelectionDividerStyle(isPendingSelection)} />
                                  <span className={`text-[18px] font-semibold ${showCorrect ? 'text-[#46b96a]' : showWrong ? 'text-[#ef5350]' : 'text-[#2d7087]'
                                    }`} style={getThemedReviewSelectionTextStyle(isPendingSelection)}>
                                    {option.text}
                                  </span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <aside className="hidden lg:flex w-[320px] shrink-0 h-full min-h-0 rounded-xl border border-[#e0eaee] bg-white p-5 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="flex items-center gap-3">
                {userImage ? (
                  <img src={userImage} alt={userName} className="w-11 h-11 rounded-full object-cover bg-[#daf0f7]" />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-[#daf0f7] text-[#146f8e] flex items-center justify-center font-bold">
                    {userInitials || 'K'}
                  </div>
                )}
                <div>
                  <div className="text-[#355c68] text-sm font-bold">{userName}</div>
                  <div className="text-[#94a8b3] text-xs">1. Seviye</div>
                </div>
              </div>

              <div className="mt-6 flex flex-col items-center text-center">
                <div className="rounded-[2rem] bg-[#f7fafc] px-5 py-4">
                  <ModeIllustration mode={mode} />
                </div>
                <h3 className="mt-4 text-[34px] leading-none font-black" style={getThemeChromeTextStyle()}>{mode.name}</h3>
              </div>

              {phase === 'idle' && <SidebarPreparationCard paragraphs={infoParagraphs} fontFamily={activeFontFamily} />}
            </div>

            <div className="shrink-0 pt-4">
              <p className="text-xs text-[#98a8b1]">Geçen Süre</p>
              <p className="text-[44px] leading-none font-black mt-1" style={getThemeChromeTextStyle()}>
                {formatClock(phase === 'review' ? elapsedSeconds : (phase === 'countdown' ? 0 : elapsedSeconds))}
              </p>

              <button
                onClick={() => { void handlePrimaryAction(); }}
                disabled={isPrimaryDisabled}
                className={`mt-4 w-full h-12 rounded-lg font-bold text-base transition-colors inline-flex items-center justify-center gap-2 ${isPrimaryDisabled
                  ? 'bg-[#dfe7ea] text-[#7f929c] cursor-not-allowed'
                  : 'text-white'
                  }`}
                style={isPrimaryDisabled ? undefined : getThemeChromeButtonStyle()}
              >
                {phase === 'review' ? null : isRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {primaryLabel}
              </button>

              {phase !== 'review' && (isRunning || phase === 'idle') && (
                <button
                  onClick={handleReset}
                  className="mt-4 w-full h-12 rounded-lg bg-[#f1f3f5] text-[#2d6175] font-bold text-base transition-colors hover:bg-[#e7ecef]"
                >
                  Sıfırla
                </button>
              )}
            </div>
          </aside>

          {isFullscreen && (
            <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 text-[12px] font-medium text-[#96a6af]">
              Tam ekrandan çıkmak için Esc tuşuna basın veya tıklayın
            </div>
          )}

          {settingsOpen && (
            <div className="fixed inset-0 z-[120] bg-black/55 backdrop-blur-[4px] flex items-center justify-center">
              <div className="w-[920px] bg-white rounded-xl shadow-lg p-6 select-none">
                <div className="flex items-center justify-between mb-5">
                  <h4 className="text-xl font-bold" style={getThemedLabelStyle()}>Egzersiz Ayarları</h4>
                  <button className="text-gray-500 hover:text-gray-700" onClick={() => setSettingsOpen(false)}>
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-7">
                  <div className="space-y-5">
                    <SettingsStepperRow
                      label="Gösterim Hızı"
                      value={`${shared.wordsPerMinuteToClosestPaceMs(settings.wordsPerMinute)} ms`}
                      onDecrease={() => {
                        const currentPaceMs = shared.wordsPerMinuteToClosestPaceMs(settings.wordsPerMinute);
                        const currentIndex = shared.PACE_MS_OPTIONS.findIndex((paceMs) => paceMs === currentPaceMs);
                        const nextPaceMs = shared.PACE_MS_OPTIONS[Math.max(0, currentIndex - 1)];
                        setSettings((prev) => ({
                          ...prev,
                          wordsPerMinute: shared.paceMsToWordsPerMinute(nextPaceMs),
                          paceMs: nextPaceMs,
                        }));
                      }}
                      onIncrease={() => {
                        const currentPaceMs = shared.wordsPerMinuteToClosestPaceMs(settings.wordsPerMinute);
                        const currentIndex = shared.PACE_MS_OPTIONS.findIndex((paceMs) => paceMs === currentPaceMs);
                        const nextPaceMs = shared.PACE_MS_OPTIONS[Math.min(shared.PACE_MS_OPTIONS.length - 1, currentIndex + 1)];
                        setSettings((prev) => ({
                          ...prev,
                          wordsPerMinute: shared.paceMsToWordsPerMinute(nextPaceMs),
                          paceMs: nextPaceMs,
                        }));
                      }}
                    />

                    {isHorizontalVariant ? (
                      <SettingsStepperRow
                        label="Kaybolacak Kelime Sayısı"
                        value={`${effectiveDisappearWordCount}`}
                        onDecrease={() => setSettings((prev) => ({
                          ...prev,
                          disappearWordCount: DISAPPEARING_WORD_COUNT_OPTIONS[
                            Math.max(0, DISAPPEARING_WORD_COUNT_OPTIONS.indexOf(effectiveDisappearWordCount as (typeof DISAPPEARING_WORD_COUNT_OPTIONS)[number]) - 1)
                          ],
                        }))}
                        onIncrease={() => setSettings((prev) => ({
                          ...prev,
                          disappearWordCount: DISAPPEARING_WORD_COUNT_OPTIONS[
                            Math.min(
                              DISAPPEARING_WORD_COUNT_OPTIONS.length - 1,
                              DISAPPEARING_WORD_COUNT_OPTIONS.indexOf(effectiveDisappearWordCount as (typeof DISAPPEARING_WORD_COUNT_OPTIONS)[number]) + 1
                            )
                          ],
                        }))}
                      />
                    ) : (
                      <SettingsStepperRow
                        label="Seviye"
                        value={`${settings.level}`}
                        onDecrease={() => setSettings((prev) => ({ ...prev, level: Math.max(1, prev.level - 1) }))}
                        onIncrease={() => setSettings((prev) => ({ ...prev, level: Math.min(13, prev.level + 1) }))}
                      />
                    )}

                    <SettingsToggleRow
                      label="Başlamadan önce geriye say"
                      checked={settings.enableCountdown}
                      onChange={() => setSettings((prev) => ({ ...prev, enableCountdown: !prev.enableCountdown }))}
                    />
                  </div>

                  <div className="space-y-5 border-l border-[#edf2f5] pl-7">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Boyutu</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {FONT_SIZE_LABELS[settings.fontSize]}
                        </span>
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        {FONT_SIZE_OPTIONS.map((sizeOption) => {
                          const isActive = settings.fontSize === sizeOption.value;
                          return (
                            <button
                              key={sizeOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, fontSize: sizeOption.value }))}
                              className="h-10 rounded-lg border transition-colors"
                              style={getThemedOptionButtonStyle(isActive)}
                            >
                              <span className={`font-semibold ${sizeOption.preview}`}>{sizeOption.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <p className="mb-2 text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Karakteri</p>
                      <div className="grid grid-cols-2 gap-2">
                        {FONT_FAMILY_OPTIONS.map((fontOption) => {
                          const isActive = settings.fontFamily === fontOption.value;
                          return (
                            <button
                              key={fontOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, fontFamily: fontOption.value }))}
                              className="h-10 rounded-lg border text-sm transition-colors"
                              style={{ ...getThemedOptionButtonStyle(isActive), fontFamily: fontOption.css }}
                            >
                              {fontOption.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Vurgu Rengi</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {ACCENT_COLOR_OPTIONS.find((colorOption) => colorOption.value === settings.accentColor)?.label || 'Varsayılan'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        {ACCENT_COLOR_OPTIONS.map((colorOption) => {
                          const isActive = settings.accentColor === colorOption.value;
                          return (
                            <button
                              key={colorOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, accentColor: colorOption.value }))}
                              className="h-7 w-7 rounded-full border-2 transition-transform hover:scale-105"
                              style={{ ...getThemedSwatchStyle(isActive, colorOption.hex), backgroundColor: colorOption.hex }}
                              title={colorOption.label}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
